/**
 * The **atom interpreter** — the bridge from the recipe (effect-atoms) to the record
 * (grounded steps). `interpretEffect` reads an {@link EffectAtom} list against live
 * state + input and **emits the grounded steps** that the resolution records (`save`,
 * `status_add`, `resource_delta`, `hp_delta`, …). Control-flow atoms (`force_save`,
 * `if`, `choose`) branch and *vanish* from the output; grounded atoms (`add_status`)
 * map ~1:1 to a step. This is the rules half (it rolls save dice, takes branches);
 * `applyResolved` (in `domain`) stays rules-free playback of whatever this emits.
 *
 * **The seam to preserve:** the interpreter NEVER reshapes the step layer and NEVER
 * puts branching into `applyResolved` — it only *composes into* the existing grounded
 * steps. If an effect needs a primitive not in the step set, that is a NEW step (added
 * in `domain/encounter/steps.ts` with its inverse + round-trip test), not a faked atom.
 *
 * Inputs flow through the same {@link import("../resolve").InputSource} the resolver
 * uses: save rolls draw `input.dice`; a `choose` resolves through an optional
 * recorded-choice seam, and so does a `gm_ruling` predicate's answer. All of it is recorded into the emitted steps, so replay is
 * rules-free and version-stable.
 */

import type {
  Cell,
  Combatant,
  CombatantId,
  EncounterState,
  ResolvedStep,
  SaveStep,
  StatusAddStep,
  StatusRemoveStep,
  StatusSuspendSetStep,
  ResourceDeltaStep,
  ResourceSetStep,
  ContestStep,
  DamageType,
  ForcedMoveStep,
  DisclosureSetStep,
  SpawnObjectStep,
  DespawnObjectStep,
  AreaEffect,
  AreaAddStep,
  AreaRemoveStep,
  DeployedFrom,
  StatusInstance,
  MoveBudgetDeltaStep,
  MoveStep,
} from "@lancer-vtt/domain";
import { applyResolved, isCharacter, hasStatus, heldLimitedPool, isWeaponLoaded, kraulLineSnapSteps, loadedResourceKey, saveBonusFor, sideOf, voluntaryMoveBar, teleportLandings, swapFits, grantedMoveLandings, resourceCapKey, resourceCapRawKey, isItemDestroyed, destroyedItemResourceKey, FLYING_STATUS, GROUNDED_STATUS, DISCLOSURE_RANK, disclosureOf, grantedActionAllowanceKey, freeActionUseKey, objectGroupMembers, isRooted, rootedVetoWarning, REPAIR_CAP_RESOURCE, statusFacetsFromInstance, worldFor, encounterGridMode, tagCell, placementFitter, threatValue, weaponInstanceKey } from "@lancer-vtt/domain";
import { resolutionNote, unwrap, type RequestAttack } from "@lancer-vtt/shared";
import { rollD20, rollDamage, type DiceSource } from "../../dice";
import type {
  EffectAtom,
  AtomActorRef,
  ExtraDamageAtom,
  DamageAtom,
  ForceSaveAtom,
  SkillCheckAtom,
  ForcedMoveAtom,
  MoveSelfTowardTargetAtom,
  MoveUpToAtom,
  DeployObjectAtom,
  DeployMineAtom,
  RecallObjectAtom,
  RedeployObjectAtom,
  ReloadAtom,
  RepairObjectGroupAtom,
  CreateAreaAtom,
  PauseStatusExpiryAtom,
  ForEachAtom,
  AreaSelector,
  ChooseRandomAtom,
  ChooseFromAreaAtom,
  PlaceAdjacentAtom,
  TeleportAtom,
  TeleportSwapAtom,
  MoveSpacesAtom,
  DestroySystemAtom,
  ForcedAttackAtom,
  ForcedAttackRequest,
  ContestedRollAtom,
  RollAtom,
  ChooseAtom,
  ChooseOption,
  MagnitudeValue,
  StatScaledAmount,
} from "./atoms";
import { evaluatePredicate, objectGroupOut, resolveActorId, bondmatePartner, type PredicateContext, type TriggerEvent, type SaveEvent } from "./predicate";
import { compare } from "./compare";
import { moveReachAnchors, moveWalk, nearestAdjacentLandings, pushDestinationWithCollision, travelDestination, travelDestinationWithCollision, travelReachableCells } from "./spatialAnswerer";
import { enumerateArea, type MovePath } from "./spatialQuery";
import { displaySourceName } from "../narrate/narrate";
import { clampHp, heatDeltaStep } from "./hp";
import { heatAsEnergyDamage, resolveHeatCascade } from "../tables";
import { foldSelfSaveCheck, rollSelfCheck, selfCheckContext, settleCheck, spendConsumedStatuses, spendFoldedCaps, type CheckRoll } from "../selfCheck";
import { nonAttackArmor, nonAttackExposed, nonAttackGrantCaps, nonAttackImmune, nonAttackImmuneWarning, nonAttackResistant } from "./nonAttackResistance";
import { checkDirectedDeployPlacement, legalAreaDeployCells, legalDirectedDeployCells } from "../deploy";
import { spawnObjectGroup, spawnMine, spawnDrone } from "../spawn";
import { grantOvershieldStep } from "../overshield";
import { beginRodeoSteps, checkRodeoLegality, endRodeoSteps } from "../rodeo";
import { statusBlockedByImmunity, choosesInvoluntaryHeading, immuneToInvoluntaryMovement, smallerCharacterImmunity, smallerCharacterImmunityWarning, seedModifiers, seedSystemModifiers, seedPilotModifiers, SEED_OVERLAY } from "./seed";
import { foldModifiers, attackPredicateContext, scopeSaveModifiers } from "./modifiers";
import type { BoundModifier } from "./modifiers";
import type { LoadedOverlay } from "./loader";
import { gatherHandlersAt, combatantEffectIds, gatherConferredModifiers, offeredInvadeOptions } from "./loader";
import { createResolutionLedger, type ResolutionLedger } from "./resolutionLedger";
import { affordable, capUseSteps, chargesLeft, emitEvent, reactorsUsed, spendStepsNow, useReaction, type ExpendChargesEvent } from "../events/gameEvents";
import type { EventLoop } from "../events/loop";
import { drawRoll } from "../events/rollEvents";
import { raiseHeatCleared } from "../events/trackEvents";
// `forced_save_declared` fires from INSIDE the interpreter (interpretForceSave, below)
// rather than from the `resolve/` fire sites like every other point — see that function's own doc for
// why. `triggerBus.ts` imports `interpretEffect`/`predicateContextFrom` from THIS module,
// so this is a genuine module cycle; it's safe because both `fireTrigger` and
// `interpretEffect` are hoisted function declarations (never invoked at module-evaluation
// time, only from inside a later function call), the standard safe shape for an ESM
// circular import. `firedTriggerPoints.pin.test.ts` covers this second fire site.
import { fireTrigger, staticHandlers, type Handler } from "./triggerBus";

// The choice seam (`ChoiceSource`, `NeedsDecisionSignal`, `recordedChoices`,
// `promptingChoices`) lives in `./choices` so `predicate.ts`'s `gm_ruling` can halt through it
// without a circular import; re-exported here for existing importers.
export { NeedsDecisionSignal, recordedChoices, promptingChoices, type ChoiceSource } from "./choices";
import { NeedsDecisionSignal, type ChoiceSource } from "./choices";
import { interceptNonAttackDamage, type InterceptSink } from "../nonAttackIntercept";
import { dealDamagePacket, landDamageInstance } from "../resolve/damagePacket";
import { isPhysical } from "../resolve/damage";
import { compareContestRolls } from "../contest";
import { foreignHeatDestroy, saveNegatesDamage } from "../grunt";

// ── the resolution context the interpreter binds atoms against ────────────────────

/**
 * The facts + inputs an effect interprets against. Built by the caller (the resolver
 * for a triggered/on-hit effect; the trigger bus for a lifecycle effect) from the
 * resolution: the live {@link EncounterState}, the bound actor ids (`self`/`target`/
 * `attacker`), the input sources (dice for saves, an optional choice source), and the
 * default save DC.
 */
export type EffectContext = {
  state: EncounterState;
  /**
   * `self` = the combatant whose effect this is (attacker/carrier); `target` = the primary target;
   * `partner` = a `choose_from_area` pick (Mirage) or a `for_each` member (Summon) bound `as: "partner"`, inside that atom's body only.
   */
  actors: { self?: string; target?: string; attacker?: string; partner?: string };
  /** The authority's dice (save rolls). */
  dice: DiceSource;
  /**
   * Optional recorded-choice answerer for `choose` atoms and `gm_ruling` predicates; absent →
   * a `choose` takes the first option and a `gm_ruling` reads `false`.
   */
  choices?: ChoiceSource;
  /**
   * The recorded **template-center** {@link Cell} an area selector with `origin: "site"`
   * enumerates from — the draw-and-record input a `for_each` reads,
   * validated against the encounter grid by the resolver before it lands here. Absent for a
   * non-area resolution; a `site`-origin `for_each` with no recorded site enumerates nothing.
   */
  site?: Cell;
  /**
   * **The walk this resolution's `move_self_toward_target` just made** — who moved and every
   * anchor it occupied, start and endpoint inclusive (`npc-features-fielded-roster` Phase 15.1).
   * A per-resolution mutable slot, set beside {@link site} by the move, so a later atom in the
   * same list can read it: an {@link import("./atoms").AreaSelector} with `passed_through`
   * (Impale's "passed through" set) and `place_adjacent` (where the mover ended). Absent until a
   * move lands; a failed move leaves any earlier value alone.
   */
  movePath?: MovePath;
  /**
   * **Is the attack this effect fired inside one that targets more than one character?** The
   * engine half of RAW's bonus-damage rule (Core p.67's BONUS DAMAGE sidebar, restated in the
   * p.78 COMBAT TERMINOLOGY glossary): *"if an attack that targets more than one character
   * deals bonus damage, the bonus damage is halved."* Read by exactly one atom —
   * {@link interpretExtraDamage}, and only for an atom that opted in with
   * {@link import("./atoms").ExtraDamageAtom.bonusDamage}. `undefined`/`false` everywhere else:
   * a single-target attack, a non-attack lifecycle firing, an activation.
   *
   * Threaded here by the trigger bus off {@link import("./triggerBus").FireContext.areaAttack},
   * whose doc records which fact each of the three attack fire sites derives it from. It is a
   * fact about the RESOLUTION SHAPE, not about how many targets were hit: a Blast rolls a
   * separate attack per caught target and a missed target takes nothing at all, so nothing here
   * counts hits.
   */
  areaAttack?: boolean;
  /** **The attack this effect fired inside can't deal bonus damage** (a secondary attack, the Annihilator's): a `bonusDamage` `extra_damage` deals nothing. */
  noBonusDamage?: boolean;
  /** **The type this attack's bonus damage takes** (Walking Armory's HELLFIRE: "deals any bonus damage as Burn"): a `bonusDamage` `extra_damage` deals this type instead of its own. */
  bonusDamageType?: DamageType;
  /** How many times heat this attack adds to `target` is halved (a Brace's Resistance, Covering Fire's half): see `FireContext.heatHalvings`. */
  heatHalvings?: { target: string; times: number };
  /** The attacked combatant's accepted reaction resists this attack (a Brace): see `FireContext.reactionResisted`. */
  reactionResisted?: { target: string };
  /** The Armor the firing attack's damage left against `target`, drawn down in place: see `FireContext.attackArmor`. */
  attackArmor?: { target: string; left: number };
  /** A Chaff Grunt that passed the save whose `on_pass` branch this is: `damage`/`extra_damage` against it deal nothing (`grunt.ts`). */
  saveNegatesDamageFor?: string;
  /**
   * **How many characters the attack this effect fired inside targets** — the `{TARGET_COUNT}`
   * binding's one source. The area fan-out's own settled `targetIds.length` (AFTER the
   * per-target LoS exclusion — the characters that actually resolve, not the raw template
   * catch), or `1` on the single-target spine.
   *
   * Read by {@link resolveMagnitude} for a {@link import("./atoms").TargetCountScaledAmount}
   * magnitude — Cluster Munitions' "+{2/3/4} damage … for each target beyond the first"
   * (`npcf_cluster_munitions_bombard`, Core p.294). Threaded here by the trigger bus off
   * {@link import("./triggerBus").FireContext.targetCount}, exactly as `areaAttack` above is.
   *
   * **The scalar twin of `areaAttack`, not a replacement for it.** `areaAttack` is a fact about
   * the resolution SHAPE (did this fan out) and is what RAW's bonus-damage halving reads; this
   * is the COUNT. A one-target Blast is `areaAttack: true, targetCount: 1`.
   *
   * `undefined` at a firing that carries no fan-out fact at all (a lifecycle point, an
   * activation, the DECLARED fire of an attack that IS fanning out — where the template has not
   * been enumerated yet). A `{TARGET_COUNT}` magnitude then reads `1` and records an advisory,
   * mirroring `TierScaledAmount`'s no-frozen-tier degrade — never a crash.
   */
  targetCount?: number;
  /**
   * The totals of the enclosing `roll` atoms that carry a `bind` name, by that name — what a
   * {@link import("./atoms").RolledAmount} magnitude reads. Set only for the roll's own branch.
   */
  rolled?: Readonly<Record<string, number>>;
  /**
   * The recorded **direction** {@link Cell} for a line/cone template (carried for parity) — OR
   * the recorded answer to a mid-resolution
   * spatial {@link NeedsDecisionSignal} (`move_self_toward_target{toward:"chosen"}` reads it, same
   * as the pre-recorded case — see that function's doc). `undefined` when nothing has recorded a
   * direction yet.
   */
  toward?: Cell;
  /**
   * The player-picked cells for a **directed multi-pick placement** action — the {@link Cell}
   * twin of `RequestActivate.sites`
   * (`shared/src/index.ts`), threaded here by `resolveActivate` the same way `site`/`toward`
   * are. Ordered; a per-cell grid mismatch is warn-and-allow, same posture as `site`/`toward`.
   * Absent for a non-placement action. Consumed by {@link interpretDeployObject}.
   */
  sites?: Cell[];
  /**
   * The other space of the two-space mark whose clause is firing (`AreaEffect.pair`, threaded by
   * `resolveAreaTrigger`): what `forced_move { toward: "pair" }` pushes toward.
   */
  pair?: Cell;
  /**
   * **Interactive round-trip mode** — mirrors
   * `EffectsConfig.interactive`/`ChoiceSource.requireAnswer`, but for a SPATIAL decision rather
   * than a discrete `choose` (which wraps its own requirement in the `ChoiceSource`). When `true`
   * and an atom needs a direction/cell it doesn't have (`toward` above is `undefined`), it halts
   * with a {@link NeedsDecisionSignal} instead of recording a silent no-op. `undefined`/`false`
   * (replay, a scripted fixture, the CLI's non-interactive default) preserves the faithful
   * no-roundtrip no-op every `move_self_toward_target` test pins.
   */
  interactive?: boolean;
  /**
   * **Can a direction/cell answer actually get back here?** {@link interactive} says the caller
   * is a live round-trip; this says
   * whether the round-trip has a *wire slot* for a SPATIAL answer specifically.
   *
   * The client answers a direction halt by re-submitting the **echoed intent** with the picked
   * cell recorded on it (`refereeIntent`'s `{ ...intent, recordedDice }` — the ORIGINAL wire
   * intent, not any shot synthesized from it), and only some intent shapes carry a field for it.
   * A shape without one cannot answer at all: the prompt re-raises forever and the encounter
   * **hangs mid-combat** — the worst class of bug for a live game. The shapes that cannot answer
   * today are `request_full_tech`, `request_activate`, `request_stabilize` and
   * `request_overcharge`.
   * The authority on the list is `intentCarriesReactionToward` (`referee/refereeIntent.ts`),
   * whose table is `satisfies`-constrained against the wire types so it cannot rot.
   *
   * So `false` makes the spatial halt behave exactly like NON-interactive for these atoms: no
   * `NeedsDecisionSignal`, take the faithful no-roundtrip default (walk the full authored `dist`
   * for a magnitude question, an honest `to: null` no-op for a direction one) — plus a warning
   * naming the degrade, because a silent full-distance walk would lie about a choice the player
   * never got. `undefined` reads as **answerable** (including every scripted/replay path, where
   * {@link interactive} is unset anyway).
   *
   * Deliberately a SEPARATE axis from {@link interactive} rather than just forcing that to
   * `false`: `interactive` also governs the discrete `choose` round-trip, which a Barrage CAN
   * answer (`RequestBarrage.choices` exists). Collapsing the two would break ammo prompts on
   * Barrage shots to fix a movement prompt.
   */
  directionAnswerable?: boolean;
  /**
   * Default save DC when a `force_save` omits one — the **attacker's** frozen
   * `saveTarget` (the printed/computed save target — `decisions/0163`, never a formula),
   * supplied by the caller. The defender rolls
   * `d20 + saveBonusFor(defender, atom.stat)` against it.
   */
  defaultSaveDc?: number;
  /**
   * The **origin tag** an `add_status` atom
   * stamps onto the emitted {@link StatusAddStep}'s `cause`, so **Shut Down** can later end
   * "tech-caused statuses" by provenance rather than a name-list (Impaired arrives BOTH from
   * FRAGMENT SIGNAL — tech — and the structure table's Glancing Blow — not tech). The caller
   * sets this only when the resolution IS one of the two tech spines (a tech attack's on-hit
   * effect, or a tech-activate's action effect); every other caller (a lifecycle handler, a
   * non-tech weapon's on-hit rider, a granted action) leaves it `undefined` so its emitted
   * statuses carry no cause.
   */
  statusCause?: "tech";
  /**
   * The overlay to fold a `force_save` actor's own carrier-self save/check modifiers
   * from (Impaired's `saveCheckBonus: -1`, Bolster's `+2`) — the SAME seam `saves.ts`'s
   * turn-start re-save and `contest.ts`'s opposed check already fold through. Absent →
   * `SEED_OVERLAY` (what every other caller here defaults to).
   */
  overlay?: LoadedOverlay;
  /**
   * The typed **per-family event payload** (`AttackEvent`/`ActionEvent`/`MoveEvent`,
   * `predicate.ts`) threaded onto the TRIGGER-firing context so a
   * `triggered`/`action` clause's `condition` can gate on the firing's event facts —
   * without it these facts reach ONLY a `modifier` clause's `condition` via
   * `attackPredicateContext`, and a `triggered` clause gated on
   * `weapon_type`/`weapon_mount_class`/`ranged_attack`/`first_attack_this_turn`
   * schema-validates but silently never fires. Set by
   * the caller ONLY at the fire point the payload's family belongs to
   * (`fireAttackDeclared`/the on_hit-on_crit/on_miss firing for an `AttackEvent`,
   * `resolveActionTakenTrigger` for an `ActionEvent`, `resolveMoveTrigger` for a
   * `MoveEvent`); every other caller leaves it `undefined`, which reads as no match on
   * every event-fact predicate op — correct, since a weapon-gated clause on a
   * lifecycle trigger is nonsensical.
   */
  event?: TriggerEvent;
  /**
   * The resolution-scoped **fact ledger** (the cross-firing
   * substrate) — the ONE mutable field on this type. Every other field above is a frozen
   * fact, independently recomputed identically wherever it's threaded; `ledger` is a single
   * object SHARED BY REFERENCE across every `EffectContext`/`FireContext` this resolution
   * constructs, so a `choose` atom's decision (recorded by `interpretChoose`, below) is
   * visible to a LATER same-resolution `condition` via the `chose` {@link
   * import("./predicate").Predicate} op (projected onto {@link PredicateContext.chosen} by
   * {@link predicateContextFrom}). Absent for a caller that doesn't construct one (a
   * save/contest/lifecycle point outside an attack resolution) — a `chose` predicate then
   * reads `false` for every value, same as every other resolution-scoped fact here. See
   * {@link ResolutionLedger}'s own doc for the walking_armory gap this exists to close.
   */
  ledger?: ResolutionLedger;
  /**
   * The **firing clause's own carrying-instance's source** — the source-scoped substrate a
   * third combatant is
   * reached through, instead of a fourth `actors` slot (the closed set stays closed). Set
   * ONLY by a caller that gathered the currently-firing clause off a `StatusInstance`
   * carrying a `source` (`gatherStatusHandlers`, `loader.ts`), from that SAME instance's
   * `source` field — mirrors {@link actionId}'s one-caller-sets-it-else-absent shape
   * exactly. Projected onto {@link PredicateContext.carrierSource} by {@link
   * predicateContextFrom} so a `has_status` with `source: "carrier"` predicate can read it. `undefined`
   * for every caller that isn't gathering off a sourced status instance (a lifecycle point,
   * a raw attack resolution, a save/contest) — reads as no match, the same absent-means-
   * false convention every other threaded fact here uses.
   */
  carrierSource?: string;
  /**
   * **The currently-firing clause's own name + carrier** (`resolution-narration` 1.2) — set
   * ONLY by the trigger bus (`triggerBus.ts`, one `EffectContext` per gathered `Handler`, right
   * before `interpretEffect`), mirroring {@link actionId}'s and {@link carrierSource}'s
   * one-caller-sets-it-else-absent shape exactly. `name` is the clause's display name
   * (`Handler.effect.name`, falling back to `Handler.name` — see `triggerBus.ts`'s doc comments
   * on both fields for which populates when); `carrier` is the combatant the clause's `Handler`
   * belongs to (`Handler.source`). Consumed by {@link interpretExtraDamage} to stamp a rider
   * `damage` step's `DamageStep.source` (`{ kind: "rider", name, carrier }`) — absent for every
   * other caller (a raw attack resolution, a save, a lifecycle point not going through the
   * bus), which is why `interpretExtraDamage` must degrade to an unnamed `source`-less rider
   * rather than inventing a name.
   *
   * **Write-once at construction, and a NARROWER lifetime than {@link site} — do not assume the
   * two match just because they sit side by side.** `site` is a per-RESOLUTION *mutable slot*:
   * `interpretMoveSelfTowardTarget` assigns `ctx.site = to` mid-effect-list so a LATER atom in
   * the same list can enumerate against the resolved endpoint. This field is the opposite —
   * per-HANDLER, fixed by the bus before `interpretEffect` runs, and never assigned anywhere in
   * this module. Nothing reads it after a mutation because nothing mutates it at all. The
   * distinction is spelled out because the neighbouring field documents itself as deliberately
   * mutable, and a reader who generalises from it would wrongly expect `firing` to change
   * partway through an effect list.
   */
  firing?: { name: string; carrier: CombatantId };
  /**
   * **The item an activation fired** (`narration-ledger-retirement` 2.3): its frozen display name
   * (`Affordance.name`). Set ONLY by `resolveActivate`, for a granted affordance. It is
   * {@link interpretExtraDamage}'s second source of provenance: a rider dealt by the activation's
   * own effect list (Frag Grenades' `for_each` → `force_save` → `extra_damage`) records
   * `{ kind: "rider", name }`, where a bus-fired rider records {@link firing}'s name instead.
   */
  activating?: { name: string };
  /**
   * **The overlay entry an activation's clause belongs to** (`ll1-drones` D1): the granted
   * affordance's `itemId` (or an effect-class entry id). Set ONLY by `resolveActivate`. A
   * `deploy_object`/`deploy_mine` stamps it onto the minted combatant's `deployedFrom`, which is
   * how a destroyed drone finds the deployer's system. Absent outside an activation.
   */
  activatingEntry?: string;
  /**
   * **Is this interpretation firing inside the reaction interception window's `pendingDamage`
   * gather?** (`npc-features-fielded-roster` Phase 7.1.) Set `true` ONLY by `gatherReaction`'s
   * `compileAuthoredOffers`/`compileProtectorOffers` (`reactionRegistry.ts`) when they build the
   * `EffectContext` for an accepted offer's own `outcome` — the two compilers that read
   * {@link InterpretResult.pendingDamage} back off this call's result. A
   * {@link import("./atoms").ResistPendingDamageAtom} checks this flag to decide whether setting
   * `pendingDamage.resist` will actually be READ by anyone: `true` here, silently set it; absent/
   * `false` everywhere else (a lifecycle trigger, an action, a raw attack resolution), push a
   * documented no-op advisory instead — mirroring the honest-degrade posture every other
   * reaction-window-only atom takes, just gated on a context fact instead of "reached the generic
   * interpreter at all" (the shape pre-extraction gives `reduce_pending_damage`'s siblings).
   */
  pendingDamageWindow?: boolean;
  /**
   * **Where a `forced_attack` atom queues its attack** (`npc-features-fielded-roster` Phase 13.2).
   * Shared by reference, like {@link ledger}. Set only by a caller whose resolution the referee
   * finishes with the queued attacks: `reactionRegistry.ts`'s settled-damage pass and authored
   * damage-window offers, and `resolveActivate`. Absent everywhere else, where a `forced_attack` degrades to an advisory
   * saying the attack is not resolved at this site.
   */
  forcedAttacks?: ForcedAttackRequest[];
  /** The intent's event loop, when the caller holds one (`events/loop.ts`): a queued forced attack records the event that caused it. */
  loop?: EventLoop;
  /**
   * **A `damage` atom's Intercede** (`ll1-granted-reactions` S5): its answers and the spend it
   * records, shared by reference like {@link forcedAttacks}. Set only by a caller that turns the
   * spend into `spend_reaction` commands: `resolveActivate`, the trample sweep, the damage
   * notification and an attack's automatic damage clauses. Absent, the atom offers no Intercede,
   * since nothing could spend the reaction.
   */
  intercede?: InterceptSink;
  /**
   * **Where a `choose` atom records a decline** (`decisions/0162`). Shared by reference, like
   * {@link forcedAttacks}. When a `choose` takes an option whose authored `effect` is `[]`, it
   * pushes the atom's `prompt` here: an empty option IS a decline, engine-inferred, no authored
   * facet. Set only by `resolveActivate`, which reads it back to tell a declined activation (free)
   * from one that ran and emitted nothing (charged). Absent everywhere else.
   */
  declines?: string[];
  /**
   * **The attack that opened this window** (`npc-features-fielded-roster` Phase 11.1). Set only by
   * the damage window's authored offers (`reactionRegistry.ts`'s `compileAuthoredOffers`); read by
   * `forced_attack`'s repeat form (`weapon: "triggering"`, Rebound's reflection).
   */
  triggeringAttack?: RequestAttack;
  /**
   * **The weapon a picked `forced_attack` fires** (`ll1-granted-reactions` S3): the instance key
   * the reactor answered with. Set only by `compileAuthoredOffers` for an offer whose effect
   * carries a {@link import("./atoms").ForcedAttackWeaponPick}.
   */
  pickedWeapon?: string;
  /**
   * **The random pick that bound `target`** (`chained-resolution-sequence` 1.1): set only by
   * `choose_random` on the context its body runs under. A `forced_attack` in that body carries it
   * onto its request, and the referee records it on the forced attack's `link` step.
   */
  pick?: { from: CombatantId[]; roll?: number };
  /**
   * **The attack a declared reaction is reacting to** (`ll1-granted-reactions` S2): its attacker
   * and that attack's to-hit bonus, which a `contested_roll` against the attacker rolls with. Set
   * only by `declaredReactions.ts`; a `cause_miss` there raises `attack_forced_miss` on the target.
   */
  inFlightAttack?: { attacker: CombatantId; bonus: number };
}

/** What interpreting an effect produced: the emitted steps + any advisory warnings. */
export type InterpretResult = {
  /** The grounded steps to splice into the resolution's step list (in order). */
  steps: ResolvedStep[];
  /** Advisory notes (deferred spatial facts, taken-default choices) — surfaced, not blocking. */
  warnings: string[];
  /**
   * Set when a {@link import("./atoms").ResistPendingDamageAtom} fired anywhere in this
   * interpretation while {@link EffectContext.pendingDamageWindow} was `true` — Rebound's
   * "Use → roll 1d6 → on 4+ resist". Read ONLY by `gatherReaction`'s
   * `compileAuthoredOffers`/`compileProtectorOffers` (`reactionRegistry.ts`), which OR it into the
   * offer's own `ReactionOutcome.resistant`. Omitted (never `{ resist: false }`) when the atom
   * never fired with the flag set — mirrors every other conditionally-composed field in this
   * codebase (`ReactionOutcome.reduce`/`.split`/`.redirect`).
   */
  pendingDamage?: { resist: boolean };
}

/**
 * The {@link PredicateContext} view of an {@link EffectContext}. One
 * builder for the `state`/`actors`/`rulings` projection an {@link EffectContext}
 * shares with a {@link PredicateContext}, plus the typed per-family `event` payload, so
 * a `triggered` clause's `condition`
 * reads the same event facts a `modifier` clause's can — exported so every
 * from-`EffectContext` predicate-context construction (the `if` arm here, the trigger
 * bus's condition gate, the action path's activation gate) routes through it instead
 * of an inline `{ state, actors }` literal. The raw-attack
 * `attackPredicateContext` (no `EffectContext` in scope, just `state`+ids) keeps its
 * own builder.
 */
export function predicateContextFrom(ctx: EffectContext): PredicateContext {
  return {
    state: ctx.state,
    actors: ctx.actors,
    rulings: ctx.choices,
    event: ctx.event,
    // Project the resolution-scoped fact ledger's `choose` record so a `chose` predicate
    // reaches a `triggered` clause's own `condition` (`fireTrigger`'s `evaluatePredicate`
    // call) exactly like it reaches a `modifier`'s (`attackPredicateContext`, `resolve/attackModifiers.ts`).
    chosen: ctx.ledger?.choices,
    collisions: ctx.ledger?.collisions,
    costs: ctx.ledger,
    // Project the firing clause's own carrying-instance source so a `has_status` with `source: "carrier"`
    // predicate can read it.
    carrierSource: ctx.carrierSource,
  };
}

/**
 * Resolve an {@link AtomActorRef} to a live combatant (or undefined). The
 * named-ref → id mapping (`self`/`target`/`attacker ?? self`/raw) is the *same*
 * resolution a predicate uses, so it delegates to the shared {@link resolveActorId}
 * rather than re-implementing it (one implementation, no drift). The two extra
 * behaviors an effect needs and a predicate doesn't are layered here: an omitted
 * ref (`undefined`) defaults to `target ?? self` (the effect's primary recipient),
 * and the resolved id is looked up to the full combatant.
 */
function resolveActor(ref: AtomActorRef | undefined, ctx: EffectContext): Combatant | undefined {
  const id =
    ref === undefined ? ctx.actors.target ?? ctx.actors.self : resolveActorId(ref, ctx.actors);
  return id ? ctx.state.combatants[id] : undefined;
}

/**
 * Read the named {@link StatScaledAmount} stat off a combatant's frozen `base` — the
 * closed `stat` enum's one indirection point (`"size"` → `ms_type_i_flight_system`'s
 * heat cost, `"maxHp"` → `heal`'s stat-relative amount, `"speed"` → Charge/Avalanche
 * Charge's Speed-scaled move distance, `npc-features-fielded-roster` Phase 3.3). Extend
 * here (+ the `StatScaledAmount.stat` union in `atoms.ts`) when a new demand surfaces a
 * further stat-scaled magnitude.
 */
function statValue(who: Combatant, stat: StatScaledAmount["stat"]): number {
  switch (stat) {
    case "size":
      return who.base.size;
    case "maxHp":
      return who.base.maxHp;
    case "speed":
      return who.base.speed;
  }
}

/**
 * Resolve a {@link MagnitudeValue} to a plain number: a
 * bound number passes through unchanged (the common case); a {@link StatScaledAmount}
 * reads the referenced combatant's frozen stat at INTERPRET time (unlike a `{VAL}` template,
 * which binds once at load — see that type's own doc for why this needs to be later); a
 * {@link import("./atoms").TierScaledAmount} (`npc-features-fielded-roster` Phase 3.4, demand (d)) reads the
 * referenced combatant's own frozen {@link import("@lancer-vtt/domain").Combatant.tier} and
 * indexes the authored `[T1, T2, T3]` tuple by `tier - 1` — a combatant with NO frozen tier
 * (a mech, a PC, an object) reads the T1 (index 0) value and, when `warnings` is passed,
 * records an advisory (never a crash, mirroring every other "unresolved fact" degrade in
 * this file). `who`
 * defaults to `self`; an unresolved actor reads as `0` (an honest no-op magnitude, never a
 * crash — mirrors every other "unresolved ref" degrade in this file).
 *
 * **Rounding (the ONE pinned rule):** a fractional
 * `base * mult + add` rounds UP (`Math.ceil`) — the Lancer core rulebook's Golden Rule II,
 * "always round up (to the nearest whole number)," the RAW default for every rounded
 * fraction not otherwise specified (confirmed against the printed pg_patch clause, which
 * states no override). This is the single resolver every stat-scaled magnitude (`add_heat`'s
 * Size read, `heal`'s maxHp read) goes through — no parallel rounding math anywhere else.
 * `TierScaledAmount` is a plain tuple index, not a rounded formula, so the rounding rule
 * doesn't apply to it.
 *
 * A {@link import("./atoms").TargetCountScaledAmount} (the `{TARGET_COUNT}` binding) reads
 * {@link EffectContext.targetCount} — the firing attack's settled target count — applies the
 * authored counting rule (`"all"` / `"beyond_first"`), and runs the SAME `count * mult + add`
 * → `Math.ceil` formula `StatScaledAmount` does, so the rounding rule DOES apply to it. Its
 * `mult` may itself be a `TierScaledAmount` (Cluster Munitions' `{2/3/4}` per extra target),
 * resolved by one nested call into this same function. A firing with no target count reads a
 * single target and records an advisory, mirroring the no-frozen-tier degrade.
 */
function resolveMagnitude(value: MagnitudeValue, ctx: EffectContext, warnings?: string[]): number {
  if (typeof value === "number") return value;
  if ("moved" in value) {
    if (ctx.event?.family !== "move") {
      warnings?.push(`no move fired this clause — reading 0 spaces (advisory, moved_spaces_magnitude)`);
      return 0;
    }
    return Math.ceil(ctx.event.distance * (value.mult ?? 1) + (value.add ?? 0));
  }
  if ("rolled" in value) {
    const total = ctx.rolled?.[value.rolled];
    if (total === undefined) {
      warnings?.push(`no roll bound as "${value.rolled}" encloses this atom — reading 0 (advisory, rolled_magnitude_binding)`);
      return 0;
    }
    return Math.ceil(total * (value.mult ?? 1) + (value.add ?? 0));
  }
  // The `{TARGET_COUNT}` binding — checked FIRST because it is the one variant with no `who`
  // (target count is a fact about the RESOLUTION, not about any combatant), so it must not fall
  // through to the `resolveActor` line below.
  if ("targetCount" in value) {
    if (ctx.targetCount === undefined) {
      warnings?.push(
        `no target count on this firing — reading a single target (advisory, target_count_magnitude)`,
      );
    }
    const n = ctx.targetCount ?? 1;
    const counted = value.targetCount === "beyond_first" ? Math.max(0, n - 1) : n;
    // `mult` nests exactly one level (a plain number or a `TierScaledAmount`) — Cluster
    // Munitions is the PRODUCT of the two bindings, `{2/3/4}` per target beyond the first.
    const mult = value.mult === undefined ? 1 : typeof value.mult === "number" ? value.mult : resolveMagnitude(value.mult, ctx, warnings);
    return Math.ceil(counted * mult + (value.add ?? 0));
  }
  const who = resolveActor(value.who ?? "self", ctx);
  if ("tier" in value) {
    if (who?.tier === undefined) {
      warnings?.push(
        `${who?.name ?? value.who ?? "self"}: no frozen NPC tier — reading the T1 magnitude (advisory, npc_tier_magnitude)`,
      );
      return value.tier[0];
    }
    // `who.tier` is `1|2|3`; `noUncheckedIndexedAccess` can't narrow a computed tuple index,
    // so the `?? value.tier[0]` fallback is defensive-only — it's never actually reached for
    // an in-range `Combatant.tier`.
    return value.tier[who.tier - 1] ?? value.tier[0];
  }
  const base = who ? statValue(who, value.stat) : 0;
  return Math.ceil(base * (value.mult ?? 1) + (value.add ?? 0));
}

/**
 * The shared **target-resolution prologue**: resolve `ref` to a live combatant and run
 * `fn` only when it lands. Every grounded-target atom arm begins with this
 * "resolve-or-bail" preamble; folding it here keeps each handler to its genuine body.
 * The caller passes the ref explicitly — `atom.target` (status/damage/set_hp) or
 * `atom.target ?? "self"` (resource atoms default to the acting combatant) — so the
 * defaulting stays visible per-atom.
 */
function withTarget(
  ref: AtomActorRef | undefined,
  ctx: EffectContext,
  fn: (tgt: Combatant) => void,
): void {
  const tgt = resolveActor(ref, ctx);
  if (tgt) fn(tgt);
}

/**
 * Heavy Frame's veto at an interpreter site: `true` (and a warning naming the carrying entity)
 * when `targetId` is immune to `blocks` from this resolution's acting combatant (`self`) because
 * that combatant is a smaller character. See `smallerCharacterImmunity` (`seed.ts`).
 */
function vetoedBySmallerCharacter(args: { ctx: EffectContext; targetId: string; blocks: "forced_move" | { status: string }; what: string; warnings: string[] }): boolean {
  const { ctx, targetId, blocks, what, warnings } = args;
  const actorId = selfId(ctx);
  const source = smallerCharacterImmunity({ state: ctx.state, targetId, actorId, blocks, overlay: ctx.overlay, ...(ctx.event !== undefined ? { event: ctx.event } : {}) });
  if (source === undefined || actorId === undefined) return false;
  warnings.push(smallerCharacterImmunityWarning({ state: ctx.state, targetId, actorId, source, what }));
  return true;
}

/** Resolve the `self` combatant (the effect's owner), for `source`-stamping a status. */
function selfId(ctx: EffectContext): string | undefined {
  return ctx.actors.self;
}

// ── the interpreter ────────────────────────────────────────────────────────────────

/**
 * Interpret an effect-atom list against the context, emitting grounded steps. Walks
 * the atoms in order; control-flow atoms recurse on the taken branch. Pure given the
 * `dice`/`choices` sources (all randomness/choice flows through them and is recorded
 * into the emitted steps).
 */
export function interpretEffect(atoms: readonly EffectAtom[], ctx: EffectContext): InterpretResult {
  // A caller with no resolution ledger (an activation, a mine's clauses) gets one, in place like the
  // `site`/`movePath` writes it reads back, so its atoms still see each other's facts (a push
  // chaining off the last one's landing). Nothing settles it, so an event raised into it throws
  // rather than vanishing (`decisions/0211` §4).
  ctx.ledger ??= createResolutionLedger("refuses");
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];
  // Mutable accumulator threaded through every recursive `interpretAtom` call the same way
  // `steps`/`warnings` are — a `resist_pending_damage` atom nested inside `if`/`roll` mutates it
  // in place (`atoms.ts`'s own doc: this is the one atom that reaches the generic interpreter on
  // purpose, unlike its pre-extracted `reduce`/`split`/`redirect_pending_damage` siblings).
  const pendingDamage = { resist: false };
  for (const atom of atoms) interpretAtom({ atom, ctx, steps, warnings, pendingDamage });
  return { steps, warnings, ...(pendingDamage.resist ? { pendingDamage } : {}) };
}

/**
 * One atom handler: the body of a former switch arm. Receives the **narrowed** atom
 * (the registry keys each op to its concrete atom type), the context, and the output
 * accumulators. Control-flow handlers (`if`/`choose`/`force_save`/`roll`) recurse on
 * the taken branch via {@link interpretAtom}.
 */
type AtomHandlerArgs<A extends EffectAtom> = {
  atom: A;
  ctx: EffectContext;
  steps: ResolvedStep[];
  warnings: string[];
  /** The `resist_pending_damage` accumulator — see {@link interpretEffect}'s own doc. */
  pendingDamage: { resist: boolean };
};
type AtomHandler<A extends EffectAtom> = (args: AtomHandlerArgs<A>) => void;

/** The op→handler registry: one entry per {@link EffectAtom} arm. */
type AtomRegistry = { [A in EffectAtom as A["op"]]: AtomHandler<A> };

/**
 * The atom-interpretation **registry** — the op→body dispatch table. The
 * grounded-target arms open with the shared {@link withTarget} prologue. {@link
 * interpretAtom} looks an atom's `op` up here and runs it; an unknown `op` (not
 * type-reachable over the closed {@link EffectAtom} union) is a silent no-op.
 */
const ATOM_REGISTRY: AtomRegistry = {
  add_status: ({ atom, ctx, steps, warnings }) =>
    withTarget(atom.target, ctx, (tgt) => {
      // Idempotent at resolve time too: don't emit a no-op add for a status already present
      // — UNLESS an EARLIER atom in this SAME interpretation already queued that exact
      // instance's removal. `interpretEffect` reads one frozen `ctx.state` snapshot for
      // every atom's CONDITION reads (no atom sees an earlier atom's effect on state), but
      // the `steps` accumulator itself is threaded live across the whole atom list (and
      // into nested `if`/`choose` branches) — so a `remove_status`/`remove_status_from_source`
      // immediately preceding this `add_status` in the SAME effect list, targeting the SAME
      // combatant+status, is visible here even though `tgt.statuses` (the frozen snapshot)
      // still shows it present. Without this check, an unconditional remove-then-add pair
      // silently DESTROYS the status instead of refreshing it: the remove step is queued,
      // then this check reads the stale frozen state and skips the add outright, leaving
      // NEITHER the old nor a new instance behind (`t_exemplar:1`'s "new marks from any
      // character replace existing marks" is the case this protects).
      // A same-target recast with NO preceding removal in this interpretation still no-ops —
      // this only widens the ALREADY-explicitly-removed case.
      const alreadyQueuedRemoval = steps.some(
        (s) => s.op === "status_remove" && s.target === tgt.id && s.status === atom.status,
      );
      if (!alreadyQueuedRemoval && tgt.statuses.some((s) => s.name === atom.status)) return;
      // Immunity, status scope: a target immune to the NAMED status blocks the add at
      // EMISSION — a note, never a step (grounded steps stay pure; a blocked status simply never
      // becomes one). Covers every authored `add_status` atom (table-driven forced-save
      // consequences included — `force_save`'s on_fail/on_pass branches recurse through
      // this same handler).
      // A status an earlier atom in this list already removed confers nothing: the Grounding Mine
      // lands a flyer and then knocks it Prone, though Flying is immune to Prone.
      const removed = new Set(steps.flatMap((s) => (s.op === "status_remove" && s.target === tgt.id ? [s.status] : [])));
      const immunityState =
        removed.size === 0 ? ctx.state : { ...ctx.state, combatants: { ...ctx.state.combatants, [tgt.id]: { ...tgt, statuses: tgt.statuses.filter((s) => !removed.has(s.name)) } } };
      if (statusBlockedByImmunity({ state: immunityState, targetId: tgt.id, name: atom.status, overlay: ctx.overlay })) {
        warnings.push(`${tgt.name}: IMMUNE to ${atom.status} — status not applied (RAW p.68).`);
        return;
      }
      // Heavy Frame: a status the target is immune to only from a SMALLER acting character.
      if (vetoedBySmallerCharacter({ ctx, targetId: tgt.id, blocks: { status: atom.status }, what: `knocked ${atom.status}`, warnings })) return;
      // Grounded (the Bouncing Mine's "can't fly"): no content can lift its carrier into flight.
      if (atom.status === FLYING_STATUS && hasStatus(tgt.statuses, GROUNDED_STATUS)) {
        warnings.push(`${tgt.name}: GROUNDED — can't fly; Flying not applied.`);
        return;
      }
      // An authored literal `sourceOverride` wins over the auto-derived acting combatant —
      // see `AddStatusAtom.sourceOverride`'s doc for why (item-identity provenance a
      // `has_status` with `source: { id }` clause elsewhere can compare against, distinct from the
      // dynamic-caster `has_status` with `source: "carrier"` reads).
      const src = atom.sourceOverride ?? selfId(ctx);
      // Resolve the atom's symbolic `partner` ref (same
      // vocabulary as `target`) to a live combatant id, e.g. `"attacker"` from the
      // target's perspective. An unresolved ref (dangling/missing actor) is omitted
      // rather than stamping a bogus id.
      const partner = atom.partner !== undefined ? resolveActorId(atom.partner, ctx.actors) : undefined;
      const step: StatusAddStep = {
        op: "status_add",
        target: tgt.id,
        status: atom.status,
        ...(atom.until !== undefined ? { expiry: atom.until } : {}),
        // Stamp `appliedTurn` EXPLICITLY at emission for a `turn`/`this_turn` expiry, mirroring
        // `interpretCreateArea`'s unconditional `AreaEffect.appliedTurn` treatment — this is
        // the one and only place it lands for a fresh, content-driven `add_status`. Never
        // defaulted downstream (see `StatusAddStep.appliedTurn`'s doc): omitting it here
        // leaves the instance's `turn`-kind expiry unable to skip its own application turn.
        ...(atom.until?.kind === "turn" || atom.until?.kind === "this_turn" ? { appliedTurn: ctx.state.activation.turnOrdinal } : {}),
        ...(src !== undefined ? { source: src } : {}),
        ...(partner !== undefined ? { partner } : {}),
        ...(ctx.statusCause !== undefined ? { cause: ctx.statusCause } : {}),
        ...(atom.endsOn !== undefined ? { endsOn: atom.endsOn } : {}),
      };
      steps.push(step);
      // Fall-trigger — a co-effect, NOT the Prone-immunity block just above (compose, don't tangle): a
      // `Flying` target that just landed ANY can't-move status also grounds at that
      // same emission, so a second `status_remove` step for Flying rides in the SAME
      // steps list right behind the add — `invertSteps` (domain) reverses BOTH for
      // free on undo/redo, no bespoke inverse plumbing. "Can't-move" is read off the
      // ONE shared bar {@link voluntaryMoveBar} already enforces for movement legality
      // (Immobilized/Stunned/Shut Down/Down and Out), computed against the statuses
      // the target carries AFTER this add, so it never drifts from that wall.
      if (hasStatus(tgt.statuses, FLYING_STATUS)) {
        const wouldBar =
          voluntaryMoveBar({ statuses: [...tgt.statuses, { name: atom.status, expiry: { kind: "until_removed" } }] }) !=
          null;
        if (wouldBar) {
          const flying = tgt.statuses.find((s) => s.name === FLYING_STATUS)!;
          const groundStep: StatusRemoveStep = {
            op: "status_remove",
            target: tgt.id,
            status: FLYING_STATUS,
            ...(flying.expiry !== undefined ? { expiry: flying.expiry } : {}),
            ...(flying.appliedTurn !== undefined ? { appliedTurn: flying.appliedTurn } : {}),
            ...(flying.source !== undefined ? { source: flying.source } : {}),
            ...(flying.partner !== undefined ? { partner: flying.partner } : {}),
            ...(flying.cause !== undefined ? { cause: flying.cause } : {}),
          };
          steps.push(groundStep);
        }
      }
    }),

  // Plant a weapon-pinned follow-up reservation on the acting attacker. See `ReserveFollowupAtom`'s
  // doc — `source`=self, `partner`=the just-hit primary target (the excluded character),
  // `weapon`=the triggering weapon INSTANCE read off the attack event payload (the one facet
  // no author ref can express). The planted status IS the reservation the room-gate later
  // matches a follow-up `request_attack` against (same weapon ∧ different target ∧ acting turn).
  reserve_followup: ({ atom, ctx, steps, warnings }) => {
    const self = selfId(ctx);
    if (self === undefined) return; // no acting attacker bound — honest no-op
    const attacker = ctx.state.combatants[self];
    if (attacker === undefined) return;
    // Idempotent at resolve time (mirrors `add_status`): a live reservation of this name plants
    // nothing again. The real 1/round wall is the trigger clause's own `n{max:1}` cadence cap.
    if (attacker.statuses.some((s) => s.name === atom.status)) return;
    const weapon = ctx.event?.family === "attack" ? ctx.event.weaponInstance : undefined;
    if (weapon === undefined) {
      warnings.push(
        `reserve_followup: no triggering weapon instance on the attack event — "${atom.status}" reservation not planted (fired off a non-resolved-attack context?)`,
      );
      return;
    }
    const partner = ctx.actors.target; // the just-hit character, excluded from the follow-up's targets
    const step: StatusAddStep = {
      op: "status_add",
      target: self,
      status: atom.status,
      ...(atom.until !== undefined ? { expiry: atom.until } : {}),
      // Same explicit-at-emission stamp as `add_status`, above — this atom's own real content
      // (`t_executioner:1`'s Backswing Cut, `until: "source_next_turn_start"`) is a `turn`-kind
      // expiry too.
      ...(atom.until?.kind === "turn" || atom.until?.kind === "this_turn" ? { appliedTurn: ctx.state.activation.turnOrdinal } : {}),
      source: self,
      ...(partner !== undefined ? { partner } : {}),
      weapon,
    };
    steps.push(step);
  },

  remove_status: ({ atom, ctx, steps }) =>
    withTarget(atom.target, ctx, (tgt) => {
      // `exceptSource`: an instance this actor caused stays (Grease Monkey 2).
      const kept = atom.exceptSource === undefined ? undefined : resolveActor(atom.exceptSource, ctx)?.id;
      const inst = tgt.statuses.find((s) => s.name === atom.status && (kept === undefined || s.source !== kept));
      if (inst === undefined) return; // nothing to remove
      // Record the live instance's facets onto the removal. A bare `{op, target, status}`
      // removal is enough only while every status it removes gets its mechanics from the pack
      // overlay (the name alone rebuilds it). A GM ruling carries its mechanics ON the
      // instance, so a bare removal would make the undo restore a chip that renders
      // identically and does nothing. This is the path a `consumeOnAttack` ruling's own
      // compiled self-removal takes.
      const step: StatusRemoveStep = {
        op: "status_remove",
        target: tgt.id,
        status: atom.status,
        ...statusFacetsFromInstance(inst),
      };
      steps.push(step);
    }),

  // The source-scoped CROSS-TARGET sibling of `remove_status` — scan EVERY combatant (not just
  // the resolution's own target) for a live instance named `atom.status` whose `source` is the
  // acting combatant (self), removing each match. Mirrors `shutdown.ts`'s tech-caused
  // multi-instance removal: a direct emission carrying the live instance's full facets (not a
  // generic inverse-of-add), since the instance is already in hand. A per-TARGET exclusive mark
  // is NOT this atom's job — that authors a plain `remove_status { target: "target" }`
  // (`t_exemplar:1`).
  remove_status_from_source: ({ atom, ctx, steps }) => {
    const src = selfId(ctx);
    if (src === undefined) return; // no acting combatant to scope the scan by — honest no-op
    for (const c of Object.values(ctx.state.combatants)) {
      for (const inst of c.statuses) {
        if (inst.name !== atom.status || inst.source !== src) continue;
        const step: StatusRemoveStep = {
          op: "status_remove",
          target: c.id,
          status: inst.name,
          ...statusFacetsFromInstance(inst),
        };
        steps.push(step);
      }
    }
  },

  pause_status_expiry: interpretPauseStatusExpiry,

  // `add_resource` defaults to `self` (the acting combatant), unlike status which targets.
  add_resource: ({ atom, ctx, steps }) =>
    withTarget(atom.target ?? "self", ctx, (tgt) => {
      // A capped grant tops the resource up to `max` and no further.
      const delta = atom.max !== undefined && atom.delta > 0 ? Math.max(0, Math.min(atom.delta, atom.max - (tgt.current.resources?.[atom.key] ?? 0))) : atom.delta;
      if (delta === 0) return;
      const step: ResourceDeltaStep = { op: "resource_delta", target: tgt.id, key: atom.key, delta };
      steps.push(step);
    }),

  set_resource: ({ atom, ctx, steps }) =>
    withTarget(atom.target ?? "self", ctx, (tgt) => {
      const prev = tgt.current.resources?.[atom.key] ?? 0;
      if (prev === atom.val) return; // no-op
      const step: ResourceSetStep = { op: "resource_set", target: tgt.id, key: atom.key, val: atom.val, prev };
      steps.push(step);
    }),

  // Grant a bonus use of `atom.actionId` THIS TURN — a plain
  // `resource_delta` +amount onto the namespaced `grantedActionAllowanceKey` bag, exactly like
  // `add_resource` (grounded ~1:1 to a step; no side-channel threading needed). Defaults to
  // `self` (the triggered clause's carrier), unlike `add_status`'s target-default, mirroring
  // `add_resource`'s own `?? "self"` — this is a grant onto the ACTOR's own budget, not a
  // status landing on a target.
  grant_action_allowance: ({ atom, ctx, steps }) =>
    withTarget(atom.who ?? "self", ctx, (tgt) => {
      const amount = atom.amount ?? 1;
      if (amount <= 0) return;
      const step: ResourceDeltaStep = {
        op: "resource_delta",
        target: tgt.id,
        key: grantedActionAllowanceKey(atom.actionId),
        delta: amount,
      };
      steps.push(step);
    }),

  // Plant a per-verb cost earmark.
  // Deliberately the same SHAPE as `grant_action_allowance` just above (a `resource_delta` onto a
  // namespaced key, defaulting to `self`) and a deliberately DIFFERENT namespace: the allowance
  // buys a bonus USE, this buys a zero COST, and a carrier may hold both at once without either
  // reading the other's counter.
  grant_free_action_use: ({ atom, ctx, steps }) =>
    withTarget(atom.who ?? "self", ctx, (tgt) => {
      const amount = atom.amount ?? 1;
      if (amount <= 0) return;
      const step: ResourceDeltaStep = {
        op: "resource_delta",
        target: tgt.id,
        key: freeActionUseKey(atom.verb),
        delta: amount,
      };
      steps.push(step);
    }),

  extra_damage: interpretExtraDamage,

  add_heat: ({ atom, ctx, steps, warnings }) =>
    withTarget(atom.target, ctx, (tgt) => {
      // `atom.amount` may be a bound number, a `StatScaledAmount`
      // (`ms_type_i_flight_system`'s "Size +1 Heat"), or a `TierScaledAmount` — resolve it
      // to a plain number off THIS combatant's own live state before anything below runs.
      const resolved = resolveMagnitude(atom.amount, ctx, warnings);
      const counted = atom.upTo === undefined ? 0 : (tgt.current.resources?.[atom.upTo.key] ?? 0);
      const capped = atom.upTo === undefined ? resolved : Math.max(0, Math.min(resolved, atom.upTo.max - counted));
      // Heat from the attack this fired inside, against a target that resists it (a Brace): halved, rounding up.
      const halvings = ctx.heatHalvings?.target === tgt.id ? ctx.heatHalvings.times : 0;
      let amount = capped;
      for (let i = 0; i < halvings; i++) amount = Math.ceil(amount / 2);
      if (atom.upTo !== undefined && amount > 0) steps.push({ op: "resource_delta", target: tgt.id, key: atom.upTo.key, delta: amount });
      // INVADE's "2 Heat Damage": add to the heat *track*. A target with NO heat track
      // at all (`maxHeat` absent — the Biological signature) takes the heat as Energy damage
      // instead (`heatAsEnergyDamage`), never a no-op.
      if (tgt.base.maxHeat === undefined) {
        // An energy packet sourced to the clause's carrier, on the target as the steps ahead of it left it.
        const taker = steps.length > 0 ? (applyResolved(ctx.state, steps).combatants[tgt.id] ?? tgt) : tgt;
        const result = heatAsEnergyDamage({ target: taker, amount, dice: ctx.dice, turnOrdinal: ctx.state.activation.turnOrdinal, selfCheck: selfCheckContext(ctx.state, ctx.overlay, { loop: ctx.loop }), source: ctx.actors.self ?? null });
        steps.push(...result.steps);
        warnings.push(...result.warnings);
        return;
      }
      // Heat gained runs the heat cascade, so heat past the cap overheats (RAW p.67: mark 1 Stress,
      // roll the Overheating table, clear heat) as attack heat does; under the cap it is the same
      // single `heat_delta`. A negative amount (cooling) stays a plain clamped delta.
      if (amount > 0) {
        const cascade = resolveHeatCascade({ target: tgt, amount, dice: ctx.dice, turnOrdinal: ctx.state.activation.turnOrdinal, selfCheck: selfCheckContext(ctx.state, ctx.overlay, { choices: ctx.choices, loop: ctx.loop }) });
        steps.push(...cascade.steps);
        warnings.push(...cascade.warnings);
        destroyWeakGrunt(tgt, amount);
        return;
      }
      const heat = heatDeltaStep(tgt, amount);
      if (heat) steps.push(heat);
      if (heat) raiseHeatCleared(ctx.loop, { combatant: tgt.id, before: tgt.current.heat ?? 0, after: (tgt.current.heat ?? 0) + heat.delta });
      destroyWeakGrunt(tgt, heat?.delta ?? 0);

      // A Weak Grunt is destroyed by heat from any source but its own systems and weapons.
      function destroyWeakGrunt(target: typeof tgt, gained: number): void {
        const destroy = foreignHeatDestroy({ target, amount: gained, from: ctx.actors.self ?? "gm", steps });
        if (destroy) {
          steps.push(destroy);
          warnings.push(`${target.name}: a Weak Grunt takes heat from another source and is destroyed.`);
        }
      }
    }),

  set_hp: ({ atom, ctx, steps }) =>
    withTarget(atom.target, ctx, (tgt) => {
      const before = tgt.current.hp;
      const after = clampHp(tgt, atom.val);
      const delta = after - before;
      if (delta !== 0) steps.push({ op: "hp_delta", target: tgt.id, delta });
    }),

  // Grow the acting unit's move budget. Checked at emission as well as playback, so a grant aimed
  // at a unit that is not taking its turn says so instead of vanishing.
  grant_move: ({ atom, ctx, steps, warnings }) =>
    withTarget(atom.who ?? "self", ctx, (tgt) => {
      const cells = resolveMagnitude(atom.cells, ctx, warnings);
      if (cells <= 0) return;
      const turn = ctx.state.activation.turn;
      if (turn.phase !== "acting" || turn.actor !== tgt.id) {
        warnings.push(`${tgt.name}: not taking its turn — no move budget to grant ${cells} more spaces to`);
        return;
      }
      const step: MoveBudgetDeltaStep = { op: "move_budget_delta", target: tgt.id, delta: cells };
      steps.push(step);
    }),

  // A cost: raised as an event and settled at `resolve()`'s end, where a replacement (Efficiency)
  // can still waive it. Affordability is checked here, before the roll, net of pending spends.
  expend_charges: ({ atom, ctx, warnings }) =>
    withTarget("self", ctx, (self) => {
      const pool = heldLimitedPool(self, atom.items);
      if (pool === undefined) {
        warnings.push(`${self.name}: holds no Limited pool for ${atom.items.join("/")} — can't spend ${atom.count}`);
        return;
      }
      const cost: ExpendChargesEvent = { kind: "expend_charges", carrier: self.id, pool: pool.slot, amount: atom.count };
      if (!affordable({ ...cost, max: pool.max }, ctx.state, ctx.ledger)) {
        warnings.push(`${self.name}: has ${chargesLeft({ ...cost, max: pool.max }, ctx.state, ctx.ledger)} charge(s) left — can't spend ${atom.count}`);
        return;
      }
      emitEvent(ctx.ledger!, cost);
    }),

  // A cadence cap's use (`event-engine-costs` 6.1): raised into the resolution's ledger when one
  // settles it, settled in place otherwise. Per target when the atom names one.
  use_cap: ({ atom, ctx, steps, warnings }) =>
    withTarget("self", ctx, (self) => {
      const on = atom.on === undefined ? undefined : resolveActor(atom.on, ctx)?.id;
      if (atom.on !== undefined && on === undefined) return;
      const use = { kind: "resource_cap_use" as const, carrier: self.id, key: resourceCapRawKey(atom.cap, on) };
      if (!affordable({ ...use, max: atom.cap.max }, ctx.state, ctx.ledger)) {
        warnings.push(`${self.name}: ${atom.cap.key} is already used${on !== undefined ? ` on ${ctx.state.combatants[on]?.name ?? on}` : ""} — nothing spent`);
        return;
      }
      if (ctx.ledger?.settlement === "settles") emitEvent(ctx.ledger, use);
      else steps.push(...capUseSteps([use], ctx.loop));
    }),

  overshield: ({ atom, ctx, steps }) =>
    withTarget(atom.target, ctx, (tgt) => {
      const step = grantOvershieldStep({ combatant: tgt, value: atom.value });
      if (step) steps.push(step);
    }),

  clear_burn: ({ atom, ctx, steps }) =>
    withTarget(atom.target, ctx, (tgt) => {
      const prev = tgt.current.burn ?? 0;
      if (prev > 0) steps.push({ op: "burn_set", target: tgt.id, burn: 0, prev });
    }),

  begin_rodeo: ({ atom, ctx, steps, warnings }) =>
    withTarget("self", ctx, (self) => {
      const legal = checkRodeoLegality(ctx.state, self.id);
      if (!legal.ok) {
        warnings.push(legal.error);
        return;
      }
      steps.push(...beginRodeoSteps(ctx.state, self.id, atom.rider_statuses));
    }),

  end_rodeo: ({ atom, ctx, steps }) =>
    withTarget("self", ctx, (self) => {
      steps.push(...endRodeoSteps(ctx.state, self.id, atom.lockout));
    }),

  // The Scan reveal: read the
  // target's PRIOR disclosure level (never mutate here — the interpreter only records; the
  // actual write happens at playback via `applyResolvedStep`'s `disclosure_set` case, which
  // calls the SAME `withDisclosureLevel` helper the GM's manual `set_disclosure` reducer case
  // calls). A target already at `level` emits nothing (mirrors `set_resource`'s no-op) — the
  // "already there" check uses `disclosureOf`'s DEFAULTED read, matching what a
  // viewer would actually see. The step's recorded `prev`, though, is the RAW stored field
  // (`tgt.disclosure`, never `disclosureOf`) — undo must restore the exact prior raw state,
  // including a genuinely "never set" combatant, losslessly (which is also why
  // `SetDisclosureCommand.disclosure` is optional; the GM's manual `set_disclosure` reducer
  // case likewise captures `c.disclosure` raw). Raise-only: a target already at or past `level`
  // emits nothing, so a Lotus field's reveal never undoes a GM's fuller disclosure.
  reveal: ({ atom, ctx, steps }) =>
    withTarget(atom.target, ctx, (tgt) => {
      if (DISCLOSURE_RANK[disclosureOf(tgt)] >= DISCLOSURE_RANK[atom.level]) return;
      const step: DisclosureSetStep = { op: "disclosure_set", target: tgt.id, disclosure: atom.level, prev: tgt.disclosure };
      steps.push(step);
    }),

  // `amount` is a `MagnitudeValue` (the same shape `add_heat` takes) — a stat-relative heal
  // (e.g. pg_patch's half of the TARGET's own max HP), a tier-scaled heal, or a plain bound
  // number, through the
  // ONE `resolveMagnitude` resolver (pinned rounding rule: round UP, Golden Rule II — see its
  // doc). Same clampHp + hp_delta path as `set_hp`, just computed relative to current HP.
  heal: ({ atom, ctx, steps, warnings }) =>
    withTarget(atom.target, ctx, (tgt) => {
      const before = tgt.current.hp;
      const healAmount = resolveMagnitude(atom.amount, ctx, warnings);
      const after = clampHp(tgt, before + healAmount);
      const delta = after - before;
      if (delta !== 0) steps.push({ op: "hp_delta", target: tgt.id, delta });
    }),

  // A standalone, mitigated, cascade-integrated hit for a clause firing outside any attack
  // resolution — see `interpretDamage`'s own doc.
  damage: interpretDamage,

  // `reduce_pending_damage`/`split_pending_damage`/`redirect_pending_damage` are RESERVED for
  // the reaction interception
  // window (`gatherReaction`, `reactionRegistry.ts`) — the ONLY place that recognizes them (it
  // extracts them from the atom list before this generic path ever sees them). Reached here means
  // the atom was authored somewhere the window doesn't gather (a lifecycle trigger, an `action`,
  // …) — an honest advisory no-op, mirroring the empty `builtin` hatch, never a silent drop or a
  // crash.
  reduce_pending_damage: ({ warnings }) =>
    warnings.push(
      "reduce_pending_damage: only meaningful within the reaction window's on_damage_taken gather — no-op here (advisory)",
    ),
  split_pending_damage: ({ warnings }) =>
    warnings.push(
      "split_pending_damage: only meaningful within the reaction window's automatic on_damage_taken gather — no-op here (advisory)",
    ),
  redirect_pending_damage: ({ warnings }) =>
    warnings.push(
      "redirect_pending_damage: only meaningful within the reaction window's protector-gather — no-op here (advisory)",
    ),

  // `resist_pending_damage` (`npc-features-fielded-roster` Phase 7.1) is a DIFFERENT shape from
  // its three siblings just above: it is NOT pre-extracted, so it genuinely reaches this generic
  // path on every firing, including the one that matters (Rebound's roll nested inside `roll`'s
  // `on_hit`, which a top-level pre-extraction scan structurally can't reach). Whether it DOES
  // anything is gated on `ctx.pendingDamageWindow` instead of on "did I reach the generic
  // interpreter at all" — see `atoms.ts`'s `ResistPendingDamageAtom` doc and this file's own
  // `EffectContext.pendingDamageWindow` doc for the full account.
  resist_pending_damage: ({ ctx, warnings, pendingDamage }) => {
    if (ctx.pendingDamageWindow) {
      pendingDamage.resist = true;
    } else {
      warnings.push(
        "resist_pending_damage: only meaningful within the reaction window's pendingDamage gather — no-op here (advisory)",
      );
    }
  },

  forced_move: interpretForcedMove,
  move_self_toward_target: interpretMoveSelfTowardTarget,
  deploy_object: interpretDeployObject,
  deploy_mine: interpretDeployMine,
  recall_object: interpretRecallObject,
  move_up_to: interpretMoveUpTo,
  despawn: ({ atom, ctx, steps, warnings }) =>
    withTarget(atom.target, ctx, (tgt) => {
      if (tgt.deployedFrom === undefined) {
        warnings.push(`despawn: ${tgt.name} was not deployed by a system (recorded as a no-op)`);
        return;
      }
      const step: DespawnObjectStep = { op: "despawn_object", combatant: tgt };
      steps.push(step);
    }),
  reload: interpretReload,
  redeploy_object: interpretRedeployObject,
  repair_object_group: interpretRepairObjectGroup,
  create_area: interpretCreateArea,
  force_save: interpretForceSave,
  skill_check: interpretSkillCheck,
  roll: interpretRoll,

  if: ({ atom, ctx, steps, warnings, pendingDamage }) => {
    const pctx = predicateContextFrom(ctx);
    const branch = evaluatePredicate(atom.cond, pctx) ? atom.then : atom.else ?? [];
    for (const a of branch) interpretAtom({ atom: a, ctx, steps, warnings, pendingDamage });
  },

  choose: interpretChoose,
  for_each: interpretForEach,
  choose_random: interpretChooseRandom,
  choose_from_area: interpretChooseFromArea,
  place_adjacent: interpretPlaceAdjacent,
  teleport: interpretTeleport,
  move_spaces: interpretMoveSpaces,
  destroy_system: interpretDestroySystem,
  forced_attack: interpretForcedAttack,
  contested_roll: interpretContestedRoll,

  // A declared reaction makes the attack on its target miss: the fact lands under the declaration,
  // and that target's attempt reads it (`event-engine-to-hit` R11).
  cause_miss: ({ ctx, warnings }) => {
    const { target, self } = ctx.actors;
    if (ctx.inFlightAttack !== undefined && ctx.loop !== undefined && target !== undefined && self !== undefined) ctx.loop.raise({ kind: "attack_forced_miss", target, by: self }, { trigger: () => undefined });
    else warnings.push("cause_miss: only meaningful in a reaction to an attack's declaration — no-op here (advisory)");
  },

  builtin: ({ atom, warnings }) => {
    // The first-party hatch: the registry is empty — a referenced
    // builtin is surfaced as a deferral, never a crash.
    warnings.push(`builtin "${atom.id}" not registered (deferred — first-party hatch is post-v1, D1)`);
  },

  // Surface an authored GM-facing warning verbatim — no step, just the SAME `warnings`
  // channel every honest no-op degrade already rides (Impale's un-automated
  // pull-to-endpoint, `npc-features-fielded-roster` Phase 6.2). See `atoms.ts`'s doc.
  advisory: ({ atom, warnings }) => {
    warnings.push(atom.message);
  },
};

function interpretAtom(args: AtomHandlerArgs<EffectAtom>): void {
  const { atom, ctx, steps, warnings, pendingDamage } = args;
  // Lookup-or-no-op: an op with no handler (not type-reachable) falls through silently.
  const handler = ATOM_REGISTRY[atom.op] as AtomHandler<EffectAtom> | undefined;
  handler?.({ atom, ctx, steps, warnings, pendingDamage });
}

/**
 * Interpret a `forced_move`: emit a `forced_move`
 * grounded step displacing the target `dist` spaces away from the `away_from` source —
 * a combatant (default `attacker`), or the area template **center** (`ctx.site`) when
 * `away_from: "site"` (the Blast-Knockback radial push). The
 * **destination cell** is the spatial answerer's job — query
 * `pushDestination` (walk the hex line away, stop at the map edge / a blocker). The map
 * query runs ONCE here, at resolve time, and the resolved `to` is recorded into the step
 * (playback never re-queries). When the answerer can't place it (either combatant
 * unplaced, or no away-direction — no map answer), the step records `to: null` (an honest
 * no-op displacement, not a faked cell) and a deferral warning surfaces.
 *
 * **Chosen-direction mode** (`atom.toward === "chosen"`): displaces `target` toward a
 * CASTER-picked cell instead of away from a source.
 * Structurally mirrors {@link interpretMoveSelfTowardTarget}'s own `"chosen"` halt (same
 * `NeedsDecisionSignal`/`ctx.toward`/`travelDestination` machinery), but differs in three
 * load-bearing ways:
 *
 *   1. **The blanket-immunity gate above runs UNCONDITIONALLY before this branch** — an immune
 *      target raises no halt and gets no displacement, full stop. This is the entire reason the
 *      mode lives here rather than on the voluntary atom, which has no such gate.
 *   2. **The halt's `who` AND `promptId` key on the CHOOSER (`atom.chooser`, default
 *      `"attacker"`), never on `tgt` (the mover).** The backend attributes an answered
 *      cross-actor prompt by the id embedded in its `promptId` (`backend/src/encounterRoom.ts`'s
 *      `resolutionAttribution`/`isVerifiedAnswer`) — a mover-keyed halt would route the
 *      direction choice to the very combatant the effect is displacing, reintroducing the exact
 *      defanging bug `t_hacker:1` was once re-authored away from. The promptId is
 *      `` `forced_move_toward:${chooser.id}` `` — a DIFFERENT namespace from
 *      `move_self_toward_target`'s `` `move_toward:${mover.id}` ``, not merely a
 *      differently-valued instance of the same one: chooser and mover are different combatants
 *      by construction in every shipped carrier, but nothing rules out the SAME resolution also
 *      raising an unrelated `move_toward:<id>` halt (e.g. a future entry where today's chooser
 *      is ALSO a self-mover elsewhere in the same effect list) — keeping the prefixes distinct
 *      makes that collision structurally impossible rather than "true today, fragile tomorrow."
 *   3. **The destination reads `travelDestination` (walk TOWARD `ctx.toward`), not
 *      `pushDestination` (walk AWAY from a source).** RAW is "a direction of your choice" — a
 *      heading to walk toward, not a push away from anything; there is no "away from" combatant
 *      in this mode. This is the SAME resolver `move_self_toward_target{toward:"chosen"}`
 *      already reads off `ctx.toward`, with `tgt` (not the chooser) as `travelDestination`'s
 *      `moverId` — the chooser only picks the heading, `tgt` is who actually walks it.
 *
 * Non-interactive replay (`ctx.interactive` unset — a scripted fixture, a replay, the CLI's
 * default) never raises the halt: an unrecorded `ctx.toward` falls straight through to an
 * honest `to: null` no-op with a deferral warning, mirroring `interpretMoveSelfTowardTarget`'s
 * identical discipline — never a faked destination.
 */
const MOVE_TOWARD_PREFIX = "move_toward:";
const FORCED_MOVE_TOWARD_PREFIX = "forced_move_toward:";

/**
 * The `move_self_toward_target{toward:"chosen"}` deterministic promptId — MINTED here, the
 * ONE place the string is built. {@link
 * interpretMoveSelfTowardTarget} calls this both to raise its halt AND to report which
 * decision it answered (via {@link import("./resolutionLedger").ResolutionLedger
 * .answeredDirectionPromptId}); every OTHER consumer that needs to recognize/reconstruct a
 * `move_toward:*` promptId (the backend's cross-actor attribution/verification/teardown at
 * `encounterRoom.ts`) calls this SAME function rather than hand-typing the template literal.
 * A hand-reconstructed `move_toward:${intent.target.id}` is the mirror-drift hazard this
 * closes — that assumption silently breaks the moment a declared-phase self-lunge
 * (`t_siege_specialist:2`, `t_hunter:1`) makes the mover the ATTACKER, not the intent's target.
 */
export function moveTowardPromptId(moverId: string): string {
  return `move_toward:${moverId}`;
}

/**
 * Parse a `move_toward:*` promptId back to the mover id it was minted for, or `undefined` for
 * any other key. Sibling reader to {@link moveTowardPromptId} — mirrors `reactionRegistry.ts`'s
 * `isReactionPromptId`/`reactionDefenderId` self-describing-id pattern.
 */
export function moveTowardMoverId(promptId: string): string | undefined {
  return promptId.startsWith(MOVE_TOWARD_PREFIX) ? promptId.slice(MOVE_TOWARD_PREFIX.length) : undefined;
}

/**
 * The `forced_move{toward:"chosen"}` deterministic promptId — MINTED here (mirrors {@link
 * moveTowardPromptId}'s doc, but a DIFFERENT namespace: `forced_move`'s `toward:"chosen"` mode
 * keys its halt on the CHOOSER (default `attacker`), never on the mover being displaced — see
 * {@link interpretForcedMove}'s own doc, point 2, for why the two must never collide).
 */
export function forcedMoveTowardPromptId(chooserId: string): string {
  return `forced_move_toward:${chooserId}`;
}

/**
 * Parse a `forced_move_toward:*` promptId back to the chooser id it was minted for, or
 * `undefined` for any other key. Sibling reader to {@link forcedMoveTowardPromptId}.
 */
export function forcedMoveTowardChooserId(promptId: string): string | undefined {
  return promptId.startsWith(FORCED_MOVE_TOWARD_PREFIX) ? promptId.slice(FORCED_MOVE_TOWARD_PREFIX.length) : undefined;
}

/**
 * The single, non-mirrored derivation of "which combatant does `deciders` (the backend's
 * `PendingDecisionLedger` decider cache — server-corroborated, never trusted off the
 * wire) record as the pending decider for a direction-answer prompt this resolution's
 * `reactionToward` might be answering" — across BOTH direction-halt namespaces ({@link
 * moveTowardPromptId} and {@link forcedMoveTowardPromptId}). The one exported seam the backend
 * goes through, reached via `PendingDecisionLedger.directionDecision`
 * (`backend/src/pendingDecisionLedger.ts`) so its three callers
 * (`resolutionAttribution`/`isVerifiedAnswer`/`teardownAnsweredDecisions`) neither
 * hand-reconstruct a promptId, miss the `forced_move_toward:` namespace, nor hold a live
 * reference to the cache itself.
 *
 * Checks each CANDIDATE id (the caller supplies them — ordinarily an intent's `attacker` and,
 * when its `target` is a unit, that unit's id: the only two combatants a direction halt raised
 * during this resolution's declared/on-hit/on-crit/on-miss fire points can ever be keyed to)
 * against both namespaces' deterministic promptId shape, returning the first that is a LIVE
 * entry in `deciders` whose recorded decider is that SAME id — self-describing by construction,
 * since `sendDecisionPrompt` always stamps `decision.who` as the same id the promptId embeds.
 * **Deliberately does not scan the whole `deciders` map**: an unrelated, still-pending decision
 * from a DIFFERENT in-flight resolution could otherwise be matched by accident. Constraining the
 * scan to the caller's own candidates keeps this exactly as corroborated as the single-target
 * check it replaces — it just no longer assumes which of the two the mover has to be.
 */
export function pendingDirectionDecision(
  deciders: ReadonlyMap<string, string>,
  candidateIds: readonly string[],
): { promptId: string; deciderId: string } | undefined {
  for (const id of candidateIds) {
    const moveId = moveTowardPromptId(id);
    if (deciders.get(moveId) === id) return { promptId: moveId, deciderId: id };
    const forcedId = forcedMoveTowardPromptId(id);
    if (deciders.get(forcedId) === id) return { promptId: forcedId, deciderId: id };
  }
  return undefined;
}

/**
 * The single-slot answer guard: **`RequestAttack.reactionToward` is one
 * scalar `Cell` per intent**, but a single `resolve()` pass can contain TWO independent
 * `"chosen"`-direction movers with different promptIds — the declared-phase self-lunge (mover =
 * attacker) and the on_miss reposition (mover = target, e.g. Ace's Acrobatics) — when both fire
 * in the same resolution. `fireAttackDeclared` always runs before the to-hit roll, so on an
 * ANSWERED re-submit the declared-phase mover consumes `ctx.toward` FIRST, deterministically,
 * every time; a later `"chosen"` consumer in the SAME pass — for a genuinely DIFFERENT promptId —
 * would otherwise read the SAME already-recorded cell as if it were ITS OWN answer, silently
 * moving a combatant whose controller was never asked (`directionPromptCollision.test.ts`).
 *
 * Called by {@link interpretMoveSelfTowardTarget}/{@link interpretForcedMove} the instant either
 * finds `ctx.toward` already defined, BEFORE treating it as this atom's own answer. Returns `true`
 * ("refused — the caller must record an honest `to: null` no-op and stop") when the ledger's
 * {@link import("./resolutionLedger").ResolutionLedger.answeredDirectionPromptId} is ALREADY
 * stamped with a DIFFERENT promptId this same `resolve()` pass — i.e. some earlier atom in this
 * resolution already legitimately consumed `ctx.toward` for its own, different, decision. Returns
 * `false` (proceed normally) when nothing has claimed the answer yet, or when the ledger already
 * names THIS atom's own promptId (a second atom answering the identical decision — not a
 * collision, e.g. two clauses on the same mover keyed to the same prompt).
 *
 * **Why "refuse" (a silent no-op + warning) rather than "re-halt the second mover with its own
 * fresh `needs_decision`":** re-raising a halt would just ask the SAME single-slot
 * `reactionToward` field for a THIRD answer. On that third `resolve()` call the ledger starts
 * fresh, so the declared-phase mover — still firing FIRST — would consume the fresh answer as ITS
 * OWN, moving the WRONG combatant instead. Two independent decisions cannot be serviced through
 * one wire slot at all; widening the transport to a per-mover map is the real fix, and is not
 * built yet — this guard's only job is to stop the SILENT wrong move, not to complete a
 * round-trip the transport can't carry.
 */
function directionPromptCollisionGuard(args: {
  ctx: EffectContext;
  promptId: string;
  moverLabel: string;
  warnings: string[];
}): boolean {
  const { ctx, promptId, moverLabel, warnings } = args;
  const already = ctx.ledger?.answeredDirectionPromptId;
  if (already === undefined || already === promptId) return false;
  warnings.push(
    `${moverLabel}: refusing to silently reuse the reactionToward answer already consumed by "${already}" this resolution (D4 single-slot collision guard) — recorded as a no-op; the controller was never asked this direction`,
  );
  return true;
}

/**
 * The warning a spatial atom emits when it degrades because the caller's intent shape has no wire
 * slot to answer a direction into (see
 * {@link EffectContext.directionAnswerable}). Written in the surrounding honest-no-op voice: it
 * says WHAT happened, WHY it was unanswerable, and that it is a degrade rather than an error.
 *
 * One helper rather than two inline strings so the two atoms that can degrade
 * ({@link interpretMoveSelfTowardTarget}, {@link interpretForcedMove}) can never drift into
 * describing the same condition two different ways — `fallback` is the only part that differs,
 * and it differs because the degrades genuinely differ (a magnitude question still has a heading
 * to walk; a direction question does not).
 */
function unanswerableDirectionWarning(moverLabel: string, fallback: string): string {
  return `${moverLabel}: no direction answer is possible for this action — the intent shape that would carry the answer back has no slot for one, so this resolved non-interactively (${fallback}) instead of halting on a prompt nobody could answer. Not an error; the controller was never offered the choice.`;
}

/**
 * The distance a **voluntary** `move_self_toward_target` walk actually TRAVELLED (`from` → `to`),
 * honestly `0` when either is `null` — an unplaced mover, a mover already standing on its anchor
 * cell, or a walk obstructed on its very first candidate all end up back at (or never leaving)
 * `from`. Uses `@lancer-vtt/space`'s own grid metric
 * (`world.distance`), never the authored `atom.dist` BOUND — recording the bound unconditionally
 * logs a chosen 1-space answer against an authored "up to 4" (`t_drone_commander:1`) as 4.
 * **Deliberately scoped to the voluntary atom
 * only** — `interpretForcedMove`'s involuntary Knockback/push sites keep recording the authored
 * tag-bound value even on a short/no-op push (see {@link import("@lancer-vtt/domain").ForcedMoveStep}'s
 * own doc for why that's a DIFFERENT, deliberate convention for that atom).
 */
function forcedMoveDistance(state: EncounterState, from: Cell | null, to: Cell | null): number {
  if (from === null || to === null) return 0;
  return worldFor(state).distance(from, to);
}

/**
 * A `forced_move` starts where this resolution's earlier pushes left its target
 * (`ResolutionLedger.displaced`), not at the frozen pre-resolution cell, and records where it lands
 * for the next one. The push itself reads a scratch state with the target stood there.
 */
function interpretForcedMove(args: AtomHandlerArgs<ForcedMoveAtom>): void {
  const { atom, ctx, steps } = args;
  const tgt = resolveActor(atom.target, ctx);
  const at = tgt !== undefined ? ctx.ledger?.displaced?.[tgt.id] : undefined;
  const moved: AtomHandlerArgs<ForcedMoveAtom> =
    tgt !== undefined && at !== undefined
      ? { ...args, ctx: { ...ctx, state: { ...ctx.state, combatants: { ...ctx.state.combatants, [tgt.id]: { ...tgt, pos: at } } } } }
      : args;
  const before = steps.length;
  interpretForcedMoveFrom(moved);
  if (!ctx.ledger) return;
  for (const s of steps.slice(before)) {
    if (s.op === "forced_move" && s.to !== null) (ctx.ledger.displaced ??= {})[s.target] = s.to;
  }
}

function interpretForcedMoveFrom(args: AtomHandlerArgs<ForcedMoveAtom>): void {
  const { atom, ctx, warnings } = args;
  const tgt = resolveActor(atom.target, ctx);
  if (!tgt || atom.dist <= 0) return;
  // Movement immunity (Stasis Generator's "can't be moved"). A
  // blanket-immune target is never force-moved: surface a note, emit no step — mirroring the
  // `statusBlockedByImmunity` precedent above (a blocked effect is a note, never a step; grounded
  // steps stay pure). This gates only INVOLUNTARY displacement (`forced_move`); a voluntary
  // self-reposition (`interpretMoveSelfTowardTarget`, which also emits a forced_move step) is
  // never blocked by immunity — the mover chose to move. Runs BEFORE either `forced_move` mode
  // below (deterministic `away_from` OR chosen-direction `toward`), so the gate can't be
  // bypassed by picking a different heading mode.
  if (immuneToInvoluntaryMovement({ state: ctx.state, targetId: tgt.id, overlay: ctx.overlay })) {
    warnings.push(`${tgt.name}: IMMUNE — can't be moved involuntarily; forced move not applied.`);
    return;
  }
  // A rooted map asset (a scale tree) is never force-moved (`decisions/0193` point 5): same
  // no-step-plus-note shape as the blanket gate, ahead of either heading mode.
  if (isRooted(tgt)) {
    warnings.push(rootedVetoWarning(tgt, "moved"));
    return;
  }
  // Heavy Frame: no step at all, like the blanket gate above — the push never happens.
  if (vetoedBySmallerCharacter({ ctx, targetId: tgt.id, blocks: "forced_move", what: "moved", warnings })) return;
  if (pickedOwnHeading(args, tgt)) return;
  forcedMoveAsAuthored(args, tgt);
}

/** The prompt id a `chooseInvoluntaryHeading` carrier's pick asks under — one per moved combatant. */
export function involuntaryHeadingPromptId(targetId: string): string {
  return `own_heading:${targetId}`;
}

/**
 * Sea Legs (`ModifierContribution.chooseInvoluntaryHeading`): when another character's effect
 * force-moves a carrier that still has the round's use, the carrier picks where it lands among the
 * spaces the same-distance move reaches in every direction, plus where the push would have put it.
 * Taking that last one spends nothing and falls through to the authored push; any other lands the
 * pick and spends the use. Returns whether it emitted the move.
 */
function pickedOwnHeading(args: AtomHandlerArgs<ForcedMoveAtom>, tgt: Combatant): boolean {
  const { atom, ctx, steps } = args;
  const pusher = ctx.actors.self;
  if (!tgt.pos || pusher === undefined || pusher === tgt.id) return false;
  const source = choosesInvoluntaryHeading({ state: ctx.state, targetId: tgt.id, overlay: ctx.overlay });
  if (source === undefined) return false;
  const capKey = resourceCapKey(source);
  if ((tgt.current.resources?.[capKey] ?? 0) >= 1) return false;
  const world = worldFor(ctx.state);
  const grid = encounterGridMode(ctx.state);
  const landings = new Map<string, Cell>();
  for (const heading of world.disk(tgt.pos, atom.dist).cells()) {
    if (world.distance(tgt.pos, heading) !== atom.dist) continue;
    const to = travelDestination({ state: ctx.state, moverId: tgt.id, dist: atom.dist, towardCell: heading });
    if (to !== null && !world.equals(to, tgt.pos)) landings.set(world.key(to), tagCell(grid, to));
  }
  if (landings.size === 0) return false;
  // Where the authored push would put it, when that needs no one's answer.
  let asPushed: string | undefined;
  if (atom.toward !== "chosen") {
    const scratch: ResolvedStep[] = [];
    forcedMoveAsAuthored({ ...args, steps: scratch, warnings: [] }, tgt);
    const to = scratch[0]?.op === "forced_move" ? scratch[0].to : null;
    if (to !== null) {
      asPushed = world.key(to);
      if (!landings.has(asPushed)) landings.set(asPushed, tagCell(grid, to));
    }
  }
  const keys = [...landings.keys()];
  const promptId = involuntaryHeadingPromptId(tgt.id);
  const answered = ctx.choices?.choose(promptId, keys);
  if (answered === undefined) {
    if (ctx.choices?.requireAnswer) {
      throw new NeedsDecisionSignal(promptId, `${tgt.name}: choose where you are moved (${atom.dist} spaces)`, {
        options: keys.map((k) => ({ id: k, label: k === asPushed ? `${k} (as pushed)` : k, cell: landings.get(k)! })),
        who: tgt.id,
        answerKind: "cell",
      });
    }
    return false;
  }
  if (answered === asPushed) return false;
  steps.push({ op: "forced_move", target: tgt.id, from: tgt.pos, to: landings.get(answered)!, distance: atom.dist } satisfies ForcedMoveStep);
  steps.push(...spendStepsNow([{ kind: "counter", carrier: tgt.id, key: capKey, delta: 1 }], ctx.state, ctx.loop));
  return true;
}

/** The authored `forced_move` past its vetoes: a chosen-direction push, a pull toward something, or Knockback away. */
function forcedMoveAsAuthored(args: AtomHandlerArgs<ForcedMoveAtom>, tgt: Combatant): void {
  const { atom, ctx, steps, warnings } = args;
  if (atom.toward === "chosen") {
    const chooser = resolveActor(atom.chooser ?? "attacker", ctx);
    if (!chooser) {
      warnings.push(`forced_move: no chooser resolved for ${tgt.id}'s chosen-direction push (recorded as a no-op)`);
      const step: ForcedMoveStep = { op: "forced_move", target: tgt.id, from: tgt.pos ?? null, to: null, distance: atom.dist };
      steps.push(step);
      return;
    }
    if (ctx.toward === undefined && ctx.interactive) {
      // An intent shape with no wire slot for a direction answer must DEGRADE, not halt — see
      // {@link EffectContext.directionAnswerable}. Falls through to the ordinary unanswered path
      // below, which records the honest `to: null` no-op; the warning is what keeps it honest.
      if (ctx.directionAnswerable === false) {
        warnings.push(unanswerableDirectionWarning(`${chooser.name}'s chosen-direction push of ${tgt.name}`, "no-op"));
      } else {
        throw new NeedsDecisionSignal(forcedMoveTowardPromptId(chooser.id), `${chooser.name}: choose a direction to push ${tgt.name}`, {
          options: [],
          who: chooser.id,
          answerKind: "direction",
        });
      }
    }
    // The single-slot `reactionToward` collision
    // guard — see {@link directionPromptCollisionGuard}'s own doc for the full mechanism and why
    // "refuse," not "re-halt."
    if (ctx.toward !== undefined) {
      const refused = directionPromptCollisionGuard({
        ctx,
        promptId: forcedMoveTowardPromptId(chooser.id),
        moverLabel: `${chooser.name}'s chosen-direction push of ${tgt.name}`,
        warnings,
      });
      if (refused) {
        const step: ForcedMoveStep = { op: "forced_move", target: tgt.id, from: tgt.pos ?? null, to: null, distance: atom.dist };
        steps.push(step);
        return;
      }
    }
    // `ctx.toward` is defined here, so this pass is consuming an already-recorded
    // chosen-direction answer — report which decision that was
    // (see {@link ResolutionLedger.answeredDirectionPromptId}'s own doc) so a caller never has
    // to re-derive it from `intent.target.id` (the mirror-drift hazard this closes).
    if (ctx.toward !== undefined && ctx.ledger) ctx.ledger.answeredDirectionPromptId = forcedMoveTowardPromptId(chooser.id);
    const from = tgt.pos ?? null;
    const to: Cell | null =
      tgt.pos && ctx.toward
        ? travelDestination({ state: ctx.state, moverId: tgt.id, dist: atom.dist, towardCell: ctx.toward })
        : null;
    if (to === null) {
      warnings.push(
        `forced_move: could not place ${tgt.id}'s ${atom.dist}-space chosen-direction push (no target position, no recorded direction, or no map answer — recorded as a no-op, D9)`,
      );
    }
    const step: ForcedMoveStep = { op: "forced_move", target: tgt.id, from, to, distance: atom.dist };
    steps.push(step);
    return;
  }
  if (atom.toward !== undefined) {
    // `"site"` pulls toward the recorded area center (a mine's own space), else a combatant's cell.
    const anchorActor = atom.toward === "site" || atom.toward === "pair" ? undefined : resolveActor(atom.toward, ctx);
    const anchorCell = atom.toward === "site" ? ctx.site : atom.toward === "pair" ? ctx.pair : anchorActor?.pos;
    const from = tgt.pos ?? null;
    const pulled = tgt.pos && anchorCell ? travelDestinationWithCollision({ state: ctx.state, moverId: tgt.id, dist: atom.dist, towardCell: anchorCell, ...(anchorActor !== undefined ? { anchorId: anchorActor.id } : {}) }) : null;
    const to = pulled?.to ?? null;
    // A pull that stopped short records its collision, as a push does (the Gravity Rifle's Prone).
    if (pulled?.collision !== undefined && ctx.ledger) (ctx.ledger.collisions ??= []).push({ mover: tgt.id, ...pulled.collision });
    if (to === null) warnings.push(`forced_move: could not pull ${tgt.id} ${atom.dist} spaces toward ${atom.toward} (no map answer — recorded as a no-op, D9)`);
    const step: ForcedMoveStep = { op: "forced_move", target: tgt.id, from, to, distance: atom.dist };
    steps.push(step);
    return;
  }
  const awayRef = atom.away_from ?? "attacker";
  const from = tgt.pos ?? null;
  // `away_from: "site"` pushes away from the recorded area template CENTER
  // (`ctx.site`) — a Blast-Knockback radial push — instead of away from a combatant. Any other ref
  // resolves to a combatant's cell (the usual Knockback, default `attacker`).
  let pushed: ReturnType<typeof pushDestinationWithCollision> = null;
  if (tgt.pos) {
    if (awayRef === "site") {
      pushed = ctx.site ? pushDestinationWithCollision({ state: ctx.state, targetId: tgt.id, awayFromCell: ctx.site, dist: atom.dist }) : null;
    } else {
      const away = resolveActor(awayRef, ctx);
      pushed = away?.pos ? pushDestinationWithCollision({ state: ctx.state, targetId: tgt.id, awayFromId: away.id, dist: atom.dist }) : null;
    }
  }
  const to = pushed?.to ?? null;
  const collision = pushed?.collision;
  if (collision !== undefined && ctx.ledger) (ctx.ledger.collisions ??= []).push({ mover: tgt.id, ...collision });
  if (to === null) {
    warnings.push(
      `forced_move: could not place ${tgt.id}'s ${atom.dist}-space displacement (no map answer — recorded as a no-op, D9)`,
    );
  }
  const step: ForcedMoveStep = { op: "forced_move", target: tgt.id, from, to, distance: atom.dist, ...(collision !== undefined ? { collision } : {}) };
  steps.push(step);
}

/**
 * Interpret a `move_self_toward_target`: emit a `forced_move` grounded step — the SAME step
 * `forced_move` uses (a plain positional record, agnostic to voluntary/involuntary) — for a
 * **voluntary** self-reposition. `who` defaults to `self` (the acting combatant; unlike
 * `forced_move`'s target-default, a voluntary move is never about the resolution's target).
 * The anchor cell `toward` resolves to: a combatant's CURRENT cell (default `"target"`,
 * Hunter's Lunge), or — the reserved keyword `"chosen"` — the player-recorded destination off
 * `ctx.toward` (Ace's any-direction Acrobatics; the SAME slot a line/cone area template
 * already threads, `resolve/area.ts`'s `intent.toward`). The destination is the spatial answerer's
 * job (`travelDestination` — the toward-mirror of `pushDestination`, sharing `walkAndBackoff`),
 * queried ONCE here at resolve time (playback never re-queries). No mover, no anchor cell,
 * or zero legal progress all record an honest no-op (`to: null`) with a deferral warning —
 * never a faked destination.
 *
 * **`toward: "chosen"` interactive halt**: a triggered
 * reaction (Ace's Acrobatics, on-miss) has no PRE-recorded direction — unlike
 * `resolveActivate`'s `intent.toward` (submitted up front by the player before resolving), the
 * mover doesn't know it needs a direction until mid-resolution. So when `ctx.toward` is still
 * `undefined` here AND `ctx.interactive` is set (the server's live round-trip, mirroring
 * `ChoiceSource.requireAnswer`), this halts with a **spatial** {@link NeedsDecisionSignal}
 * (`answerKind: "direction"`, `who` = the mover — reusing the existing cross-actor `who` routing)
 * instead of recording the silent no-op. The resolver's catch site turns that into
 * `needs_decision`; the answered re-submit records the chosen cell (the CLI's `--react-toward`,
 * `RequestAttack.reactionToward`) which `resolve/attackTriggerFires.ts`'s on_miss fire site threads back onto
 * `ctx.toward` — so the RE-run reads it here as an already-recorded direction and completes
 * (the destination is still queried exactly once, on this second pass). Non-interactive
 * (`ctx.interactive` unset — replay / a scripted fixture / the CLI's default) records the
 * honest no-op instead.
 *
 * The promptId (`move_toward:<moverId>`) is deterministic, not authored — `move_self_toward_target`
 * carries no `prompt` field of its own (unlike `choose`), and a per-mover key is sufficient since
 * at most one such reaction is in flight per resolution today (Ace's carrier vector, the only
 * live case).
 *
 * **`upTo` — the magnitude answer.** Orthogonal to `toward`:
 * it widens WHICH atoms need an answer (`needsAnswer` below), it never changes where the direction
 * comes from. `toward` (unaffected by `upTo`) still resolves the ANCHOR cell exactly as above; when
 * `upTo` is set, the SAME `ctx.toward`/`move_toward:<moverId>` halt/wire machinery `toward:"chosen"`
 * already uses instead carries a MAGNITUDE pick — the answered cell is projected onto the
 * `mover → anchor` ray (`travelDestination`'s `upToCell`, nearest-cell clamp, never exact-match
 * required) to cap how far the walk goes. Unused when `toward:"chosen"` is ALSO set: `ctx.toward` is
 * already spoken for as the direction answer in that mode, so there's no separate magnitude slot.
 *
 * **`as: "flying"`.** Threaded straight through to
 * `travelDestination`'s own `as`, which threads it into `movementContext` — a per-walk override,
 * never a status stamped on the mover (see the atom's own doc for why: leaking flight into the
 * rest of the activation, colliding with the per-activation heading lock and Type-I's
 * provenance-marked flight heat clause).
 *
 * **`distance` records the TRAVELLED distance, not the authored `atom.dist` ceiling.** Computed
 * via {@link forcedMoveDistance} from the
 * emitted step's own `from`/`to` — a chosen 1-space answer, a wall-blocked walk that made zero
 * progress, or a mover already standing on its anchor cell (`to: null`) each log the real
 * figure instead of repeating the authored bound regardless of what happened.
 */
function interpretMoveSelfTowardTarget(args: AtomHandlerArgs<MoveSelfTowardTargetAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const mover = resolveActor(atom.who ?? "self", ctx);
  // `atom.dist` is a `MagnitudeValue` (`npc-features-fielded-roster` Phase 3.3) — resolved
  // ONCE here, off the MOVER's own live state (`{ stat: "speed" }` reads the mover's frozen
  // `base.speed`, Charge/Avalanche Charge's "move Speed in a straight line"), before anything
  // below runs. Every remaining reference in this function reads the resolved `dist`, never
  // `atom.dist` directly.
  const dist = resolveMagnitude(atom.dist, ctx, warnings);
  if (!mover || dist <= 0) return;
  // A Grounded mover's authored flight walk goes on foot.
  const grounded = atom.as === "flying" && hasStatus(mover.statuses, GROUNDED_STATUS);
  if (grounded) warnings.push(`${mover.name}: GROUNDED — can't fly; the move goes on foot.`);
  const as = grounded ? undefined : atom.as;
  // An involuntary walk (Puppet System) stops at immunity to involuntary movement (Stasis, Black
  // Thumb's rider), not at Heavy Frame's "can't be pushed or pulled by smaller characters".
  if (atom.involuntary === true && immuneToInvoluntaryMovement({ state: ctx.state, targetId: mover.id, overlay: ctx.overlay })) {
    warnings.push(`${mover.name}: IMMUNE — can't be moved involuntarily; move not applied.`);
    return;
  }
  // Someone other than the mover may pick the heading (Puppet System's invader); that halt is the
  // chooser's, in the chooser-keyed namespace `forced_move`'s chosen push uses.
  const chooser = atom.chooser !== undefined ? resolveActor(atom.chooser, ctx) : mover;
  if (!chooser) {
    warnings.push(`move_self_toward_target: no chooser resolved for ${mover.name}'s move (recorded as a no-op)`);
    return;
  }
  const promptId = chooser.id === mover.id ? moveTowardPromptId(mover.id) : forcedMoveTowardPromptId(chooser.id);
  const towardRef = atom.toward ?? "target";
  // `upTo` widens which atoms need a recorded answer to include the "toward stays
  // engine-derived, only the MAGNITUDE is player-picked" shape (Hunter's Lunge) alongside the
  // "the whole direction is player-picked" shape (`toward:"chosen"`, Ace's Acrobatics).
  const needsAnswer = towardRef === "chosen" || atom.upTo === true;
  if (needsAnswer && ctx.toward === undefined && ctx.interactive) {
    // An intent shape with no wire slot for a direction answer must DEGRADE, not halt — see
    // {@link EffectContext.directionAnswerable}. The two degrades differ, and the warning says
    // which happened: a `toward:"chosen"` atom has no heading to fall back on (honest no-op, via
    // `towardCell === null` below), while an `upTo` atom's direction is engine-derived, so only
    // the MAGNITUDE was unanswerable and the walk takes the full authored `dist`.
    if (ctx.directionAnswerable === false) {
      warnings.push(
        unanswerableDirectionWarning(
          `${mover.name}'s ${towardRef === "chosen" ? "chosen-direction move" : "move toward its target"}`,
          towardRef === "chosen" ? "no-op" : `full authored ${dist}`,
        ),
      );
    } else {
      // For the FIXED-direction shape (Hunter's Lunge —
      // `toward` stays engine-derived, only the magnitude is picked), the anchor is already
      // known at throw time, so the client-paintable reachable-cell set can be computed and
      // carried display-only on the halt (see `NeedsDecisionSignal.reachableCells`'s own doc). A
      // `toward:"chosen"` halt has no anchor yet (the direction ITSELF is the unanswered
      // question) — `reachableCells` stays absent and the client keeps its bare-crosshair
      // affordance.
      const anchor = towardRef !== "chosen" ? resolveActor(towardRef, ctx)?.pos : undefined;
      throw new NeedsDecisionSignal(
        promptId,
        chooser.id === mover.id
          ? `${mover.name}: choose ${towardRef === "chosen" ? "a direction" : "how far"} to move`
          : `${chooser.name}: choose a direction to move ${mover.name}`,
        {
          options: [],
          who: chooser.id,
          answerKind: "direction",
          ...(anchor && mover.pos
            ? { reachableCells: travelReachableCells({ state: ctx.state, moverId: mover.id, dist, towardCell: anchor, ...(as !== undefined ? { as } : {}) }) }
            : {}),
        },
      );
    }
  }
  // The single-slot `reactionToward` collision
  // guard — see {@link directionPromptCollisionGuard}'s own doc.
  if (needsAnswer && ctx.toward !== undefined) {
    const refused = directionPromptCollisionGuard({
      ctx,
      promptId,
      moverLabel: `${mover.name}'s chosen-direction move`,
      warnings,
    });
    if (refused) {
      const noOpFrom = mover.pos ?? null;
      const step: ForcedMoveStep = { op: "forced_move", target: mover.id, from: noOpFrom, to: null, distance: forcedMoveDistance(ctx.state, noOpFrom, null) };
      steps.push(step);
      return;
    }
  }
  // `ctx.toward` is defined here, so this pass is
  // consuming an already-recorded chosen-direction answer — report which decision that was
  // (see {@link ResolutionLedger.answeredDirectionPromptId}'s own doc) so a caller never has to
  // re-derive it from `intent.target.id` (the mirror-drift hazard this closes: the mover here can
  // be the ATTACKER, e.g. `t_siege_specialist:2`'s declared-phase self-lunge — not always the
  // intent's target, e.g. Ace's on-miss reposition).
  if (needsAnswer && ctx.toward !== undefined && ctx.ledger) ctx.ledger.answeredDirectionPromptId = promptId;
  const towardCell: Cell | null = towardRef === "chosen" ? ctx.toward ?? null : resolveActor(towardRef, ctx)?.pos ?? null;
  // The magnitude answer only caps travel when `upTo` is set AND the direction did NOT itself
  // come from `ctx.toward` (`towardRef !== "chosen"`) — when it did, `ctx.toward` is already the
  // direction answer, not a separate magnitude one.
  const upToCell: Cell | undefined = atom.upTo === true && towardRef !== "chosen" ? ctx.toward ?? undefined : undefined;
  const from = mover.pos ?? null;
  const path = from && towardCell ? walkPath({ atom, ctx, mover, dist, from, towardCell, upToCell, as }) : null;
  const to: Cell | null = path?.at(-1) ?? null;
  if (path === null || to === null) {
    warnings.push(
      `move_self_toward_target: could not place ${mover.id}'s ${dist}-space travel (no mover position, no anchor cell, or no map answer — recorded as a no-op, D9)`,
    );
  } else {
    // Stash the RESOLVED endpoint into `ctx.site` — the SAME per-resolution mutable slot
    // `for_each`/`create_area`'s `origin:"site"` already reads (`interpretForEach` threads
    // `ctx.site` through verbatim) — so a LATER atom in the SAME effect list can enumerate
    // relative to where the mover actually ENDED UP, never its frozen pre-move `ctx.state`
    // position (`ctx.state` is a fixed snapshot for the whole resolution; a `status_add`/
    // move step only RECORDS a change, it never mutates `ctx.state` itself). Impale's
    // endpoint-adjacency save (`npc-features-fielded-roster` Phase 6.2 — "pick a character
    // that ended adjacent to" the Cataphract's landing cell) is the first author of this
    // ordering: move BEFORE a `site`-origin selector in the same list. Only set on an
    // ACTUAL move (`to !== null`) — a failed/no-op move leaves any earlier `ctx.site` (e.g.
    // a drawn Blast center) untouched rather than clobbering it with nothing.
    ctx.site = to;
    // The walked anchors, for a later `passed_through` selector / `place_adjacent`, and for the
    // referee's move checkpoint and move hazards.
    ctx.movePath = {
      mover: mover.id,
      path,
      ...(atom.provokesReactions === true ? { provokesReactions: true as const } : {}),
      ...(atom.involuntary === true ? { voluntary: false as const } : {}),
    };
  }
  const step: ForcedMoveStep = { op: "forced_move", target: mover.id, from, to, distance: forcedMoveDistance(ctx.state, from, to) };
  steps.push(step);
}

/**
 * The anchors a `move_self_toward_target` walk crosses, `from` first and the landing last. An
 * `asMove` walk runs its movement budget along the heading under the movement rules
 * ({@link moveWalk}); the plain walk is `travelDestination`'s landing on the `from → towardCell`
 * ray, so its path is the ray's prefix ending on that (grid-tagged) landing. `null` when no map
 * answer exists.
 */
function walkPath(args: {
  atom: MoveSelfTowardTargetAtom;
  ctx: EffectContext;
  mover: Combatant;
  dist: number;
  from: Cell;
  towardCell: Cell;
  upToCell: Cell | undefined;
  /** The walk's flight override: the atom's own, dropped while the mover is Grounded. */
  as: MoveSelfTowardTargetAtom["as"];
}): Cell[] | null {
  const { atom, ctx, mover, dist, from, towardCell, upToCell, as } = args;
  if (atom.asMove === true) return moveWalk({ state: ctx.state, moverId: mover.id, budget: dist, headingCell: towardCell });
  const to = travelDestination({
    state: ctx.state,
    moverId: mover.id,
    dist,
    towardCell,
    ...(upToCell !== undefined ? { upToCell } : {}),
    ...(as !== undefined ? { as } : {}),
  });
  if (to === null) return null;
  const world = worldFor(ctx.state);
  const ray = world.line(from, towardCell);
  const end = ray.findIndex((c) => world.equals(c, to));
  return [...(end >= 0 ? ray.slice(0, end) : [from]), to];
}

/**
 * Interpret a `deploy_object` (ms_pattern_a_jericho_deployable_cover): mint
 * `atom.sectionCount` freshly-spawned object/deployable combatants via the
 * existing {@link import("../spawn").spawnObjectGroup} helper
 * — this wires content onto that adapter, it does not rebuild it —
 * placing them at the **player-supplied** `ctx.sites` cells (threaded by `resolveActivate`
 * from `RequestActivate.sites`) rather than auto-picking a chain. Emits one {@link
 * SpawnObjectStep} per section, `pos` already resolved (playback never re-queries the map).
 *
 * **Every carrier takes player-chosen cells** — the classification is atom-wide, with no
 * per-carrier opt-in: a 1-pick deploy (`ms_turret_drones`) and a 2-pick deploy
 * (jericho) both flow through the SAME `ctx.sites` read and the SAME wall below.
 *
 * **The wall**: `ctx.sites` must be present and exactly `atom.sectionCount` cells long, and
 * must pass {@link import("../deploy").checkDirectedDeployPlacement} (free + adjacent to
 * the deployer + adjacent to the previous pick) at the section footprint's actual
 * `size` (`atom.size ?? 1`, the SAME default `spawnObjectGroup` applies). `resolveActivate`
 * is reachable from the CLI and from tests directly, not only through the backend room's
 * own gate — so this check is NOT redundant with the room
 * gate, it is the engine-level wall the room gate mirrors, and the two must never disagree
 * because they read the same predicate.
 *
 * Idempotent per `groupId` (mirrors `add_status`'s idempotent-by-presence discipline): if
 * the FIRST section's deterministic id is already live, this is a silent no-op (a second
 * activation while the first group hasn't been recalled/destroyed). No mover position,
 * missing/wrong-length `ctx.sites`, or an illegal cell (or the underlying
 * `spawnObjectGroup` call itself rejecting — a structurally invalid `sectionCount`/armor)
 * all record an honest no-op with a deferral warning — never a partial or off-RAW
 * placement.
 */
function interpretDeployObject(args: AtomHandlerArgs<DeployObjectAtom>): void {
  const { atom, ctx, warnings } = args;
  const near = resolveActor(atom.near ?? "self", ctx);
  if (!near || !near.pos) {
    warnings.push(`deploy_object: "${atom.name}" — ${atom.near ?? "self"} isn't placed (recorded as a no-op)`);
    return;
  }
  if (atom.within !== undefined) {
    interpretAreaDeployObject(args, near);
    return;
  }
  // A `repeatable` deploy mints a fresh group each time (Restock's second charge, a second drone).
  const base = `${near.id}:${atom.groupId}`;
  const groupId = atom.repeatable ? `${base}:${freshGroupOrdinal(ctx.state, base)}` : base;
  // A drone group with no member left on the board (shot down, its system since repaired) is
  // wreckage: the deploy clears it and mints afresh. Anything else still out is an idempotent no-op.
  const wreck = atom.kind === "drone" && !objectGroupOut(ctx.state, groupId, true) ? objectGroupMembers(ctx.state, groupId) : [];
  if (wreck.length === 0 && ctx.state.combatants[`${groupId}:0`] !== undefined) {
    return; // already deployed and not yet recalled/destroyed — idempotent no-op.
  }
  const sites = ctx.sites;
  if (!sites || sites.length !== atom.sectionCount) {
    warnings.push(
      `deploy_object: "${atom.name}" — expected ${atom.sectionCount} picked site(s), got ${sites?.length ?? 0} (recorded as a no-op)`,
    );
    return;
  }
  const size = atom.size ?? 1;
  const verdict = checkDirectedDeployPlacement({
    state: ctx.state,
    deployerId: near.id,
    cells: sites,
    size,
    ...(atom.placement ? { placement: atom.placement } : {}),
  });
  if (!verdict.ok) {
    warnings.push(`deploy_object: "${atom.name}" — ${verdict.error} (recorded as a no-op)`);
    return;
  }
  for (const combatant of wreck) args.steps.push({ op: "despawn_object", combatant } satisfies DespawnObjectStep);
  spawnDeploySections({ args, near, groupId, cells: sites });
}

/**
 * Mint one section per cell of a `deploy_object` group and emit a {@link SpawnObjectStep} for
 * each, `pos` already resolved. Shared by the adjacency arm and the area arm.
 */
function spawnDeploySections(p: { args: AtomHandlerArgs<DeployObjectAtom>; near: Combatant; groupId: string; cells: Cell[] }): void {
  const { args, near, groupId, cells } = p;
  const { atom, ctx, steps, warnings } = args;
  const deployedFrom = deployedFromFor(ctx, near);
  const link = deployedFrom ? { deployedFrom } : {};
  if (atom.kind === "drone") {
    cells.forEach((pos, i) => {
      const combatant: Combatant = { ...droneSection({ atom, near, groupId, index: i, count: cells.length, turnOrdinal: ctx.state.activation.turnOrdinal }), ...link, pos };
      const step: SpawnObjectStep = { op: "spawn_object", combatant };
      steps.push(step);
    });
    return;
  }
  if (atom.aura !== undefined || atom.statuses !== undefined) {
    warnings.push(`deploy_object: "${atom.name}" — aura and statuses are read only for kind "drone"; ignored`);
  }
  const spawned = spawnObjectGroup({
    groupId,
    name: atom.name,
    sectionCount: cells.length,
    ...(atom.kind !== undefined ? { kind: atom.kind } : {}),
    ...(atom.armor !== undefined ? { armor: atom.armor } : {}),
    // Per-call size/evasion/edef overrides (ms_turret_drones' Size 0.5 / 10 Evasion /
    // 10 Edef), threaded straight through to `spawnObjectGroup`.
    ...(atom.size !== undefined ? { size: atom.size } : {}),
    ...(atom.evasion !== undefined ? { evasion: atom.evasion } : {}),
    ...(atom.edef !== undefined ? { edef: atom.edef } : {}),
    ...(atom.objectProfile !== undefined ? { objectProfile: atom.objectProfile } : {}),
    // The deployed sections take the DEPLOYER's side, so a
    // player's own cover doesn't read hostile to them (which is what has it offer Overwatch
    // against their own units). `near` IS the deployer — the same actor `deploy_mine` below
    // reads its `deployedBy`/`controllerSlotId` off.
    allegiance: sideOf(near),
  });
  if (!spawned.ok) {
    warnings.push(`deploy_object: "${atom.name}" — ${spawned.error}`);
    return;
  }
  spawned.value.forEach((section, i) => {
    const step: SpawnObjectStep = { op: "spawn_object", combatant: { ...section, ...link, pos: cells[i]! } };
    steps.push(step);
  });
}

/**
 * The {@link DeployedFrom} link for a combatant `near` deploys inside an activation: the entry the
 * activation fired, and — when that entry is one of `near`'s equipped systems — the frozen system
 * instance a destroyed drone takes down with it. `undefined` outside an activation.
 */
function deployedFromFor(ctx: EffectContext, near: Combatant): DeployedFrom | undefined {
  const entryId = ctx.activatingEntry;
  if (entryId === undefined) return undefined;
  const itemKey = near.base.equippedSystems?.find((s) => s.id === entryId)?.instanceId;
  return { combatantId: near.id, entryId, ...(itemKey !== undefined ? { itemKey } : {}) };
}

/**
 * One `kind: "drone"` section: {@link spawnDrone}'s DRONE-tag defaults on the deployer's
 * controlling slot and side, with the atom's `size`/`evasion`/`edef`/`armor` laid over them and the
 * deployer's `droneHpBonus` added to its HP, the atom's `aura` frozen on as its `auraProfile`, and its
 * `statuses` applied.
 */
function droneSection(p: { atom: DeployObjectAtom; near: Combatant; groupId: string; index: number; count: number; turnOrdinal: number }): Combatant {
  const { atom, near, groupId, index, count } = p;
  // `spawnDrone` always succeeds (RAW's flat defaults, nothing to reject).
  const drone = unwrap(
    spawnDrone({
      combatantId: `${groupId}:${index}`,
      name: count > 1 ? `${atom.name} (${index + 1}/${count})` : atom.name,
      controllerSlotId: near.controllerSlotId ?? null,
    }),
  );
  const base = {
    ...drone.base,
    // Drone Commander 1's +5, frozen on the deployer (`base.droneHpBonus`).
    maxHp: drone.base.maxHp + (near.base.droneHpBonus ?? 0),
    allegiance: sideOf(near),
    ...(atom.size !== undefined ? { size: atom.size } : {}),
    ...(atom.evasion !== undefined ? { evasion: atom.evasion } : {}),
    ...(atom.edef !== undefined ? { edef: atom.edef } : {}),
    ...(atom.armor !== undefined ? { armor: atom.armor } : {}),
  };
  const aura = atom.aura !== undefined ? { auraProfile: atom.aura } : {};
  const turnOrdinal = p.turnOrdinal;
  const statuses = (atom.statuses ?? []).map((spec): StatusInstance => {
    const { status, until = { kind: "until_removed" } } = typeof spec === "string" ? { status: spec } : spec;
    // A turn-scoped expiry skips its own application turn by the stamped `appliedTurn`, as `add_status` stamps it.
    return { name: status, expiry: until, source: near.id, ...(until.kind === "turn" || until.kind === "this_turn" ? { appliedTurn: turnOrdinal } : {}) };
  });
  return { ...drone, base, current: { ...drone.current, hp: base.maxHp }, statuses: [...drone.statuses, ...statuses], objectGroupId: groupId, ...aura };
}

/** The options prompt an area-scoped `deploy_object` asks under for section `index` (0-based). */
export function deployObjectSitePromptId(groupId: string, index: number): string {
  return `deploy_object:${groupId}:${index}`;
}

/** The lowest ordinal `n` whose `${base}:${n}` group has no live section — a `repeatable` deploy's fresh group. */
function freshGroupOrdinal(state: EncounterState, base: string): number {
  let n = 0;
  while (state.combatants[`${base}:${n}:0`] !== undefined) n++;
  return n;
}

/**
 * The area-scoped arm of {@link interpretDeployObject} (`atom.within`, Phase 18): place
 * `sectionCount` sections in free spaces inside a Burst around the resolved origin. Section `i`'s
 * cell is the recorded answer to {@link deployObjectSitePromptId}, else `ctx.sites[i]` (the
 * attack's `--site`s); it must be one of {@link legalAreaDeployCells}'s cells given the earlier
 * picks. Interactive and unanswered, or answered with an illegal cell, halts on a `"cell"` prompt
 * listing the legal cells (a `sites` answer outside the area is named in the prompt).
 * Non-interactive, the same cases record an honest no-op with a warning: nothing is placed.
 * When the area runs out of free spaces, the sections that fit are placed and the shortfall is
 * warned.
 */
function interpretAreaDeployObject(args: AtomHandlerArgs<DeployObjectAtom>, near: Combatant): void {
  const { atom, ctx, warnings } = args;
  const within = atom.within!;
  // Combatants this resolution has already pushed stand where they landed (Total Strength Suite's
  // terrain goes beside its target after the knockback).
  const state = displacedState(ctx);
  const originId = within.origin === "self" ? ctx.actors.self : within.origin === "target" ? ctx.actors.target : undefined;
  const origin = within.origin === "site" ? ctx.site : originId !== undefined ? state.combatants[originId]?.pos ?? undefined : undefined;
  if (origin === undefined) {
    warnings.push(`deploy_object: "${atom.name}" — could not resolve the placement area's origin "${within.origin}" (recorded as a no-op)`);
    return;
  }
  const base = `${near.id}:${atom.groupId}`;
  if (!atom.repeatable && ctx.state.combatants[`${base}:0`] !== undefined) return; // idempotent, as the adjacency arm.
  const groupId = atom.repeatable ? `${base}:${freshGroupOrdinal(ctx.state, base)}` : base;
  const world = worldFor(ctx.state);
  const area = world.disk(origin, within.size).cells();
  const size = atom.size ?? 1;
  const picks: Cell[] = [];
  for (let i = 0; i < atom.sectionCount; i++) {
    const legal = legalAreaDeployCells({ state, area, size, placed: picks });
    if (legal.length === 0) {
      warnings.push(`deploy_object: "${atom.name}" — only ${picks.length} of ${atom.sectionCount} section(s) fit in free spaces within the area`);
      break;
    }
    const promptId = deployObjectSitePromptId(atom.groupId, i);
    const cells = legal.map((c) => tagCell(encounterGridMode(ctx.state), c));
    const ids = cells.map((c) => world.key(c));
    const answered = ctx.choices?.choose(promptId, ids);
    const offered = ctx.sites?.[i];
    const offeredKey = offered !== undefined ? world.key(offered) : undefined;
    const pickKey = answered ?? (offeredKey !== undefined && ids.includes(offeredKey) ? offeredKey : undefined);
    if (pickKey === undefined) {
      const why =
        offeredKey !== undefined ? `${offeredKey} is not a free space within Burst ${within.size} of ${world.key(origin)}` : `no site given`;
      if (ctx.choices?.requireAnswer) {
        const rejected = offeredKey !== undefined ? ` (${why})` : "";
        throw new NeedsDecisionSignal(promptId, `${near.name}: place ${atom.name} ${i + 1} of ${atom.sectionCount}${rejected}`, {
          options: cells.map((cell, j) => ({ id: ids[j]!, label: ids[j]!, cell })),
          who: near.id,
          answerKind: "cell",
          section: { index: i, count: atom.sectionCount, size, placed: [...picks] },
        });
      }
      warnings.push(`deploy_object: "${atom.name}" — section ${i + 1} of ${atom.sectionCount}: ${why} (recorded as a no-op)`);
      return;
    }
    picks.push(cells[ids.indexOf(pickKey)]!);
  }
  if (picks.length > 0) spawnDeploySections({ args, near, groupId, cells: picks });
}

/** `ctx.state` with every combatant this resolution's `forced_move`s moved standing where it landed (`ResolutionLedger.displaced`). */
function displacedState(ctx: EffectContext): EncounterState {
  const displaced = ctx.ledger?.displaced;
  if (displaced === undefined) return ctx.state;
  const combatants = { ...ctx.state.combatants };
  for (const [id, pos] of Object.entries(displaced)) if (combatants[id]) combatants[id] = { ...combatants[id]!, pos };
  return { ...ctx.state, combatants };
}

/**
 * Interpret a `deploy_mine` (Pattern-B HEX Charges' Explosive Mine, Pattern-A Smoke
 * Charges' Smoke Mine): mint ONE mine
 * combatant via the existing {@link import("../spawn").spawnMine}
 * helper — a mine IS a plain `Combatant`, so this pushes the SAME {@link
 * SpawnObjectStep} {@link interpretDeployObject} already emits. **No new step, no new
 * inverse, no `applyResolved` change.**
 *
 * **Placement** is the 1-pick degenerate case of `deploy_object`'s directed
 * placement: reads `ctx.sites[0]` and validates it with the SAME {@link
 * checkDirectedDeployPlacement} wall, at Size 1 (RAW's mine footprint). The mine-only
 * `checkMineAdjacency` wall is wired at the room gate —
 * deliberately NOT rung here, mirroring how `deploy_object`'s own room gate sits apart
 * from its engine-level wall.
 *
 * **Deterministic id**: `` `${near.id}:${atom.groupId}` ``, mirroring {@link
 * interpretDeployObject} exactly — idempotent-by-id (a second activation while the
 * FIRST mine is still live is a silent no-op), so `spawn_object`'s idempotent-by-id
 * apply holds and a redeploy after removal/recall mints cleanly.
 *
 * **`saveDc` is deliberately NOT authored on the atom**: it is `ctx.defaultSaveDc`
 * — the deployer's own frozen `base.saveTarget`, already computed by `resolveActivate`
 * for every activate resolution (the same value every `force_save` on this rail already
 * reads). **`deployedBy`/`controllerSlotId`** are likewise derived from the deployer
 * (`near`), never authored — the same derivation `refereeDeployAction`'s own
 * `request_deploy_mine` branch uses.
 *
 * **`damage`/`damageType`/`trigger`/`areaGrants` are each threaded straight onto the
 * matching `SpawnMineArgs` field only when authored** — an omitted key, not a
 * `key: undefined`, mirroring
 * `saveAxis`/`ap`/`objectDamage`'s conditional-spread shape. A clause authoring
 * none of `damage`/`objectDamage`/`areaGrants`, or authoring `damage`/`objectDamage` with
 * no `damageType`, reaches `spawnMine` below, which rejects either with its own
 * construction-time guards ("at least one payload," "a damage payload requires a
 * damageType") — deliberately RUNTIME rejections, not schema-tier ones (see `atoms.ts`'s
 * `DeployMineAtom` doc for the reasoning): the schema validates authored SHAPE, and "does
 * this clause do anything observable"/"does its damage payload carry a type" are
 * content-authoring judgments best surfaced as a real interpreter warning (this function's
 * own no-op-with-deferral discipline, exercised by content entry tests) rather than a bespoke
 * zod cross-field refinement bolted onto the shared, recursive `EffectAtomSchema`
 * discriminated union every other atom op also flows through.
 *
 * A missing/unplaced deployer, a missing/wrong-length `ctx.sites`, an illegal cell, or
 * `spawnMine` itself rejecting (structurally invalid burst/damage/save/damage-type, a mine
 * authoring none of `damage`/`objectDamage`/`areaGrants`, or a `damage`/`objectDamage`
 * payload with no `damageType`) all record an honest no-op with a deferral warning — never
 * a partial or off-RAW placement, mirroring {@link interpretDeployObject}'s own discipline.
 */
function interpretDeployMine(args: AtomHandlerArgs<DeployMineAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const near = resolveActor(atom.near ?? "self", ctx);
  if (!near || !near.pos) {
    warnings.push(`deploy_mine: "${atom.name}" — ${atom.near ?? "self"} isn't placed (recorded as a no-op)`);
    return;
  }
  const count = atom.count ?? 1;
  const ids = count === 1 ? [`${near.id}:${atom.groupId}`] : Array.from({ length: count }, (_, i) => `${near.id}:${atom.groupId}:${i}`);
  if (ids.some((id) => ctx.state.combatants[id] !== undefined)) {
    return; // already deployed and not yet recalled/destroyed — idempotent no-op.
  }
  const sites = ctx.sites;
  if (!sites || sites.length !== count) {
    warnings.push(`deploy_mine: "${atom.name}" — expected ${count} picked site(s), got ${sites?.length ?? 0} (recorded as a no-op)`);
    return;
  }
  // A ranged placement carries the mine-to-mine spacing itself ("that aren't adjacent to each other").
  const verdict = checkDirectedDeployPlacement({ state: ctx.state, deployerId: near.id, cells: sites, size: 1, ...(atom.placement !== undefined ? { placement: atom.placement, mine: true } : {}) });
  if (!verdict.ok) {
    warnings.push(`deploy_mine: "${atom.name}" — ${verdict.error} (recorded as a no-op)`);
    return;
  }
  const deployedFrom = deployedFromFor(ctx, near);
  for (const [i, combatantId] of ids.entries()) {
    const spawned = spawnMine({
      combatantId,
      name: atom.name,
      burstRadius: atom.burstRadius,
      // `damage` is optional (the Smoke Mine's shape) — omit the key entirely
      // rather than pass `undefined` through, mirroring the other optional fields below.
      ...(atom.damage !== undefined ? { damage: atom.damage } : {}),
      // `damageType` is optional too (an area-only mine authors neither) — omit the key
      // entirely rather than pass `undefined` through.
      // `spawnMine` rejects a `damage`/`objectDamage` payload with no `damageType`.
      ...(atom.damageType !== undefined ? { damageType: atom.damageType } : {}),
      // `saveDc` comes off the resolution's own `defaultSaveDc` (the deployer's frozen
      // `base.saveTarget`, `resolveActivate`-supplied) — never authored on the atom.
      ...(atom.saveAxis !== undefined ? { saveAxis: atom.saveAxis, saveDc: ctx.defaultSaveDc ?? 10 } : {}),
      ...(atom.ap !== undefined ? { ap: atom.ap } : {}),
      ...(atom.objectDamage !== undefined ? { objectDamage: atom.objectDamage } : {}),
      // Authored trigger predicate + area payload, threaded straight
      // onto `SpawnMineArgs`/`MineProfile` unchanged.
      ...(atom.trigger !== undefined ? { trigger: atom.trigger } : {}),
      ...(atom.areaGrants !== undefined ? { areaGrants: atom.areaGrants } : {}),
      ...(atom.single_target !== undefined ? { singleTarget: atom.single_target } : {}),
      // Only the flag freezes; detonation reads the clauses off the entry `deployedFrom` names.
      ...(atom.on_fail !== undefined || atom.on_pass !== undefined ? { clauses: true as const } : {}),
      ...(atom.reach !== undefined ? { reach: atom.reach } : {}),
      ...(atom.catches !== undefined ? { catches: atom.catches } : {}),
      ...(atom.expires !== undefined ? { expires: atom.expires } : {}),
      ...(atom.leaves !== undefined ? { leaves: atom.leaves } : {}),
      ...(atom.arm_now !== undefined ? { armNow: atom.arm_now } : {}),
      ...(atom.visible !== undefined ? { visible: atom.visible } : {}),
      deployedBy: near.id,
      // Snapshot at deploy time, not a live reference — a deliberate non-cascade (mirroring
      // `refereeDeployAction.ts`'s twin for the same verb): reassigning `near`'s controller
      // later via `set_controller` does NOT retroactively move this mine's own controller.
      controllerSlotId: near.controllerSlotId ?? null,
      // The DEPLOYER's side (the `trigger: "ally"` predicate keeps
      // resolving off `deployedBy`, deliberately — see `SpawnMineArgs.allegiance`).
      allegiance: sideOf(near),
    });
    if (!spawned.ok) {
      warnings.push(`deploy_mine: "${atom.name}" — ${spawned.error}`);
      return;
    }
    const step: SpawnObjectStep = { op: "spawn_object", combatant: { ...spawned.value, ...(deployedFrom ? { deployedFrom } : {}), pos: sites[i]! } };
    steps.push(step);
  }
}

/**
 * Interpret a `recall_object` (ms_pattern_a_jericho_deployable_cover's
 * "Sections of cover can be picked up again as a full action"): the inverse of {@link
 * interpretDeployObject}. Looks up every combatant in the `` `${near}:${groupId}` `` object
 * group via {@link objectGroupMembers} (the SAME query the group-delete command reducer
 * uses) and emits one {@link DespawnObjectStep} per member — reusing `SpawnObjectStep`'s
 * structural undo-inverse, never a new step. Recalls the WHOLE group, live
 * or Destroyed (see the atom's own doc for why) — freeing every one of its ids so a
 * subsequent `deploy_object` with the same `groupId` mints fresh sections again.
 *
 * No resolvable `near`, or no live group found, is an honest no-op with a deferral warning
 * — never a phantom despawn.
 */
function interpretRecallObject(args: AtomHandlerArgs<RecallObjectAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const near = resolveActor(atom.near ?? "self", ctx);
  if (!near) {
    warnings.push(`recall_object: "${atom.groupId}" — ${atom.near ?? "self"} isn't a live combatant (recorded as a no-op)`);
    return;
  }
  const members = objectGroupMembers(ctx.state, `${near.id}:${atom.groupId}`);
  if (members.length === 0) {
    warnings.push(`recall_object: no deployed "${atom.groupId}" group near ${near.id} to recall (recorded as a no-op)`);
    return;
  }
  for (const combatant of members) {
    const step: DespawnObjectStep = { op: "despawn_object", combatant };
    steps.push(step);
  }
}

/** The prompt a `reload` asks under when the target has more than one unloaded Loading weapon. */
export function reloadPromptId(targetId: string): string {
  return `reload:${targetId}`;
}

/** Interpret a `reload` (see {@link ReloadAtom}): clear one unloaded Loading weapon's `loaded:<key>`. */
function interpretReload(args: AtomHandlerArgs<ReloadAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  withTarget(atom.target, ctx, (tgt) => {
    const unloaded = (tgt.base.weapons ?? []).filter((w) => w.loading && !isWeaponLoaded(tgt.current.resources, weaponInstanceKey(w)));
    if (unloaded.length === 0) {
      warnings.push(resolutionNote(`${tgt.name}: no unloaded Loading weapon to reload`));
      return;
    }
    const keys = unloaded.map((w) => weaponInstanceKey(w));
    const prompt = reloadPromptId(tgt.id);
    const answered = keys.length === 1 ? keys[0] : ctx.choices?.choose(prompt, keys);
    if (answered === undefined && ctx.choices?.requireAnswer) {
      throw new NeedsDecisionSignal(prompt, `${tgt.name}: reload which weapon?`, { options: unloaded.map((w) => ({ id: weaponInstanceKey(w), label: w.name })), who: tgt.id });
    }
    const key = loadedResourceKey(answered ?? keys[0]!);
    steps.push({ op: "resource_clear", target: tgt.id, key, restoreVal: tgt.current.resources?.[key] ?? 0 });
  });
}

/** The `"cell"` prompt a `redeploy_object` asks under. */
export function redeployObjectPromptId(groupId: string): string {
  return `redeploy_object:${groupId}`;
}

/**
 * Interpret a `redeploy_object` (see {@link RedeployObjectAtom}): move the `near` group's one live
 * member to a legal cell by the deploy's own placement rule, as a `move` step.
 */
function interpretRedeployObject(args: AtomHandlerArgs<RedeployObjectAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const near = resolveActor(atom.near ?? "self", ctx);
  const live = near ? objectGroupMembers(ctx.state, `${near.id}:${atom.groupId}`).filter((m) => m.pos != null && !hasStatus(m.statuses, "Destroyed")) : [];
  const member = live[0];
  if (!near || !member || live.length > 1) {
    warnings.push(`redeploy_object: no single deployed "${atom.groupId}" to redeploy (recorded as a no-op)`);
    return;
  }
  const world = worldFor(ctx.state);
  const legal = legalDirectedDeployCells({
    state: ctx.state,
    deployerId: near.id,
    size: member.base.size,
    placed: [],
    remaining: 0,
    ...(atom.placement ? { placement: atom.placement } : {}),
  });
  const cells = legal.map((c) => tagCell(encounterGridMode(ctx.state), c));
  const ids = cells.map((c) => world.key(c));
  const promptId = redeployObjectPromptId(atom.groupId);
  const answered = ctx.choices?.choose(promptId, ids);
  const offered = ctx.sites?.[0];
  const offeredKey = offered !== undefined ? world.key(offered) : undefined;
  const pickKey = answered ?? (offeredKey !== undefined && ids.includes(offeredKey) ? offeredKey : undefined);
  if (pickKey === undefined) {
    if (ctx.choices?.requireAnswer && cells.length > 0) {
      throw new NeedsDecisionSignal(promptId, `${near.name}: redeploy ${member.name}`, {
        options: cells.map((cell, j) => ({ id: ids[j]!, label: ids[j]!, cell })),
        who: near.id,
        answerKind: "cell",
      });
    }
    const why = offeredKey !== undefined ? `${offeredKey} is not a legal space` : cells.length === 0 ? "no legal space" : "no space picked";
    warnings.push(`redeploy_object: ${member.name} — ${why} (recorded as a no-op)`);
    return;
  }
  steps.push({ op: "move", target: member.id, from: member.pos ?? null, to: cells[ids.indexOf(pickKey)]! } satisfies MoveStep);
}

/**
 * Interpret a `repair_object_group` (ms_pattern_a_jericho_deployable_cover: "Repairing the
 * system restores both sections."). Looks up the `` `${near}:${groupId}` `` object group (the
 * SAME query {@link interpretRecallObject} uses) and, for every member currently carrying the
 * `"Destroyed"` status: removes that status AND heals it to max HP (`clampHp`-bound, mirroring
 * `set_hp`'s own clamp) — restoring it in place, never a respawn (a Destroyed object/deployable
 * is never removed from `state.combatants`). Spends `atom.cost`
 * (default 1) off the SAME actor's {@link REPAIR_CAP_RESOURCE}.
 *
 * No resolvable `near`, no Destroyed member in the group (an honest "nothing to repair" —
 * mirrors `spend_repairs track:"item"`'s own reject, but as a no-op here rather than a hard
 * command-level rejection), or not enough Repairs remaining, all record an honest no-op with a
 * deferral warning and spend NOTHING — never a partial restore or a wasted Repair.
 */
function interpretRepairObjectGroup(args: AtomHandlerArgs<RepairObjectGroupAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const near = resolveActor(atom.near ?? "self", ctx);
  if (!near) {
    warnings.push(
      `repair_object_group: "${atom.groupId}" — ${atom.near ?? "self"} isn't a live combatant (recorded as a no-op)`,
    );
    return;
  }
  const members = objectGroupMembers(ctx.state, `${near.id}:${atom.groupId}`);
  const destroyed = members.filter((c) => hasStatus(c.statuses, "Destroyed"));
  if (destroyed.length === 0) {
    warnings.push(
      `repair_object_group: no Destroyed section in "${atom.groupId}" near ${near.id} to restore (recorded as a no-op, nothing to repair)`,
    );
    return;
  }
  const cost = atom.cost ?? 1;
  const repairs = near.current.resources?.[REPAIR_CAP_RESOURCE] ?? 0;
  if (repairs < cost) {
    warnings.push(
      `repair_object_group: ${near.id} has no Repairs remaining to restore "${atom.groupId}" (recorded as a no-op)`,
    );
    return;
  }
  steps.push(...spendStepsNow([{ kind: "counter", carrier: near.id, key: REPAIR_CAP_RESOURCE, delta: -cost }], ctx.state, ctx.loop));
  for (const c of destroyed) {
    steps.push({ op: "status_remove", target: c.id, status: "Destroyed" });
    const after = clampHp(c, c.base.maxHp);
    const delta = after - c.current.hp;
    if (delta !== 0) steps.push({ op: "hp_delta", target: c.id, delta });
  }
}

/**
 * Interpret a `pause_status_expiry` (ms_stay_of_execution's
 * "any active effects on your target are frozen in time"): find the target's live
 * {@link atom.status}-named instance and emit a `status_suspend_set` step flipping its
 * {@link import("@lancer-vtt/domain").StatusInstance.suspended} flag to {@link
 * atom.suspended} (omitted → `true`, the suspend direction; `false` authored explicitly
 * resumes it). No live instance of that name, or the instance already sitting at the
 * requested suspended state, is an honest no-op — mirrors `add_status`/`remove_status`'s
 * own idempotent-by-presence discipline (never a duplicate/no-op step in the record).
 */
function interpretPauseStatusExpiry(args: AtomHandlerArgs<PauseStatusExpiryAtom>): void {
  const { atom, ctx, steps } = args;
  withTarget(atom.target, ctx, (tgt) => {
    const inst = tgt.statuses.find((s) => s.name === atom.status);
    if (!inst) return; // no live instance of this name — nothing to freeze/thaw.
    const suspended = atom.suspended ?? true;
    const prev = inst.suspended ?? false;
    if (suspended === prev) return; // already in the requested state — idempotent no-op.
    const step: StatusSuspendSetStep = { op: "status_suspend_set", target: tgt.id, status: atom.status, suspended, prev };
    steps.push(step);
  });
}

/**
 * Interpret an `extra_damage` rider: record the `damage` witness, then deal the damage through
 * Overshield and the damage cascade (`resolveDamageCascade`), against the target as the steps
 * landed ahead of it left it (`ctx.state` is the event loop's scratch, `decisions/0212` item 4). Two forms compose:
 *   - **flat** (`amount`) — a fixed extra (Knockback-adjacent bonus damage).
 *   - **rolled** (`dice: {count, sides}`) — rolls through the recorded
 *     `DiceSource` (the same stream saves draw, so a `for_each` body's per-target rolls
 *     record + replay in id-order), summed and added to the flat `amount`. `half: true`
 *     then halves the total rounded **up** (the "half on a save" branch — HEX Charges).
 * No-op on a zero/negative total (no draw, no step). Armor: see {@link ExtraDamageAtom.ap} (BC-019).
 *
 * **Resistance halves it**, folded against the attacker (`ctx.actors.attacker ?? ctx.actors.self`)
 * through `nonAttackResistant`, after every other halving here, and after Armor. The
 * halved total lands in `final`/`hp_delta`; `preMitigation` keeps the pre-Resistance total.
 *
 * **RAW's BONUS DAMAGE halving** (Core p.67's sidebar, restated in the p.78 COMBAT TERMINOLOGY
 * glossary — *"if an attack that targets more than one character deals bonus damage, the bonus
 * damage is halved"*) lands here, gated on BOTH halves of its own condition: the atom opted in
 * with `bonusDamage: true` (it is RAW's printed term, not an effect's own damage) AND the firing
 * resolution is an area attack ({@link EffectContext.areaAttack}). It is a SECOND, independent
 * `Math.ceil(x / 2)` applied AFTER `half`'s — the two are different rules that happen to share
 * their arithmetic (`half` is a save outcome; this is an attack-shape rule), and nothing shipped
 * carries both today, so the order is pinned by test rather than left to be re-derived.
 *
 * **Halving lands in `preMitigation`/`final` only.** `dice` and `bonus` keep their RAW draws,
 * exactly as the `half` branch already behaves — a step's `dice` is the record of what the
 * authority actually rolled, and a replay has to be able to reproduce it. Assert on `final`,
 * never on `dice`.
 *
 * **RAW's CRITICAL HIT doubling** (Core p.64 — *"On a critical hit, all damage dice are rolled
 * twice (including bonus damage) and the highest result from each source of damage is used"*)
 * applies to a rider's own dice: a rider IS its own source, so it makes the identical
 * `rollDamage({crit:true})` call the weapon path makes (`roll()` draws `count * 2` and keeps the
 * highest `count`) rather than re-implementing the arithmetic. It is **NOT** gated on
 * `bonusDamage` — p.64's parenthetical ADDS bonus damage to the set, it does not restrict the set
 * to it, and an unflagged crit-only dice rider must double too.
 *
 * **Except when the attacker is an NPC.** Core p.281: *"NPCs can't perform critical hits. Some
 * NPCs, such as Veterans and Ultras, can perform critical hits… If an NPC performs a critical
 * hit, it usually adds damage or has a different effect, **instead of rolling damage twice and
 * choosing the higher**."* That is an exception clause carving NPCs out of p.64's mechanic, not a
 * contradiction with it — and it is reachable, because `spawn/npc.ts` clears `noCrits` for the
 * `npct_veteran`/`npct_ultra` templates, so an NPC crit really does arrive here. The
 * discriminator is `Combatant.kind`, deliberately **NOT** `base.noCrits` (which is `undefined` for
 * exactly the Veteran/Ultra case, so a gate built on it inverts where it matters) and **NOT**
 * `allegiance` (a GM-run mech is `allegiance: "npc"` and crits under p.64 normally). Full ruling,
 * including the rejected narrow "crit-matched clauses only" gate and the drone/deployable
 * boundary: `decisions/0172`.
 *
 * Ordering is forced, not chosen: the doubling changes the ROLL; `half` and the area halving are
 * arithmetic on the resulting total. Roll (doubled or not) → Exposed → `half` → area halve. A flat
 * `amount` rider has no dice for the crit to double.
 *
 * **Exposed doubles a physical rider** (Core p.77, "before applying any reductions"), so ahead of
 * both halvings, on a hit or outside an attack alike (BC-069).
 */
function interpretExtraDamage(args: AtomHandlerArgs<ExtraDamageAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const tgt = resolveActor(atom.target, ctx);
  if (!tgt) return;
  if (chaffGruntSaved(tgt, ctx, warnings)) return;
  // p.64 × p.281 — see this function's doc. Both halves required: the firing IS a resolved crit,
  // AND the attacker is not an NPC. An unbound/unknown attacker reads as no doubling, the same
  // absent-means-false convention every other fact threaded onto `EffectContext` uses.
  const attackerKind = ctx.actors.attacker !== undefined ? ctx.state.combatants[ctx.actors.attacker]?.kind : undefined;
  const crit = ctx.event?.family === "attack" && ctx.event.crit === true && attackerKind !== undefined && attackerKind !== "npc";
  // The flat half is a `MagnitudeValue`, resolved HERE at interpret time — so a rider can scale
  // off the carrier's frozen tier, a frozen stat, or the firing attack's TARGET COUNT (the
  // `{TARGET_COUNT}` binding: Cluster Munitions' "+{2/3/4} … for each target beyond the first").
  // Resolved BEFORE the dice roll so the advisory a degraded read emits lands ahead of the draw,
  // and so `half`/the bonus-damage halving/the crit rules below all see one settled number.
  if (atom.bonusDamage && ctx.noBonusDamage) {
    warnings.push("bonus damage: this attack can't deal bonus damage — none dealt");
    return;
  }
  const flat = atom.amount === undefined ? 0 : resolveMagnitude(atom.amount, ctx, warnings);
  let dice: number[] = [];
  if (atom.dice && atom.dice.count > 0 && atom.dice.sides > 0) {
    const r = rollDamage({ source: ctx.dice, count: atom.dice.count, sides: atom.dice.sides, crit });
    dice = r.draws.filter((_, i) => r.kept[i]).map((d) => d.value);
  }
  const dtype: DamageType = (atom.bonusDamage ? ctx.bonusDamageType : undefined) ?? atom.dtype ?? "kinetic";
  const attackerId = ctx.actors.attacker ?? ctx.actors.self;
  // Exposed doubles physical damage "before applying any reductions" (Core p.77), so ahead of both
  // halvings (the damage packet's step 1, `event-engine-damage` D2; BC-069).
  const exposed = isPhysical(dtype) && nonAttackExposed({ state: ctx.state, targetId: tgt.id, attackerId: attackerId ?? null, overlay: ctx.overlay });
  const raw = (dice.reduce((a, b) => a + b, 0) + flat) * (exposed ? 2 : 1);
  const halved = atom.half ? Math.ceil(raw / 2) : raw;
  // RAW's bonus-damage halving — see this function's own doc. Both halves of the condition are
  // required: the atom must have opted in AND the resolution must be an area attack.
  const total = atom.bonusDamage && ctx.areaAttack ? Math.ceil(halved / 2) : halved;
  if (total <= 0) return;
  // Resistance halves the rider (Core p.66: "all incoming damage of that type"), after Armor
  // (below). An unbound attacker folds in no-attacker mode — see `nonAttackResistance.ts`.
  // A rider fired off an attack event is damage from that attack (Siege Armor's gate, see `seed.ts`).
  // A Brace on the attack this rider fired inside resists it too (RAW: "all damage … from the triggering attack").
  const resistant =
    ctx.reactionResisted?.target === tgt.id ||
    nonAttackResistant({ state: ctx.state, targetId: tgt.id, attackerId: attackerId ?? null, fromAttack: ctx.event?.family === "attack", dtype, overlay: ctx.overlay });
  // Armor (BC-019, Core p.67 "ARMOR reduces all incoming damage from a single source", subtracted
  // once "from the total" of an attack): an AP entry ignores it; damage fired on a hit against the
  // attack's target pays what the hit left; bonus damage an activation adds after a hit has no
  // attack in scope and skips it; any other damage is its own source and pays it in full.
  const ap = atom.ap === true;
  const shared = ctx.attackArmor?.target === tgt.id ? ctx.attackArmor : undefined;
  const targetArmor = ap ? 0 : shared ? shared.left : atom.bonusDamage ? 0 : nonAttackArmor({ state: ctx.state, targetId: tgt.id, attackerId: attackerId ?? null, overlay: ctx.overlay, armor: tgt.base.armor });
  // Provenance (`resolution-narration` 1.2): `ctx.firing` is set ONLY by the trigger bus, so a
  // rider fired any other way (a save/contest path, a direct atom interpret call in a test)
  // degrades to a source-less step — never an invented name. `carrier` names the conferring
  // combatant only when it differs from the attacker (mirrors `AccuracyContribution.carrier`'s
  // conferred-by-an-ally meaning); the attacker's own talent/system rider carries no `carrier`.
  // An activation's own effect list names its item instead (`ctx.activating`,
  // `narration-ledger-retirement` 2.3); the activating actor is `attackerId`, so no `carrier`.
  const source = ctx.firing
    ? { kind: "rider" as const, name: ctx.firing.name, ...(ctx.firing.carrier !== attackerId ? { carrier: ctx.firing.carrier } : {}) }
    : ctx.activating
      ? { kind: "rider" as const, name: ctx.activating.name }
      : undefined;
  // The rider is one damage packet (`resolve/damagePacket.ts`) landed through the pipeline's
  // per-instance core with the facts above. It lands on the target as everything recorded ahead
  // of it left it (the hit, an earlier rider), not on the entry state: BC-016. Overshield takes it
  // first (Core p.105), then the damage cascade, so overflow past 0 HP costs structure exactly as
  // one larger hit would (Core p.80), and burn is dealt and marked (Core p.66, HELLFIRE's bonus
  // damage as Burn). A damage-Immune target (RAW p.68) takes nothing: the witness is flagged.
  // `crit` is the same computed fact the roll used — `DamageStep.crit` is documented as "whether
  // this was a crit (double-rolled, higher half kept)", so an NPC attacker's undoubled rider
  // records `false` even though the ATTACK crit. That is the honest record of what was rolled.
  const immune = nonAttackImmune({ state: ctx.state, targetId: tgt.id, dtype, overlay: ctx.overlay });
  const landed = landDamageInstance({
    state: ctx.state,
    target: applyResolved(ctx.state, steps).combatants[tgt.id] ?? tgt,
    instance: { dtype, amount: total, dice, bonus: flat, crit },
    ap,
    armor: targetArmor,
    resistant,
    reduce: 0,
    immune,
    ...(source ? { witness: { source } } : {}),
    route: {
      dice: ctx.dice,
      // The recorded answers ride with `interactive`: an answered System Trauma pick re-runs into them.
      ...(ctx.choices !== undefined ? { choices: ctx.choices } : {}),
      ...(ctx.interactive !== undefined ? { interactive: ctx.interactive } : {}),
      selfCheck: selfCheckContext(ctx.state, ctx.overlay, { loop: ctx.loop }),
      from: attackerId ?? "gm",
    },
  });
  if (shared) shared.left -= landed.armorUsed;
  steps.push(landed.witness, ...landed.landing);
  warnings.push(...(immune ? [nonAttackImmuneWarning(tgt.name, dtype)] : landed.warnings));
  // A capped Resistance or Immunity the rider used is spent (R7, BC-077). A rider fired off an attack
  // leaves that to the attack, whose target-side fold has already counted the target's capped grants.
  if (ctx.event?.family !== "attack") {
    const grantCaps = nonAttackGrantCaps({ state: ctx.state, targetId: tgt.id, attackerId: attackerId ?? null, dtype, overlay: ctx.overlay, resisted: landed.witness.resisted && ctx.reactionResisted?.target !== tgt.id, immune });
    const capSteps = capUseSteps(grantCaps, ctx.loop);
    ctx.loop?.land(ctx.state, capSteps);
    steps.push(...capSteps);
  }
}

/** True, with a warning, when `tgt` is a Chaff Grunt whose passed save this branch belongs to: it takes no damage. */
function chaffGruntSaved(tgt: Combatant, ctx: EffectContext, warnings: string[]): boolean {
  if (ctx.saveNegatesDamageFor !== tgt.id) return false;
  warnings.push(`${tgt.name}: a Chaff Grunt takes no damage when it succeeds on a save.`);
  return true;
}

/**
 * Interpret a `damage` atom — a standalone, MITIGATED, cascade-integrated hit for a clause
 * firing OUTSIDE any attack resolution (Trample's `on:"trample"`, `npc-features-fielded-roster`
 * Phase 5.2). See {@link import("./atoms").DamageAtom}'s own doc for the full rationale
 * (why this is not `extra_damage`, and the interactive-choice envelope this atom accepts).
 *
 * Lands one damage packet (`resolve/damagePacket.ts`), as `bulldoze.ts`'s per-target loop body
 * does, narrowed to ONE target and ONE already-resolved magnitude
 * (no dice roll of its own — `resolveMagnitude` resolves `atom.amount` once, so a `{tier}`-
 * scaled Trample hit reads the mover's own frozen tier at THIS interpret call, off `ctx.actors.self`
 * — see `resolveMagnitude`'s `who` default). Resistance folds against the clause's source,
 * `ctx.actors.attacker ?? ctx.actors.self` — for Trample that is the mover, so the hit is halved
 * by a Resistance the crossed character holds against it (`nonAttackResistant`).
 */
function interpretDamage(args: AtomHandlerArgs<DamageAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const tgt = resolveActor(atom.target, ctx);
  if (!tgt) return;
  if (chaffGruntSaved(tgt, ctx, warnings)) return;
  const preMitigation = resolveMagnitude(atom.amount, ctx, warnings);
  if (preMitigation <= 0) return;
  const dtype: DamageType = atom.dtype ?? "kinetic";
  const ap = atom.ap ?? false;
  // Intercede (`nonAttackIntercept.ts`): a protector may take the hit instead, measured from the
  // clause's carrier, and mitigates it with its own defenses.
  const irreducible = atom.irreducible === true;
  const sink = irreducible ? undefined : ctx.intercede;
  const intercept =
    sink !== undefined && ctx.actors.self !== undefined
      ? interceptNonAttackDamage({ state: ctx.state, target: tgt, source: { combatant: ctx.actors.self }, emitter: `damage.${ctx.actors.self}`, amount: preMitigation, dtype, choices: sink.answers, interactive: sink.interactive, dice: ctx.dice, overlay: ctx.overlay, spentReactions: reactorsUsed(sink.ledger) })
      : undefined;
  if (intercept) {
    useReaction(sink!.ledger, [intercept.spend]);
    steps.push(...intercept.steps);
    warnings.push(...intercept.warnings);
  }
  // The damage packet (`resolve/damagePacket.ts`), sourced to the clause's source and landing on
  // the target as the steps ahead of it left it. Its witness is recorded every time, flagged when a
  // damage-Immune target (RAW p.68) takes nothing.
  const takerId = intercept ? intercept.protectorId : tgt.id;
  const landed = dealDamagePacket({
    packet: { kind: "damage_packet", source: ctx.actors.attacker ?? ctx.actors.self ?? null, target: takerId, instances: [{ dtype, amount: preMitigation }], fromAttack: false, ap, ...(irreducible ? { irreducible } : {}) },
    state: steps.length > 0 ? applyResolved(ctx.state, steps) : ctx.state,
    dice: ctx.dice,
    ...(ctx.overlay !== undefined ? { overlay: ctx.overlay } : {}),
    // The recorded answers ride with `interactive`: an answered System Trauma pick re-runs into them.
    ...(ctx.choices !== undefined ? { choices: ctx.choices } : {}),
    ...(ctx.interactive !== undefined ? { interactive: ctx.interactive } : {}),
    selfCheck: selfCheckContext(ctx.state, ctx.overlay, { loop: ctx.loop }),
    loop: ctx.loop,
  });
  steps.push(...landed.steps);
  warnings.push(...landed.warnings);
}

/**
 * Interpret a `create_area` — mint a standing
 * {@link AreaEffect} on the map. Resolves `origin` exactly like {@link
 * import("./atoms").AreaSelector.origin} (`"site"` reads `ctx.site`, the recorded
 * draw-and-record template-center; `"self"`/`"target"` read that combatant's own live cell —
 * the SAME resolution `enumerateArea`'s `resolveOrigin` uses in `spatialQuery.ts`, not a second
 * implementation). An unresolvable origin (no recorded site, an unplaced combatant) records an
 * honest no-op with a deferral warning — never a phantom cloud.
 *
 * **`source`** resolves via the SAME symbolic actor-ref vocabulary every other atom uses
 * (`atom.source ?? "self"`) — never hardcoded to the activating combatant, so a carrier like the
 * Smoke Mine can bind the created area's expiry to whoever detonated it rather than
 * whoever deployed it, just by authoring `source: "target"` (or whichever ref is bound to the
 * detonator at that firing point).
 *
 * **Id minting is deterministic** (mirrors `wreckIdFor`/`deploy_object`'s own
 * `` `${near}:${groupId}:${i}` `` discipline — no `crypto.randomUUID`, so a resolve-time replay
 * of the SAME authored clause against the SAME state always mints the SAME id), keyed on the
 * clause's own carrier (`ctx.actors.self`, deliberately NOT `source` — two different mines
 * detonated by the SAME character must still mint two distinct areas, and keying on the
 * possibly-overridden `source` would collide them) plus the resolved origin cell
 * (grid-agnostic via `world.key`, mirroring `areaContributions.ts`'s own use of `space.key`).
 * `area_add`'s own idempotent-by-presence discipline (`steps.ts`) then means a SECOND
 * `create_area` from the same carrier at the exact same origin cell, while the first cloud is
 * still live, is a no-op rather than a redundant second record — a named edge case (same
 * carrier, same cell, still up), not a general re-throw restriction: a different origin cell,
 * a different carrier, or the same carrier after the first cloud disperses each mint a fresh id.
 */
function interpretCreateArea(args: AtomHandlerArgs<CreateAreaAtom>): void {
  const { atom, ctx, warnings } = args;
  if (atom.origin === "site_pair") {
    const sites = ctx.sites ?? [];
    const self = selfId(ctx);
    if (sites.length !== 2 || self === undefined) {
      warnings.push(`create_area: needs two picked spaces, got ${sites.length} — no area created`);
      return;
    }
    const verdict = checkDirectedDeployPlacement({ state: ctx.state, deployerId: self, cells: sites, size: 1, placement: { mutualWithin: atom.pairWithin ?? 0 } });
    if (!verdict.ok) {
      warnings.push(`create_area: ${verdict.error} — no area created`);
      return;
    }
    createAreaAt(args, { origin: sites[0]!, pair: sites[1]! });
    // The first call removes a replaced pair; the second must not remove it again.
    createAreaAt(args, { origin: sites[1]!, pair: sites[0]!, second: true });
    return;
  }
  const origin: Cell | undefined = atom.origin === "site" ? ctx.site : resolveActor(atom.origin, ctx)?.pos ?? undefined;
  createAreaAt(args, { origin });
}

/** One area of a `create_area` at `origin` (with `pair` for a two-space mark). */
function createAreaAt(args: AtomHandlerArgs<CreateAreaAtom>, at: { origin: Cell | undefined; pair?: Cell; second?: true }): void {
  const { atom, ctx, steps, warnings } = args;
  const { origin, pair, second } = at;
  if (!origin) {
    warnings.push(
      `create_area: could not resolve origin "${atom.origin}" (no recorded site / unplaced combatant) — no area created`,
    );
    return;
  }
  const carrier = selfId(ctx);
  if (carrier === undefined) {
    warnings.push("create_area: no acting combatant bound (ctx.actors.self) — no area created");
    return;
  }
  const source = resolveActorId(atom.source ?? "self", ctx.actors);
  if (source === undefined) {
    warnings.push(`create_area: could not resolve source "${atom.source ?? "self"}" — no area created`);
    return;
  }
  const world = worldFor(ctx.state);
  const id = `${carrier}:area:${world.key(origin)}`;
  const entry = ctx.activatingEntry;
  // `replace`: the source's earlier area from this entry goes first, the same spot included.
  const replacing = atom.replace === true && entry !== undefined;
  if (replacing && second !== true) {
    for (const old of Object.values(ctx.state.areaEffects ?? {})) {
      if (old.source === source && old.entry === entry) steps.push({ op: "area_remove", area: old } satisfies AreaRemoveStep);
    }
  } else if (!replacing && ctx.state.areaEffects?.[id] !== undefined) return; // already live — idempotent no-op (mirrors deploy_object).
  const area: AreaEffect = {
    id,
    origin,
    shape: atom.shape,
    size: atom.size,
    grants: atom.grants,
    ...(entry !== undefined ? { entry } : {}),
    ...(pair !== undefined ? { pair } : {}),
    source,
    expiry: atom.until,
    // The ONE creation site, always with a live `ActivationState` in hand — stamp
    // unconditionally (a required field, unlike the optional `StatusInstance.appliedTurn`,
    // which has many creation sites).
    appliedTurn: ctx.state.activation.turnOrdinal,
  };
  const step: AreaAddStep = { op: "area_add", area };
  steps.push(step);
}

/**
 * Interpret a `for_each` — the area iterator. **Control flow:**
 * it enumerates the covered combatants **once** here at resolve (the rules-layer
 * {@link enumerateArea} seam — shape/origin/filter against the live map + the recorded
 * `site`), then runs the body per enumerated target, binding `target` to each and recursing
 * the interpreter so the body's atoms emit their grounded steps **through the same per-target
 * path a single-target effect uses** — never raw steps.
 * The `for_each` atom itself emits no step (it vanishes, like `if`/`choose`): the per-target
 * steps with explicit target ids ARE the record of who was hit, so replay folds them rules-
 * free and never re-enumerates. Enumeration order is `id`, so the recorded dice
 * stream feeds targets deterministically.
 */
function interpretForEach(args: AtomHandlerArgs<ForEachAtom>): void {
  const { atom, ctx, steps, warnings, pendingDamage } = args;
  const enumerated = enumerateFromContext(atom.in, ctx);
  warnings.push(...enumerated.warnings);
  for (const tgt of enumerated.combatants) {
    // Bind the enumerated entity as `as`; the rest of the context (state, dice, choices, site,
    // defaultSaveDc) is shared.
    const perTarget: EffectContext = { ...ctx, actors: { ...ctx.actors, [atom.as]: tgt.id } };
    for (const a of atom.effect) interpretAtom({ atom: a, ctx: perTarget, steps, warnings, pendingDamage });
  }
}

/**
 * Interpret a `choose_random` — pick ONE member of the enumerated set with a die from the
 * resolution's stream and run the body against it. See {@link ChooseRandomAtom}.
 */
function interpretChooseRandom(args: AtomHandlerArgs<ChooseRandomAtom>): void {
  const { atom, ctx, steps, warnings, pendingDamage } = args;
  const enumerated = enumerateFromContext(atom.in, ctx);
  warnings.push(...enumerated.warnings);
  const pool = enumerated.combatants.filter((c) => c.id !== ctx.actors.self);
  if (pool.length === 0) {
    warnings.push(resolutionNote(emptyPickLine(atom, ctx)));
    return;
  }
  let index = 0;
  if (pool.length > 1) {
    const face = ctx.dice.roll(pool.length);
    if (!Number.isInteger(face) || face < 1 || face > pool.length) {
      throw new Error(`choose_random: die face ${face} is outside [1, ${pool.length}]`);
    }
    index = face - 1;
  }
  const pick = pool[index]!;
  const names = pool.map((c) => c.name).join(", ");
  const record = { from: pool.map((c) => c.id), ...(pool.length > 1 ? { roll: index + 1 } : {}) };
  const picked: EffectContext = { ...ctx, actors: { ...ctx.actors, target: pick.id }, pick: record };
  const at = warnings.length;
  const queuedBefore = ctx.forcedAttacks?.length ?? 0;
  for (const a of atom.effect) interpretAtom({ atom: a, ctx: picked, steps, warnings, pendingDamage });
  // A pick that queued a forced attack rides that attack's `link` step instead, where the narrator
  // and the director read it; the prose line is only for a pick nothing else records.
  if ((ctx.forcedAttacks?.length ?? 0) > queuedBefore) return;
  warnings.splice(
    at,
    0,
    pool.length === 1
      ? `random pick: ${pick.name} (the only candidate)`
      : `random pick: ${pick.name} (d${pool.length} = ${index + 1}, from ${names})`,
  );
}

/**
 * The quiet narration note (`resolutionNote`) a pick with nobody to pick leaves on the result card, named from the resolution rather
 * than hardcoded: `Berserker · Aggression: no one adjacent, no attack`. The actor is the firing
 * clause's carrier (`ctx.firing`, set by the trigger bus) or else `self`; the feature is the firing
 * clause's name, and is left out when nothing fired it. The effect itself is still a no-op.
 */
function emptyPickLine(atom: { in: AreaSelector; effect: readonly EffectAtom[] }, ctx: EffectContext): string {
  const actorId = ctx.firing?.carrier ?? ctx.actors.self;
  const head = [actorId !== undefined ? ctx.state.combatants[actorId]?.name : undefined, ctx.firing ? displaySourceName(ctx.firing.name) : undefined].filter((x): x is string => x !== undefined).join(" · ");
  const sel = atom.in;
  const where = sel.shape === "disk" && sel.size === 1 && sel.origin === "self" ? "no one adjacent" : "no one in the area";
  const outcome = atom.effect.some((a) => a.op === "forced_attack") ? "no attack" : "nothing happens";
  return `${head !== "" ? `${head}: ` : ""}${where}, ${outcome}`;
}

/** The prompt id a `line`/`cone` selector asks its aim under — one per aiming combatant. */
export function aimPromptId(selfId: string): string {
  return `aim:${selfId}`;
}

/**
 * `enumerateArea` against the resolution's own recorded site, aim, rulings and move path. A
 * `line`/`cone` with no recorded aim asks `self` for one when the resolution is interactive and can
 * carry the answer back (the Roller Grenade's roll direction); otherwise the selector finds no one.
 */
function enumerateFromContext(selector: ForEachAtom["in"], ctx: EffectContext) {
  const aimed = selector.shape === "line" || selector.shape === "cone";
  const self = ctx.actors.self;
  if (aimed && ctx.toward === undefined && ctx.interactive && ctx.directionAnswerable !== false && self !== undefined) {
    throw new NeedsDecisionSignal(aimPromptId(self), `${ctx.state.combatants[self]?.name ?? self}: choose a direction`, { options: [], who: self, answerKind: "direction" });
  }
  if (aimed && ctx.toward !== undefined && self !== undefined && ctx.ledger) ctx.ledger.answeredDirectionPromptId = aimPromptId(self);
  return enumerateArea({
    state: ctx.state,
    selector: selector.size_from === undefined ? selector : { ...selector, size: sizeFrom(selector.size_from, ctx) ?? selector.size },
    actors: ctx.actors,
    ...(ctx.site !== undefined ? { site: ctx.site } : {}),
    ...(aimed && ctx.toward !== undefined ? { toward: ctx.toward } : {}),
    ...(ctx.choices !== undefined ? { rulings: ctx.choices } : {}),
    ...(ctx.movePath !== undefined ? { movePath: ctx.movePath } : {}),
    ...(ctx.ledger?.collisions !== undefined ? { collisions: ctx.ledger.collisions } : {}),
  });
}

/** The size an area's `size_from` names, or `undefined` when the fact is missing (the authored `size` stands). */
function sizeFrom(from: "weapon_threat" | "sensors", ctx: EffectContext): number | undefined {
  if (from === "weapon_threat") return triggeringWeaponThreat(ctx);
  return ctx.actors.self !== undefined ? ctx.state.combatants[ctx.actors.self]?.base.sensors : undefined;
}

/** The Threat of the weapon whose attack fired this clause (`AreaSelector.size_from`), read off the attacker's frozen profile; `undefined` off a non-attack fire. */
function triggeringWeaponThreat(ctx: EffectContext): number | undefined {
  const instance = ctx.event?.family === "attack" ? ctx.event.weaponInstance : undefined;
  const self = ctx.actors.self !== undefined ? ctx.state.combatants[ctx.actors.self] : undefined;
  const weapon = instance !== undefined ? self?.base.weapons?.find((w) => weaponInstanceKey(w) === instance) : undefined;
  return weapon ? threatValue(weapon.range ?? []) : undefined;
}

/**
 * Interpret a `choose_from_area` — the decider picks ONE member of the enumerated set, recorded
 * under `atom.prompt`, and the body runs against it. See {@link ChooseFromAreaAtom}.
 */
function interpretChooseFromArea(args: AtomHandlerArgs<ChooseFromAreaAtom>): void {
  const { atom, ctx, steps, warnings, pendingDamage } = args;
  const enumerated = enumerateFromContext(atom.in, ctx);
  warnings.push(...enumerated.warnings);
  const pool = atom.include_self ? enumerated.combatants : enumerated.combatants.filter((c) => c.id !== ctx.actors.self);
  if (pool.length === 0) {
    warnings.push(resolutionNote(emptyPickLine(atom, ctx)));
    return;
  }
  if (atom.count === "any") {
    interpretMultiPick({ atom, ctx, steps, warnings, pendingDamage }, pool);
    return;
  }
  const ids = pool.map((c) => c.id);
  const answered = pool.length === 1 ? ids[0] : ctx.choices?.choose(atom.prompt, ids);
  if (answered === undefined && ctx.choices?.requireAnswer) {
    const self = ctx.actors.self !== undefined ? ctx.state.combatants[ctx.actors.self] : undefined;
    throw new NeedsDecisionSignal(atom.prompt, `${self?.name ?? "The actor"}: pick a character`, {
      options: pool.map((c) => ({ id: c.id, label: c.name })),
      answerKind: "combatant",
    });
  }
  const pick = pool.find((c) => c.id === answered) ?? pool[0]!;
  if (ctx.ledger) ctx.ledger.choices[atom.prompt] = pick.id;
  const names = pool.map((c) => c.name).join(", ");
  warnings.push(
    pool.length === 1
      ? `pick: ${pick.name} (the only candidate)`
      : answered === undefined
        ? `pick: ${pick.name} (unanswered — took the first of ${names})`
        : `pick: ${pick.name} (from ${names})`,
  );
  const picked: EffectContext = { ...ctx, actors: { ...ctx.actors, [atom.as]: pick.id } };
  for (const a of atom.effect) interpretAtom({ atom: a, ctx: picked, steps, warnings, pendingDamage });
}

/**
 * A `choose_from_area` with `count: "any"`: the decider picks any set of `pool` (none included),
 * recorded under `atom.prompt` as the ids joined by `,`, and the body runs once per pick in pool
 * order. Unanswered: a `"combatants"` halt in interactive mode, else no pick, with a warning.
 */
function interpretMultiPick(args: AtomHandlerArgs<ChooseFromAreaAtom>, pool: Combatant[]): void {
  const { atom, ctx, steps, warnings, pendingDamage } = args;
  const ids = pool.map((c) => c.id);
  const answered = ctx.choices?.chooseMany?.(atom.prompt, ids);
  if (answered === undefined && ctx.choices?.requireAnswer) {
    const self = ctx.actors.self !== undefined ? ctx.state.combatants[ctx.actors.self] : undefined;
    throw new NeedsDecisionSignal(atom.prompt, `${self?.name ?? "The actor"}: pick any characters`, {
      options: pool.map((c) => ({ id: c.id, label: c.name })),
      answerKind: "combatants",
    });
  }
  const picks = pool.filter((c) => answered?.includes(c.id));
  if (ctx.ledger) ctx.ledger.choices[atom.prompt] = picks.map((c) => c.id).join(",");
  warnings.push(answered === undefined ? `pick: none (unanswered — from ${pool.map((c) => c.name).join(", ")})` : `pick: ${picks.map((c) => c.name).join(", ") || "none"}`);
  for (const pick of picks) {
    const picked: EffectContext = { ...ctx, actors: { ...ctx.actors, [atom.as]: pick.id } };
    for (const a of atom.effect) interpretAtom({ atom: a, ctx: picked, steps, warnings, pendingDamage });
  }
}

/** The prompt id a `move_up_to` asks under — one per mover. */
export function moveUpToPromptId(moverId: string): string {
  return `move_up_to:${moverId}`;
}

/** Interpret a `move_up_to`: the decider picks a space within the mover's reach. See {@link MoveUpToAtom}. */
function interpretMoveUpTo(args: AtomHandlerArgs<MoveUpToAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const mover = resolveActor(atom.target ?? "target", ctx);
  if (!mover?.pos) return;
  const world = worldFor(ctx.state);
  const cells = moveReachAnchors({ state: ctx.state, moverId: mover.id, dist: atom.dist }).map((c) => tagCell(encounterGridMode(ctx.state), c));
  const ids = cells.map((c) => world.key(c));
  const promptId = moveUpToPromptId(mover.id);
  const answered = ctx.choices?.choose(promptId, ids);
  const to = answered !== undefined ? cells[ids.indexOf(answered)] : undefined;
  if (answered === undefined || to === undefined) {
    if (ctx.choices?.requireAnswer) {
      const self = ctx.actors.self !== undefined ? ctx.state.combatants[ctx.actors.self] : undefined;
      throw new NeedsDecisionSignal(promptId, `${self?.name ?? "The actor"}: move ${mover.name} up to ${atom.dist} spaces (its own space leaves it)`, {
        options: cells.map((cell, j) => ({ id: ids[j]!, label: ids[j]!, cell })),
        answerKind: "cell",
      });
    }
    warnings.push(`move_up_to: ${mover.name} stays (no space in reach picked)`);
    return;
  }
  if (ctx.ledger) ctx.ledger.choices[promptId] = answered;
  if (world.equals(to, mover.pos)) return;
  const step: ForcedMoveStep = { op: "forced_move", target: mover.id, from: mover.pos, to, distance: forcedMoveDistance(ctx.state, mover.pos, to) };
  steps.push(step);
}

/** The prompt id a tied `place_adjacent` asks under — one per relocated combatant. */
export function placeAdjacentPromptId(targetId: string): string {
  return `place_adjacent:${targetId}`;
}

/**
 * Interpret a `place_adjacent` — relocate `target` to the nearest legal space adjacent to where
 * this resolution left `to`, the GM settling a tie. See {@link PlaceAdjacentAtom}.
 */
function interpretPlaceAdjacent(args: AtomHandlerArgs<PlaceAdjacentAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const victim = resolveActor(atom.target ?? "target", ctx);
  const anchorOf = resolveActor(atom.to ?? "self", ctx);
  if (!victim?.pos || !anchorOf) {
    warnings.push(`place_adjacent: no placed target or anchor bound — not moved`);
    return;
  }
  const moved = ctx.movePath !== undefined && ctx.movePath.mover === anchorOf.id ? ctx.movePath.path.at(-1) : undefined;
  const anchor = moved ?? anchorOf.pos;
  if (!anchor) {
    warnings.push(`place_adjacent: ${anchorOf.name} is not on the map — ${victim.name} not moved`);
    return;
  }
  // Involuntary relocation: the SAME blanket-immunity gate `forced_move` runs. Heavy Frame's
  // size-gated veto runs below, once a pull would actually happen.
  if (immuneToInvoluntaryMovement({ state: ctx.state, targetId: victim.id, overlay: ctx.overlay })) {
    warnings.push(`${victim.name}: IMMUNE — can't be moved; not pulled.`);
    return;
  }
  if (isRooted(victim)) {
    warnings.push(rootedVetoWarning(victim, "pulled"));
    return;
  }
  // `ctx.state` is the pre-resolution snapshot: stand the anchor where this resolution left it,
  // so its old space reads free and its new one occupied.
  const scratch: EncounterState = moved ? { ...ctx.state, combatants: { ...ctx.state.combatants, [anchorOf.id]: { ...anchorOf, pos: moved } } } : ctx.state;
  const world = worldFor(scratch);
  const landings = nearestAdjacentLandings({
    world,
    anchor,
    anchorSize: anchorOf.base.size,
    draggeeSize: victim.base.size,
    near: victim.pos,
    endpointLegal: placementFitter(scratch, { mover: victim.id, size: victim.base.size }),
  });
  if (landings.length === 0) {
    warnings.push(`place_adjacent: no free space adjacent to ${anchorOf.name} — ${victim.name} not moved`);
    return;
  }
  if (landings.some((c) => world.equals(c, victim.pos!))) return; // already adjacent — stays put.
  // Heavy Frame: "can't be pulled by smaller characters". Before the tie prompt, so a vetoed pull
  // never asks the GM where to put it.
  if (vetoedBySmallerCharacter({ ctx, targetId: victim.id, blocks: "forced_move", what: "pulled", warnings })) return;
  let landing = landings[0]!;
  if (landings.length > 1) {
    const promptId = placeAdjacentPromptId(victim.id);
    const keys = landings.map((c) => world.key(c));
    const answered = ctx.choices?.choose(promptId, keys);
    if (answered === undefined && ctx.choices?.requireAnswer) {
      throw new NeedsDecisionSignal(promptId, `Place ${victim.name} adjacent to ${anchorOf.name}`, {
        options: landings.map((c, j) => ({ id: keys[j]!, label: keys[j]!, cell: tagCell(encounterGridMode(ctx.state), c) })),
        answerKind: "cell",
      });
    }
    if (answered === undefined) warnings.push(`place_adjacent: ${keys.length} spaces tie for ${victim.name} — took ${keys[0]}`);
    landing = landings[keys.indexOf(answered ?? keys[0]!)]!;
  }
  const to = tagCell(encounterGridMode(ctx.state), landing);
  const step: ForcedMoveStep = { op: "forced_move", target: victim.id, from: victim.pos, to, distance: world.distance(victim.pos, landing) };
  steps.push(step);
}

/** Interpret a `destroy_system`: mark the first live equipped `system` on `who` destroyed. See {@link DestroySystemAtom}. */
function interpretDestroySystem(args: AtomHandlerArgs<DestroySystemAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const who = resolveActor(atom.who ?? "self", ctx);
  const live = who?.base.equippedSystems?.find((s) => s.id === atom.system && !isItemDestroyed(who.current.resources, s.instanceId));
  if (!who || !live) {
    warnings.push(`destroy_system: no live ${atom.system} to destroy`);
    return;
  }
  const key = destroyedItemResourceKey(live.instanceId);
  steps.push({ op: "resource_set", target: who.id, key, val: 1, prev: who.current.resources?.[key] ?? 0, ...(who.current.resources?.[key] === undefined ? { prevAbsent: true as const } : {}) });
  warnings.push(`${who.name}: ${live.name} is destroyed.`);
}

/** The prompt id a `move_spaces` landing pick asks under — one per mover. */
export function moveSpacesPromptId(moverId: string): string {
  return `move_spaces:${moverId}`;
}

/** Interpret a `move_spaces`: move `who` to a picked space a `dist`-space move reaches. See {@link MoveSpacesAtom}. */
function interpretMoveSpaces(args: AtomHandlerArgs<MoveSpacesAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const mover = resolveActor(atom.who ?? "self", ctx);
  if (!mover?.pos) {
    warnings.push(`move_spaces: no placed combatant to move — not moved`);
    return;
  }
  const bar = voluntaryMoveBar(mover);
  if (bar !== null) {
    warnings.push(`${mover.name} can't move: ${bar}`);
    return;
  }
  const dist = resolveMagnitude(atom.dist, ctx, warnings);
  const world = worldFor(ctx.state);
  const stay = world.key(mover.pos);
  const landings = grantedMoveLandings(ctx.state, mover, { budget: dist, engagementStops: atom.engagement === "stops" }).map((c) => tagCell(encounterGridMode(ctx.state), c));
  if (!landings.some((c) => world.key(c) === stay)) landings.unshift(tagCell(encounterGridMode(ctx.state), mover.pos));
  const keys = landings.map((c) => world.key(c));
  const promptId = moveSpacesPromptId(mover.id);
  const answered = ctx.choices?.choose(promptId, keys);
  if (answered === undefined) {
    if (ctx.choices?.requireAnswer) {
      throw new NeedsDecisionSignal(promptId, `${mover.name}: move up to ${dist} spaces (pick its own space to stay)`, {
        options: landings.map((c, j) => ({ id: keys[j]!, label: keys[j] === stay ? `${keys[j]!} (stay)` : keys[j]!, cell: c })),
        who: mover.id,
        answerKind: "cell",
      });
    }
    warnings.push(`move_spaces: no space picked for ${mover.name} — not moved`);
    return;
  }
  if (answered === stay) return;
  steps.push({ op: "move", target: mover.id, from: mover.pos, to: landings[keys.indexOf(answered)]! } satisfies MoveStep);
}

/** The prompt id a `teleport`'s landing pick asks under — one per teleporting combatant. */
export function teleportPromptId(moverId: string): string {
  return `teleport:${moverId}`;
}

/** Interpret a `teleport`: move `who` to a picked free space within range. See {@link TeleportAtom}. */
function interpretTeleport(args: AtomHandlerArgs<TeleportAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  if (atom.swap !== undefined) {
    interpretTeleportSwap({ ...args, atom });
    return;
  }
  const mover = resolveActor(atom.who ?? "self", ctx);
  if (!mover?.pos) {
    warnings.push(`teleport: no placed combatant to teleport — not moved`);
    return;
  }
  const chooser = atom.chooser !== undefined ? resolveActor(atom.chooser, ctx) : mover;
  if (!chooser) {
    warnings.push(`teleport: no chooser resolved for ${mover.name}'s teleport — not moved`);
    return;
  }
  if (atom.involuntary === true) {
    if (immuneToInvoluntaryMovement({ state: ctx.state, targetId: mover.id, overlay: ctx.overlay })) {
      warnings.push(`${mover.name}: IMMUNE — can't be moved involuntarily; not teleported.`);
      return;
    }
  } else {
    // A voluntary teleport is still movement (RAW p.63), so what bars a voluntary move bars it.
    const bar = voluntaryMoveBar(mover);
    if (bar !== null) {
      warnings.push(`${mover.name} can't teleport: ${bar}`);
      return;
    }
  }
  const world = worldFor(ctx.state);
  let from: Cell[] | undefined;
  if (atom.from === "site") {
    if (ctx.site === undefined) {
      warnings.push(`teleport: no recorded site to measure ${mover.name}'s teleport from — not moved`);
      return;
    }
    from = [ctx.site];
  } else if (atom.from !== undefined) {
    const anchor = resolveActor(atom.from, ctx);
    if (!anchor?.pos) {
      warnings.push(`teleport: "${atom.from}" is not on the map — ${mover.name} not teleported`);
      return;
    }
    from = world.footprint(anchor.pos, anchor.base.size).cells();
  }
  const dist = resolveMagnitude(atom.dist, ctx, warnings);
  const stay = world.key(mover.pos);
  const landings = teleportLandings({ state: ctx.state, mover, range: dist, ...(from !== undefined ? { from } : {}) })
    .filter((c) => atom.optional === true || world.key(c) !== stay)
    .map((c) => tagCell(encounterGridMode(ctx.state), c));
  if (atom.optional === true && !landings.some((c) => world.key(c) === stay)) landings.unshift(tagCell(encounterGridMode(ctx.state), mover.pos));
  if (landings.length === 0) {
    warnings.push(`teleport: no free space within ${dist} for ${mover.name} — not moved`);
    return;
  }
  const promptId = teleportPromptId(mover.id);
  const keys = landings.map((c) => world.key(c));
  const answered = ctx.choices?.choose(promptId, keys);
  if (answered === undefined) {
    if (ctx.choices?.requireAnswer) {
      const verb = chooser.id === mover.id ? `${mover.name}: teleport` : `${chooser.name}: teleport ${mover.name}`;
      throw new NeedsDecisionSignal(promptId, `${verb} up to ${dist} spaces${atom.optional === true ? " (pick its own space to stay)" : ""}`, {
        options: landings.map((c, j) => ({ id: keys[j]!, label: keys[j] === stay ? `${keys[j]!} (stay)` : keys[j]!, cell: c })),
        who: chooser.id,
        answerKind: "cell",
      });
    }
    warnings.push(`teleport: no space picked for ${mover.name} — not moved`);
    return;
  }
  if (answered === stay) return;
  const to = landings[keys.indexOf(answered)]!;
  const step: ForcedMoveStep = { op: "forced_move", target: mover.id, from: mover.pos, to, distance: world.distance(mover.pos, to), teleport: true };
  steps.push(step, ...kraulLineSnapSteps(ctx.state, mover.id));
}

/** Interpret a `teleport { swap }`: `who` and `swap` trade anchors. See {@link TeleportSwapAtom}. */
function interpretTeleportSwap(args: AtomHandlerArgs<TeleportSwapAtom>): void {
  const { atom, ctx, steps, warnings } = args;
  const mover = resolveActor(atom.who ?? "self", ctx);
  const other = resolveActor(atom.swap, ctx);
  if (!mover?.pos || !other?.pos) {
    warnings.push(`teleport: swap needs two placed combatants — not swapped`);
    return;
  }
  const bar = voluntaryMoveBar(mover);
  if (bar !== null) {
    warnings.push(`${mover.name} can't teleport: ${bar}`);
    return;
  }
  if (immuneToInvoluntaryMovement({ state: ctx.state, targetId: other.id, overlay: ctx.overlay })) {
    warnings.push(`${other.name}: IMMUNE — can't be moved involuntarily; not swapped.`);
    return;
  }
  if (!swapFits(ctx.state, { a: mover.id, b: other.id })) {
    warnings.push(`teleport: ${mover.name} and ${other.name} can't swap — a footprint wouldn't land legally`);
    return;
  }
  const world = worldFor(ctx.state);
  const distance = world.distance(mover.pos, other.pos);
  steps.push({ op: "forced_move", target: mover.id, from: mover.pos, to: other.pos, distance, teleport: true });
  steps.push({ op: "forced_move", target: other.id, from: other.pos, to: mover.pos, distance, teleport: true });
  steps.push(...kraulLineSnapSteps(ctx.state, mover.id), ...kraulLineSnapSteps(ctx.state, other.id));
}

/**
 * Interpret a `contested_roll` (see {@link ContestedRollAtom}): `self` and `vs` each roll 1d20 plus
 * their attack bonus, with no Accuracy or Difficulty; `self` initiated, so it wins ties. Records a
 * `contest` step, then runs the taken branch.
 */
function interpretContestedRoll(args: AtomHandlerArgs<ContestedRollAtom>): void {
  const { atom, ctx, steps, warnings, pendingDamage } = args;
  const self = resolveActor("self", ctx);
  const other = resolveActor(atom.vs, ctx);
  if (!self || !other) {
    warnings.push(`contested_roll: could not resolve ${!self ? "self" : atom.vs} — no contest`);
    return;
  }
  const bonusOf = (c: Combatant): number =>
    ctx.inFlightAttack?.attacker === c.id ? ctx.inFlightAttack.bonus : c.base.attackBonus ?? 0;
  const roll = (c: Combatant): ContestStep["challengerRoll"] => {
    const d20 = rollD20(ctx.dice, 0).draws[0]!.value;
    const bonus = bonusOf(c);
    return { d20, accuracy: 0, bonus, total: d20 + bonus };
  };
  const challengerRoll = roll(self);
  const defenderRoll = roll(other);
  const { winner, tie } = compareContestRolls(challengerRoll.total, defenderRoll.total);
  steps.push({
    op: "contest",
    challenger: self.id,
    defender: other.id,
    challengerAxis: atom.kind,
    defenderAxis: atom.kind,
    challengerRoll,
    defenderRoll,
    winner,
    tie,
  });
  const branch = winner === "challenger" ? atom.on_win ?? [] : atom.on_lose ?? [];
  for (const a of branch) interpretAtom({ atom: a, ctx, steps, warnings, pendingDamage });
}

/**
 * Interpret a `forced_attack` — queue it for the referee (see {@link ForcedAttackAtom}); emits no
 * step. Degrades to an advisory at a site that does not finish forced attacks.
 */
function interpretForcedAttack(args: AtomHandlerArgs<ForcedAttackAtom>): void {
  const { atom, ctx, warnings } = args;
  const attacker = resolveActor(atom.attacker ?? "self", ctx)?.id;
  const target = resolveActor(atom.target ?? "target", ctx);
  const weaponLabel = typeof atom.weapon === "string" ? atom.weapon : "a picked weapon";
  if (attacker === undefined || target === undefined) {
    warnings.push(`forced attack (${weaponLabel}): no attacker or target bound — no attack`);
    return;
  }
  const attackerName = ctx.state.combatants[attacker]?.name ?? attacker;
  // A secondary attack's area can catch its own wielder (the Annihilator's Burst 1 around an
  // adjacent target); nothing attacks itself.
  if (atom.secondary === true && attacker === target.id) {
    warnings.push(`forced attack: ${attackerName} is caught by its own secondary attack — no attack on itself`);
    return;
  }
  const weapon = typeof atom.weapon === "string" ? atom.weapon : ctx.pickedWeapon;
  if (weapon === undefined) {
    warnings.push(`forced attack: ${attackerName} may attack ${target.name} with a weapon of its choice — no weapon was picked at this site, resolve it by hand`);
    return;
  }
  const repeat = atom.weapon === "triggering" ? ctx.triggeringAttack : undefined;
  if (atom.weapon === "triggering" && repeat === undefined) {
    warnings.push(`forced attack: ${attackerName} must repeat the triggering attack against ${target.name} — no triggering attack at this site, resolve it by hand`);
    return;
  }
  if (ctx.forcedAttacks === undefined) {
    warnings.push(`forced attack: ${attackerName} must attack ${target.name} with ${weapon} — not resolved at this site, resolve it by hand`);
    return;
  }
  // What caused it, for the `link` step: the trigger-bus clause that fired (Aggression), else the
  // activation whose own effect queued it (Avalanche Charge).
  const self = ctx.actors.self;
  const feature = ctx.firing ?? (ctx.activating !== undefined && self !== undefined ? { name: ctx.activating.name, carrier: self } : undefined);
  const causing = (ctx.loop ?? ctx.ledger?.loop)?.current();
  ctx.forcedAttacks.push({
    ...(causing !== undefined ? { cause: causing.id } : {}),
    attacker,
    target: target.id,
    weapon: repeat?.weapon ?? weapon,
    ...(feature !== undefined ? { feature } : {}),
    ...(ctx.pick !== undefined && ctx.actors.target === target.id ? { pick: ctx.pick } : {}),
    ...(repeat !== undefined ? { repeat } : {}),
    ...(atom.reactions === false ? { reactions: false as const } : {}),
    ...(atom.consume === false ? { consume: false as const } : {}),
    ...(atom.autoHit === true ? { autoHit: true as const } : {}),
    ...(atom.secondary === true ? { secondary: true as const } : {}),
  });
}

/**
 * Interpret a `force_save`: roll the save (one `save` *record* step), then run
 * the branch's atoms (their grounded steps emitted separately — `save` is record-only
 * and the branch effects are siblings). The `force_save` atom
 * itself never appears in the output.
 *
 * **Choosing to fail** (RAW Core p.13: "a character may always choose to fail a check
 * or save"): before rolling, checks the SAME recorded-
 * choice seam every other in-resolution decision rides (`ctx.choices`) for a
 * `concede_save:<actorId>` answer of `"concede"`. A concession is **pre-declared, not
 * interactively prompted** — no `NeedsDecisionSignal` fires when the choice is
 * unanswered (unlike `choose`); it just rolls normally, the faithful default. This
 * mirrors how Ram's optional knock-back (`ram_push`) is a pre-declared choice, not a
 * mid-resolution round-trip: a player who wants to concede says so up front (a CLI
 * `--choice concede_save:<actorId>=concede`, or a future chooser passing the same
 * key), and this atom honors it — no die is drawn, the save is recorded as an
 * automatic **fail** (`SaveStep.conceded: true`), and the `on_fail` branch runs exactly
 * as a rolled failure would (a concession is a real failure, not a no-op).
 *
 * **Kind-rejects a non-character:**
 * RAW objects/deployables aren't characters and never save. Rather than faking a
 * save bonus for one, an object/deployable resolved actor is a clean no-op — no
 * `save` step, no branch — with an advisory warning so a driver can see why (an
 * authored on-hit rider that forces a save on a target caught by an AoE that
 * happened to include an object doesn't crash the whole resolution; it just skips
 * that one target's save, exactly like an unresolved actor id already does).
 *
 * Gates on `isCharacter`, NOT `isActor`: a
 * drone is a non-actor (no turn) but IS a character and rolls +0 saves — `isActor`
 * would wrongly reject its forced save.
 *
 * **Fires `forced_save_declared`** at the
 * moment a save is actually forced on a live character — after the object/deployable
 * kind-reject above (an object never "has a save declared against it"), before the
 * auto-fail/concede/roll dance (mirrors `on_attack_declared` firing before the to-hit
 * roll). Guarded by {@link forceSaveDepth} to fire ONLY at the outermost `force_save` in
 * the current synchronous call — see that guard's own doc for why.
 */
function interpretForceSave(args: AtomHandlerArgs<ForceSaveAtom>): void {
  const { atom, ctx, steps, warnings, pendingDamage } = args;
  const actor = resolveActor(atom.actor, ctx);
  if (!actor) return;
  if (!isCharacter(actor)) {
    warnings.push(`force_save: ${actor.kind} "${actor.name}" isn't a character — objects don't save`);
    return;
  }
  const isTopLevelForce = forceSaveDepth === 0;
  forceSaveDepth++;
  try {
    if (isTopLevelForce) fireForcedSaveDeclared({ actor, ctx, steps, warnings, atom });
    interpretForceSaveRoll({ atom, ctx, actor, steps, warnings, pendingDamage });
  } finally {
    forceSaveDepth--;
  }
}

/**
 * Re-entrancy guard for `forced_save_declared`: `interpretForceSave` recurses in two shapes — (a) a `force_save`'s
 * own `on_pass`/`on_fail` branch can author ANOTHER `force_save` (a genuine cascading
 * save), and (b) the trigger fired here can itself gather a handler whose effect forces
 * a save. Either shape re-enters `interpretForceSave` while the outer call is still on
 * the stack.
 *
 * Firing `forced_save_declared` on EVERY re-entrant call would let shape (b) loop
 * forever (a fired handler that forces a save re-fires the same point, whose gathered
 * handlers force a save, …) — and even shape (a) firing once per nested save reads as a
 * dubious "declared" narrative (RAW-wise there is one compelling declaration; a
 * cascading branch is its CONSEQUENCE, not a fresh declaration).
 *
 * The guard: fire ONLY at the OUTERMOST `interpretForceSave` call in the current
 * synchronous stack. Safe as a bare module-level counter because interpretation is
 * synchronous top-to-bottom (no `await`, no yields) — there is exactly one call stack in
 * flight at a time, and `try`/`finally` in {@link interpretForceSave} decrements it on
 * every exit path (including a thrown `NeedsDecisionSignal`). Any `force_save` reached
 * while `depth > 0` (a cascading branch OR a fired handler's own force_save) skips the
 * fire — it still rolls/resolves normally, only the trigger RE-FIRE is suppressed.
 * Proven by `interpret.test.ts`'s "forced_save_declared" re-entrancy describe block.
 */
let forceSaveDepth = 0;

/**
 * Fire `forced_save_declared` at the moment `interpretForceSave` forces a save on a live
 * character `actor`. Mirrors `resolve.ts`'s
 * `resolveLifecycleTrigger` — a
 * carrier-only gather (status- + pilot-effect-borne handlers), NOT `on_hit`'s
 * weapon-borne one: by the time interpretation reaches this deep into the interpreter
 * the firing weapon (if any) is out of scope, and the invariant every call site DOES
 * share is "the compelling actor's own frozen effects" — the same invariant a lifecycle
 * point rides.
 *
 * `ctx.actors.self` is the COMPELLING actor — the clause's own owner (the attacker for
 * Concussion Missiles' on-hit `force_save`, the activating pilot for an action's own
 * `force_save`, the carrier for a lifecycle-point clause) — set at every `EffectContext`
 * construction site (`resolve/`, `reactionRegistry.ts`, `triggerBus.ts`'s own
 * `fireTrigger`). `primaryTarget` is the combatant BEING forced to save (`actor`, already
 * resolved + character-gated by the caller); `attacker` is the compelling actor,
 * mirroring `on_attack_declared`'s attacker/target framing for a "declared" point.
 *
 * `consumedCaps` (a matched handler capped via `consumeResourceOnApply`) settle here, after the
 * fired steps (`capUseSteps`): this fires from every `interpretEffect` call site transitively
 * (on-hit, on-attack-declared, an action, a reaction offer, a lifecycle point), and not all of
 * them own a ledger that settles.
 */
function fireForcedSaveDeclared(args: { actor: Combatant; ctx: EffectContext; steps: ResolvedStep[]; warnings: string[]; atom: ForceSaveAtom }): void {
  const { actor, ctx, steps, warnings, atom } = args;
  const selfId = ctx.actors.self;
  if (!selfId) return; // no compelling actor bound — defensive; every construction site sets this.
  const overlay = ctx.overlay ?? SEED_OVERLAY;
  // `gatherHandlersAt` walks status → pilot → system, `CARRIER_VECTORS`' canonical order (a
  // carrier-only gather, mirroring `resolveLifecycleTrigger`'s own shape, not `on_hit`'s
  // weapon-borne one). `forced_save_declared` lives in the `save` family
  // (`match: { phase: "declared" }`, `family.ts`'s `SaveMatch`) — `on_save`'s `phase: "resolved"`
  // sibling has no fire site yet, so `family: "save"` is unambiguous here today.
  const handlers: Handler[] = gatherHandlersAt({ state: ctx.state, carrierId: selfId, overlay, family: "save" });
  if (handlers.length === 0) return;
  const fired = fireTrigger("save", staticHandlers(handlers), {
    state: ctx.state,
    primaryTarget: actor.id,
    attacker: selfId,
    dice: ctx.dice,
    choices: ctx.choices,
    defaultSaveDc: ctx.defaultSaveDc,
    ...(ctx.site !== undefined ? { site: ctx.site } : {}),
    ...(ctx.statusCause !== undefined ? { statusCause: ctx.statusCause } : {}),
    overlay,
    // A real `SaveEvent` (`phase: "declared"`), built off the forcing `force_save` atom's own
    // `stat`/`dc`/`cause` — never a re-thread of the calling attack's `AttackEvent`. `dc` falls
    // back the SAME way `interpretForceSaveRoll`'s own roll does (`atom.dc ?? ctx.defaultSaveDc
    // ?? 10`), so the event's `dc` always matches what the save actually rolls against.
    event: {
      family: "save",
      phase: "declared",
      stat: atom.stat,
      dc: atom.dc ?? ctx.defaultSaveDc ?? 10,
      ...(atom.cause !== undefined ? { cause: atom.cause } : {}),
    } satisfies SaveEvent,
    // Thread the resolution-scoped fact ledger through too, so a
    // `forced_save_declared` clause's own `condition` can also read a same-resolution `chose`.
    ...(ctx.ledger !== undefined ? { ledger: ctx.ledger } : {}),
    ...(ctx.loop !== undefined ? { loop: ctx.loop } : {}),
  });
  // A capped clause's use settles here, through the loop (BC-028), and lands on the scratch so a
  // second forced save later in the same action reads it spent.
  const capSteps = capUseSteps(fired.consumedCaps, ctx.loop);
  ctx.loop?.land(ctx.state, capSteps);
  steps.push(...fired.steps, ...capSteps);
  warnings.push(...fired.warnings);
}

/**
 * Stamp a `force_save`'s own {@link ForceSaveAtom.stat} onto every `status_add` step its
 * branch just emitted whose expiry is a bare, axis-less `save_ends` — `decisions/0163`
 * Decision 2: the axis belongs to the *originating* forced save ("this save" at RAW
 * `:8104`/`:8366`), not to the status. Scans only `steps[fromIndex..]`, the slice this
 * force_save's own `on_fail`/`on_pass` branch just pushed (siblings/earlier saves are
 * untouched). An authored `until: { kind: "save_ends", stat: "…" }` that already names an
 * axis is a deliberate override and is left alone.
 */
function stampSaveEndsAxis(steps: ResolvedStep[], fromIndex: number, stat: string): void {
  for (let i = fromIndex; i < steps.length; i++) {
    const step = steps[i]!;
    if (step.op === "status_add" && step.expiry?.kind === "save_ends" && step.expiry.stat === undefined) {
      step.expiry = { ...step.expiry, stat };
    }
  }
}

/**
 * The roll/branch body of `force_save` — split out so
 * {@link interpretForceSave} can wrap ONLY the depth-guard bookkeeping + the fire call,
 * not re-nest this whole body inside another function's `try`. Takes the already-resolved
 * `actor` (the caller already did the null-check + `isCharacter` gate).
 */
function interpretForceSaveRoll(args: AtomHandlerArgs<ForceSaveAtom> & { actor: Combatant }): void {
  const { atom, ctx, actor, steps, warnings, pendingDamage } = args;
  const dc = atom.dc ?? ctx.defaultSaveDc ?? 10;
  // The save bonus: in Lancer a save is `d20 + the relevant save bonus` vs the DC, and
  // "relevant" means the HASE axis the atom names (`decisions/0163`) — `saveBonus` below
  // resolves it per-axis, with the pilot/drone axis-less override taking precedence.
  const bonus = saveBonus(actor, atom.stat);
  // Fold the acting combatant's own carrier-self save/check modifiers (Impaired's
  // `saveCheckBonus: -1`, Bolster's `+2`) — the SAME
  // seam `saves.ts`'s `save_ends` re-save and `contest.ts`'s opposed check
  // fold through (`foldModifiers`/`seedModifiers`), so a FORCED save (Concussion
  // Missiles' HULL save, …) honors both exactly like every other save/check in the
  // engine (RAW Core pp. 69, 78). The concede path below is untouched — a pre-declared
  // concession skips the roll entirely, so there is no d20 for a difficulty die to
  // modify.
  //
  // ALSO fold the carrier's frozen equipped-system modifiers (ms_stable_structure's
  // passive save boon — a mech SYSTEM, not a status, so it rides `seedSystemModifiers`,
  // not `seedModifiers`) and scope the whole combined list's save/check facets by THIS
  // force_save's own authored `cause` AND/OR `stat` before folding, via `modifiers.ts`'s
  // single `scopeSaveModifiers` (a `saveScope` field only counts a dimension it authors,
  // so a cause-only or stat-only scope composes cleanly). An uncaused/unstatted save
  // (`atom.cause`/`atom.stat` undefined) only honors unscoped modifiers.
  // ALSO fold the carrier's frozen PILOT-effect (talent/core-bonus) modifiers — without
  // that gather a talent-granted "Agility checks and saves +1 Accuracy"
  // (t_ace:1) is structurally unreachable, even though it's the same carrier-self
  // save/check dimension `seedModifiers`/`seedSystemModifiers` fold here.
  const overlay = ctx.overlay ?? SEED_OVERLAY;
  const carrierMods = foldSelfSaveCheck({ state: ctx.state, carrierId: actor.id, overlay, scope: { cause: atom.cause, stat: atom.stat, roll: "save" } });
  // `atom.accuracy` (`npc-features-fielded-roster` Phase 6.2) is a per-invocation shift THIS
  // force_save's own author imposes (Impale's "+1 Difficulty") — additive with, and
  // independent from, the carrier's own standing `saveCheckBonus` fold above.
  const acc = carrierMods.saveCheckBonus + (atom.accuracy ?? 0);
  // Stunned's auto-fail HULL/AGI checks and saves (RAW
  // Core p.78) — an involuntary state fact, checked BEFORE the voluntary concede choice
  // below (both land the same recorded outcome — no die, `result: "fail"` — but this one
  // isn't a player decision, so it doesn't consult `ctx.choices`). A recorded skip-roll,
  // never a fake roll.
  if (carrierMods.autoFailChecks.includes(atom.stat)) {
    const save: SaveStep = {
      op: "save",
      actor: actor.id,
      against: atom.stat,
      d20: 0,
      accuracy: 0,
      bonus,
      total: bonus,
      dc,
      result: "fail",
      autoFailed: true,
    };
    steps.push(save);
    const fromIndex = steps.length;
    for (const a of atom.on_fail ?? []) interpretAtom({ atom: a, ctx, steps, warnings, pendingDamage });
    stampSaveEndsAxis(steps, fromIndex, atom.stat);
    return;
  }
  const concedes = ctx.choices?.choose(`concede_save:${actor.id}`, ["roll", "concede"]) === "concede";
  if (concedes) {
    const save: SaveStep = {
      op: "save",
      actor: actor.id,
      against: atom.stat,
      d20: 0,
      accuracy: 0,
      bonus,
      total: bonus,
      dc,
      result: "fail",
      conceded: true,
    };
    steps.push(save);
    const fromIndex = steps.length;
    for (const a of atom.on_fail ?? []) interpretAtom({ atom: a, ctx, steps, warnings, pendingDamage });
    stampSaveEndsAxis(steps, fromIndex, atom.stat);
    return;
  }
  const rollOnce = (): CheckRoll<SaveStep> => {
    const { result: r, id } = drawRoll({
      loop: ctx.loop,
      dice: ctx.dice,
      roll: { kind: "roll", roller: actor.id, purpose: { kind: "save", stat: atom.stat, ...(atom.cause !== undefined ? { cause: atom.cause } : {}) }, spec: { dice: [{ count: 1, sides: 20 }], accuracy: acc } },
      draw: (src) => rollD20(src, acc),
      facts: (rolled) => ({ accuracyMod: rolled.accuracyMod ?? 0 }),
    });
    const d20 = r.draws[0]!.value;
    const accuracy = r.accuracyMod ?? 0;
    const total = d20 + bonus + accuracy;
    const step: SaveStep = {
      op: "save",
      actor: actor.id,
      against: atom.stat,
      d20,
      accuracy,
      // The per-source provenance behind `acc`, recorded
      // on the ROLLED path only — the auto-fail and concede early-returns above draw no die
      // and fold no accuracy into one, so they have nothing to attribute. Spread-omitted when
      // empty (never `[]`), so an unmodified save's recorded step carries no attribution key
      // at all and snapshot/replay fixtures stay stable.
      ...(carrierMods.saveCheckContributions.length > 0 ? { accuracyContributions: carrierMods.saveCheckContributions } : {}),
      bonus,
      total,
      dc,
      result: total >= dc ? "pass" : "fail",
    };
    return { roll: step, ...(id !== undefined ? { id } : {}), total, failed: step.result === "fail" };
  };
  // A reroll the saver holds (the Student-Class NHP), offered on a fail; the new roll stands.
  const first = rollOnce();
  const { standing, spend } = settleCheck({
    ctx: selfCheckContext(ctx.state, ctx.overlay, { choices: ctx.choices, loop: ctx.loop }),
    dice: ctx.dice,
    roller: actor,
    purpose: { kind: "save", stat: atom.stat, ...(atom.cause !== undefined ? { cause: atom.cause } : {}) },
    dc,
    first,
    reroll: rollOnce,
    label: (cur) => `${atom.stat.toUpperCase()} save failed (${cur.total} vs ${dc})`,
  });
  const save = standing.roll;
  if (standing !== first) warnings.push(`${actor.name}: rerolls the ${atom.stat.toUpperCase()} save (${first.total} → ${save.total}).`);
  const passed = save.result === "pass";
  // The save, then the reroll's spend (R7: the same [save, spend] order as `rollSelfCheck`), then the folded caps.
  steps.push(save, ...spend, ...spendFoldedCaps(carrierMods, undefined, ctx.loop));
  if (acc !== 0) {
    steps.push(...spendConsumedStatuses({ carrier: actor, statuses: carrierMods.consumeOnSaveCheck, state: ctx.state, loop: ctx.loop }));
    for (const status of carrierMods.consumeOnSaveCheck) {
      warnings.push(`${actor.name}: ${status} grants ${acc > 0 ? "+" : ""}${acc}◆ to this save, then is consumed.`);
    }
  }
  const branch = passed ? atom.on_pass ?? [] : atom.on_fail ?? [];
  const fromIndex = steps.length;
  // A Chaff Grunt that passes takes no damage from this save's branch (`grunt.ts`).
  const branchCtx = passed && saveNegatesDamage(actor) ? { ...ctx, saveNegatesDamageFor: actor.id } : ctx;
  for (const a of branch) interpretAtom({ atom: a, ctx: branchCtx, steps, warnings, pendingDamage });
  stampSaveEndsAxis(steps, fromIndex, atom.stat);
}

/**
 * Interpret a `skill_check`: `self` rolls `d20 + saveBonusFor(self, skill)` vs `dc` (10) through
 * `rollSelfCheck`, the burn/mine check roller, then runs `on_pass` or `on_fail`.
 */
const SKILL_CHECK_DC = 10;

function interpretSkillCheck(args: AtomHandlerArgs<SkillCheckAtom>): void {
  const { atom, ctx, steps, warnings, pendingDamage } = args;
  const actor = resolveActor("self", ctx);
  if (!actor) return;
  const check = rollSelfCheck({
    ctx: selfCheckContext(ctx.state, ctx.overlay, { loop: ctx.loop }),
    dice: ctx.dice,
    combatant: actor,
    against: atom.skill,
    bonus: saveBonusFor(actor, atom.skill),
    dc: atom.dc ?? SKILL_CHECK_DC,
    roll: "check",
  });
  steps.push(...check.steps);
  warnings.push(...check.warnings);
  const branch = check.save.result === "pass" ? atom.on_pass ?? [] : atom.on_fail ?? [];
  for (const a of branch) interpretAtom({ atom: a, ctx, steps, warnings, pendingDamage });
}

/**
 * Interpret a `roll`: roll `count`d`sides` through the recorded
 * `DiceSource` (the same seam saves use, so the draws are recorded + replayable), sum
 * the faces (or take the highest, `keep: "highest"`), compare to `threshold` via `cmp` (default `>=`), and run the `on_hit`
 * branch (else `on_miss`). The atom vanishes from the output; only the taken branch's
 * grounded steps remain (Custom Paint Job's d6 roll-branch). The roll itself draws from
 * the shared stream in order — script it like any other die. A `bind` name exposes the total to
 * the branch's {@link import("./atoms").RolledAmount} magnitudes.
 */
function interpretRoll(args: AtomHandlerArgs<RollAtom>): void {
  const { atom, ctx, steps, warnings, pendingDamage } = args;
  const r = rollDamage({ source: ctx.dice, count: Math.max(0, atom.count), sides: Math.max(1, atom.sides), crit: false });
  const faces = r.draws.map((d) => d.value);
  const total = atom.keep === "highest" ? Math.max(0, ...faces) : faces.reduce((a, b) => a + b, 0);
  const met = compare(total, atom.cmp ?? ">=", atom.threshold);
  const branch = met ? atom.on_hit ?? [] : atom.on_miss ?? [];
  const branchCtx = atom.bind === undefined ? ctx : { ...ctx, rolled: { ...ctx.rolled, [atom.bind]: total } };
  for (const a of branch) interpretAtom({ atom: a, ctx: branchCtx, steps, warnings, pendingDamage });
}

/** A `choose` atom's options whose `when` holds (all of them when none carries one). */
function openChooseOptions(options: readonly ChooseOption[], ctx: EffectContext): ChooseOption[] {
  if (!options.some((o) => o.when !== undefined)) return [...options];
  const pctx = predicateContextFrom(ctx);
  return options.filter((o) => o.when === undefined || evaluatePredicate(o.when, pctx));
}

/**
 * Interpret a `choose`: resolve the selected option through the recorded-choice
 * seam (or the first option by default), then run that option's effects. The taken
 * branch is recorded by virtue of its emitted steps; the *which-option* record is the
 * recorded choice itself (when a `ChoiceSource` is supplied).
 *
 * **Actor-routing**: `atom.actor === "target"`
 * routes an unanswered choice's {@link NeedsDecisionSignal} to the resolution's TARGET
 * (`who` set to the resolved target id) instead of the attacker-side default
 * — the hacker talent's "take 2 Heat or be pushed, TARGET'S choice" gate. Omitted/`"attacker"`
 * throws with no `who` at all (the resolver's own catch site defaults `sig.who ?? attacker.id`). A
 * `"target"` atom with no bound target in this `EffectContext` (e.g. an `on_attack_declared`
 * fire, before any target-side context exists) falls through to the SAME unrouted default
 * rather than throwing on an unresolvable actor — deliberately fail-open, not a crash.
 */
function interpretChoose(args: AtomHandlerArgs<ChooseAtom>): void {
  const { atom, ctx, steps, warnings, pendingDamage } = args;
  const options = openChooseOptions([...atom.options, ...extendedOptions(atom, ctx)], ctx);
  if (options.length === 0) return;
  const ids = options.map((o) => o.id);
  const picked = ctx.choices?.choose(atom.prompt, ids);
  // Interactive path: an unanswered `choose` whose
  // source `requireAnswer`s halts here with a signal the resolver round-trips as a
  // prompt — rather than silently defaulting. The signal carries the authored options
  // (the bare `ChoiceSource` only sees ids).
  if (picked === undefined && ctx.choices?.requireAnswer) {
    const decider = atom.actor === "target" ? resolveActorId("target", ctx.actors) : undefined;
    throw new NeedsDecisionSignal(atom.prompt, atom.prompt, {
      options: options.map((o) => ({ id: o.id, label: o.label ?? o.id })),
      ...(decider !== undefined ? { who: decider } : {}),
    });
  }
  const option = (picked ? options.find((o) => o.id === picked) : undefined) ?? options[0]!;
  // Record the ACTUAL decision (recorded answer or the silent
  // first-option default below) onto the resolution-scoped fact ledger, keyed by this
  // atom's own `prompt` — a LATER same-resolution `condition` reads it back via the `chose`
  // predicate (`predicate.ts`). Written BEFORE running the option's own effect atoms so a
  // `choose` nested inside another `choose`'s option still records outer-to-inner in call
  // order. No-op when the caller built no ledger (a save/contest/lifecycle-point `choose`).
  if (ctx.ledger) ctx.ledger.choices[atom.prompt] = option.id;
  if (option.effect.length === 0) ctx.declines?.push(atom.prompt);
  if (!ctx.choices) {
    warnings.push(`choose "${atom.prompt}": took default option "${option.id}" (no recorded-choice transport yet, D4)`);
  }
  for (const a of option.effect) interpretAtom({ atom: a, ctx, steps, warnings, pendingDamage });
}

/** The options a `choose`'s `extend` appends: the attacker's {@link offeredInvadeOptions} against the bound target. */
function extendedOptions(atom: ChooseAtom, ctx: EffectContext): ChooseOption[] {
  const attackerId = ctx.actors.attacker ?? ctx.actors.self;
  const targetId = ctx.actors.target;
  if (atom.extend !== "invade_options" || attackerId === undefined || targetId === undefined) return [];
  return offeredInvadeOptions({ state: ctx.state, attackerId, targetId, overlay: ctx.overlay ?? SEED_OVERLAY }).map((o) => ({ id: o.id, label: o.label, effect: o.effect }));
}

/**
 * The save bonus a combatant rolls on the named HASE `stat` — its `hull`/`agility`/
 * `systems`/`engineering` axis, or the axis-less `saveBonus` override RAW grants the on-foot
 * pilot (GRIT) and the drone (`+0`). See `decisions/0163`. Until 2026-08-28 this discarded
 * `stat` entirely and returned the frozen `save` value, which held the save TARGET.
 */
function saveBonus(c: Combatant, stat: string): number {
  return saveBonusFor(c, stat);
}
